import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { TenantPosts } from "./posts/PostsHub";
import Shell from "@/components/Shell";
import { toast } from "sonner";
import { Plus, Trash2, Download, Send, Receipt, Upload, FileSpreadsheet, Pencil, KeyRound, Search, Clock, ChevronLeft, ChevronRight, Heart, AlertTriangle } from "lucide-react";
import EventsPanel from "./EventsPanel";
import StudentIdCapture from "./student_id/StudentIdCapture";
import { EducatorLMSPerformance, EducatorReviewQueue } from "./EducatorDashboard";
import LoansTransfers from "@/pages/finance/LoansTransfers";
import BankOps from "@/pages/finance/BankOps";
import SchoolWaba from "@/pages/school_admin/SchoolWaba";

function ListEditor({ value, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v || value.includes(v)) return;
    onChange([...value, v]);
    setDraft("");
  };
  const remove = (idx) => onChange(value.filter((_,i)=>i!==idx));
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input className="nb-input" placeholder={placeholder} value={draft}
               onChange={e=>setDraft(e.target.value)}
               onKeyDown={e=>{ if(e.key==='Enter'){e.preventDefault(); add();} }}
               data-testid={`${testid}-input`}/>
        <button type="button" onClick={add} className="nb-btn" data-testid={`${testid}-add`}>+ Add</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {value.map((v,i)=>(
          <span key={`${v}-${i}`} className="nb-badge bg-white flex items-center gap-1" data-testid={`${testid}-${i}`}>
            {v}<button type="button" onClick={()=>remove(i)} className="text-rose-600 font-bold ml-1">×</button>
          </span>
        ))}
        {value.length===0 && <span className="text-xs text-slate-500">None added</span>}
      </div>
    </div>
  );
}

function SubjectPartEditor({ value, onChange, otherPartValue, otherOnChange, testid, moveLabel }) {
  // Iter20: Part-A / Part-B subject list with add + reorder (↑ ↓) + move-across.
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v || value.includes(v)) return;
    onChange([...value, v]);
    setDraft("");
  };
  const remove = (idx) => onChange(value.filter((_,i)=>i!==idx));
  const move = (idx, dir) => {
    const next = [...value];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const moveAcross = (idx) => {
    const v = value[idx];
    onChange(value.filter((_, i) => i !== idx));
    if (!otherPartValue.includes(v)) otherOnChange([...otherPartValue, v]);
  };
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input className="nb-input" placeholder="Add subject" value={draft}
               onChange={e=>setDraft(e.target.value)}
               onKeyDown={e=>{ if(e.key==='Enter'){e.preventDefault(); add();} }}
               data-testid={`${testid}-input`}/>
        <button type="button" onClick={add} className="nb-btn" data-testid={`${testid}-add`}>+ Add</button>
      </div>
      <ul className="space-y-1" data-testid={`${testid}-list`}>
        {value.map((v, i) => (
          <li key={`${v}-${i}`} className="flex items-center gap-1 bg-white border-2 border-black px-2 py-1 rounded"
              data-testid={`${testid}-item-${v}`}>
            <span className="flex-1 font-bold text-sm">{i+1}. {v}</span>
            <button type="button" onClick={()=>move(i,-1)} disabled={i===0}
                    className="nb-btn nb-btn-ghost px-1 py-0 text-xs disabled:opacity-30"
                    data-testid={`${testid}-up-${v}`}>↑</button>
            <button type="button" onClick={()=>move(i,+1)} disabled={i===value.length-1}
                    className="nb-btn nb-btn-ghost px-1 py-0 text-xs disabled:opacity-30"
                    data-testid={`${testid}-down-${v}`}>↓</button>
            <button type="button" onClick={()=>moveAcross(i)}
                    className="nb-btn nb-btn-secondary px-2 py-0 text-[10px]"
                    data-testid={`${testid}-move-${v}`}>{moveLabel}</button>
            <button type="button" onClick={()=>remove(i)}
                    className="text-rose-600 font-bold px-1"
                    data-testid={`${testid}-del-${v}`}>×</button>
          </li>
        ))}
        {value.length===0 && <li className="text-xs text-slate-500 italic">None yet — add your first subject above.</li>}
      </ul>
    </div>
  );
}



const TABS = [
  {id:"overview", label:"Overview"},
  {id:"ledger", label:"Cash Flow"},
  {id:"educators", label:"Educators"},
  {id:"staff", label:"Staff & Attendance"},
  {id:"students", label:"Students"},
  {id:"attendance_overview", label:"Student Attendance"},
  {id:"academics", label:"Academics"},
  {id:"timetable", label:"Time Table Designer"},
  {id:"fees", label:"Fees"},
  {id:"donations_log", label:"Donations Log"},
  {id:"expense_log", label:"Expense Log"},
  {id:"payroll_approvals", label:"Payroll Approvals"},
  {id:"loans", label:"Loans & Transfers"},
  {id:"bank", label:"Bank Accounts"},
  {id:"id_cards", label:"ID Cards"},
  {id:"events", label:"Event Management"},
  {id:"acad_analytics", label:"Academic Analytics"},
  {id:"lms_perf", label:"LMS Performance"},
  {id:"review_queue", label:"LMS Review Queue"},
  {id:"notifications", label:"Notifications"},
  {id:"waba", label:"WhatsApp Alerts"},
  {id:"content", label:"Landing Content"},
  {id:"posts", label:"Posts & News"},
  {id:"enquiries", label:"Enquiries"},
  {id:"settings", label:"Settings"},
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <Shell title="Admin Console" subtitle="Manage your school" tabs={TABS} active={tab} onTab={setTab}>
      {tab==="overview" && <Overview/>}
      {tab==="educators" && <Educators/>}
      {tab==="staff" && <StaffAttendance/>}
      {tab==="students" && <Students/>}
      {tab==="fees" && <Fees/>}
      {tab==="notifications" && <Notifications/>}
      {tab==="waba" && <SchoolWaba/>}
      {tab==="content" && <Content/>}
      {tab==="posts" && <TenantPosts entityType="school"/>}
      {tab==="enquiries" && <Enquiries/>}
      {tab==="ledger" && <CashFlow/>}
      {tab==="donations_log" && <DonationsLog/>}
      {tab==="expense_log" && <ExpenseLog/>}
      {tab==="payroll_approvals" && <PayrollApprovals/>}
      {tab==="loans" && <LoansTransfers mode="review"/>}
      {tab==="bank" && <BankOps role="admin"/>}
      {tab==="id_cards" && <IdCards/>}
      {tab==="events" && <><EventsPanel mode="manage"/><MasterEventLog/></>}
      {tab==="acad_analytics" && <AcademicAnalytics/>}
      {tab==="lms_perf" && <EducatorLMSPerformance/>}
      {tab==="review_queue" && <EducatorReviewQueue/>}
      {tab==="settings" && <Settings/>}
      {tab==="attendance_overview" && <StudentAttendanceOverview/>}
      {tab==="academics" && <Academics/>}
      {tab==="timetable" && <TimetableDesigner/>}
    </Shell>
  );
}

function LegacySubjectMigrator({ settings, onChange }) {
  // Iter21 — one-click migration of unassigned legacy subjects into Part A or Part B.
  const legacy = settings.subjects || [];
  const partA = settings.subjects_part_a || [];
  const partB = settings.subjects_part_b || [];
  const assigned = new Set([...partA, ...partB]);
  const unassigned = legacy.filter(sub => !assigned.has(sub));
  const [choice, setChoice] = useState({}); // subject -> "a" | "b"
  const migrate = (sub) => {
    const target = choice[sub] || "a";
    if (target === "a") {
      if (!partA.includes(sub))
        onChange({ ...settings, subjects_part_a: [...partA, sub] });
    } else {
      if (!partB.includes(sub))
        onChange({ ...settings, subjects_part_b: [...partB, sub] });
    }
    toast.success(`Moved '${sub}' to Part ${target.toUpperCase()} — click Save Settings to persist`);
  };
  if (unassigned.length === 0) {
    return (
      <div className="nb-card p-3 bg-[#86EFAC] text-xs">
        <b>All legacy subjects migrated</b> — every subject now sits in Part A or Part B.
      </div>
    );
  }
  return (
    <div className="nb-card p-4 bg-[#FDBA74] space-y-2" data-testid="legacy-migrator">
      <div className="display text-sm font-bold">Migrate Legacy Subjects</div>
      <p className="text-[11px]">{unassigned.length} legacy subject(s) not yet classified. Assign to Part A (Scholastic) or Part B (Non-Scholastic). Historical marks stay intact — only the classification moves.</p>
      <div className="space-y-1">
        {unassigned.map(sub => (
          <div key={sub} className="flex items-center gap-2 bg-white border-2 border-black px-2 py-1 rounded"
               data-testid={`legacy-row-${sub}`}>
            <span className="flex-1 font-bold text-sm">{sub}</span>
            <select className="nb-select text-xs w-40"
                    value={choice[sub] || "a"}
                    onChange={e=>setChoice({...choice, [sub]: e.target.value})}
                    data-testid={`legacy-select-${sub}`}>
              <option value="a">Scholastic (Part A)</option>
              <option value="b">Non-Scholastic (Part B)</option>
            </select>
            <button type="button" onClick={()=>migrate(sub)}
                    className="nb-btn nb-btn-secondary text-xs"
                    data-testid={`legacy-move-${sub}`}>Move</button>
          </div>
        ))}
      </div>
    </div>
  );
}



function TimetableDesigner() {
  const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat"];
  const PERIODS = [1,2,3,4,5,6,7,8];
  const [settings, setSettings] = useState({grades:[], sections:[], subjects:[]});
  const [staff, setStaff] = useState([]);
  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("");
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    api.get("/settings").then(r => setSettings(r.data));
    // Pull teaching staff (educators + non-class teachers) from unified /staff endpoint
    api.get("/staff").then(r => setStaff(r.data.filter(x => x.department === "Teaching")))
       .catch(() => setStaff([]));
  }, []);
  const load = () => {
    if (!cls || !sec) return;
    api.get("/timetables", { params: { class_name: cls, section: sec }})
       .then(r => setRows(r.data));
  };
  useEffect(() => { load(); }, [cls, sec]);  // eslint-disable-line
  const grid = {};
  rows.forEach(r => { grid[`${r.day_of_week}|${r.period}`] = r; });
  const openCell = (day, period) => {
    const existing = grid[`${day}|${period}`];
    setEdit({
      day_of_week: day, period,
      subject: existing?.subject || "",
      educator_name: existing?.educator_name || "",
      start_time: existing?.start_time || "",
      end_time: existing?.end_time || "",
      tt_id: existing?.tt_id || null,
    });
  };
  const save = async () => {
    if (!cls || !sec || !edit.subject) return toast.error("Subject required");
    try {
      await api.post("/timetables", {
        class_name: cls, section: sec, day_of_week: edit.day_of_week,
        period: edit.period, subject: edit.subject,
        educator_name: edit.educator_name, start_time: edit.start_time,
        end_time: edit.end_time,
      });
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeCell = async () => {
    if (!edit?.tt_id) { setEdit(null); return; }
    if (!window.confirm("Clear this slot?")) return;
    await api.delete(`/timetables/${edit.tt_id}`); toast.success("Cleared"); setEdit(null); load();
  };
  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#93C5FD] flex flex-wrap items-end gap-3">
        <div>
          <div className="display text-xl font-bold">Time Table Designer</div>
          <p className="text-sm mt-1">Design a weekly class schedule. Students &amp; educators see it in their consoles.</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <label><div className="nb-label">Class</div>
            <select value={cls} onChange={e=>setCls(e.target.value)} className="nb-select w-32 mt-1" data-testid="tt-class">
              <option value="">—</option>
              {settings.grades?.map(g => <option key={g} value={g}>{g}</option>)}
            </select></label>
          <label><div className="nb-label">Section</div>
            <select value={sec} onChange={e=>setSec(e.target.value)} className="nb-select w-24 mt-1" data-testid="tt-section">
              <option value="">—</option>
              {settings.sections?.map(s => <option key={s} value={s}>{s}</option>)}
            </select></label>
        </div>
      </div>
      {(!cls || !sec) ? (
        <div className="nb-card p-6 text-slate-500">Choose a class and section above to start designing.</div>
      ) : (
        <div className="nb-card p-0 overflow-x-auto">
          <table className="nb-table">
            <thead><tr><th>Period</th>{DAYS.map(d => <th key={d}>{d}</th>)}</tr></thead>
            <tbody>
              {PERIODS.map(p => (
                <tr key={p}>
                  <td className="font-bold mono">P{p}</td>
                  {DAYS.map(d => {
                    const s = grid[`${d}|${p}`];
                    return (
                      <td key={d} onClick={() => openCell(d, p)}
                          className={`cursor-pointer hover:bg-[#FDE047] ${s ? "bg-[#FDE047]" : ""}`}
                          data-testid={`tt-cell-${d}-${p}`}>
                        {s ? (
                          <div>
                            <div className="font-bold text-sm">{s.subject}</div>
                            {s.educator_name && <div className="text-xs">{s.educator_name}</div>}
                            {s.start_time && <div className="text-xs mono">{s.start_time}-{s.end_time}</div>}
                          </div>
                        ) : <span className="text-slate-300 text-xs">click to add</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={() => setEdit(null)}>
          <div className="nb-card p-6 bg-white max-w-md w-full space-y-3"
               onClick={e => e.stopPropagation()}>
            <h3 className="display text-xl font-bold">{edit.day_of_week} · Period {edit.period}</h3>
            <label className="block"><span className="nb-label">Subject *</span>
              <select className="nb-select" value={edit.subject}
                      onChange={e => setEdit({...edit, subject: e.target.value})}
                      data-testid="tt-edit-subject">
                <option value="">-- pick a subject --</option>
                {(settings.subjects || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select></label>
            <label className="block"><span className="nb-label">Educator / Teaching Staff</span>
              <select className="nb-select" value={edit.educator_name}
                      onChange={e => setEdit({...edit, educator_name: e.target.value})}
                      data-testid="tt-edit-educator">
                <option value="">-- pick an educator --</option>
                {staff.map(s => (
                  <option key={`${s.kind}|${s.beneficiary_id}`} value={s.name}>
                    {s.name}{s.subject ? ` · ${s.subject}` : ""}{s.designation ? ` (${s.designation})` : ""}
                  </option>
                ))}
              </select></label>
            <div className="grid grid-cols-2 gap-2">
              <label><span className="nb-label">Start</span>
                <input type="time" className="nb-input" value={edit.start_time}
                       onChange={e => setEdit({...edit, start_time: e.target.value})}/></label>
              <label><span className="nb-label">End</span>
                <input type="time" className="nb-input" value={edit.end_time}
                       onChange={e => setEdit({...edit, end_time: e.target.value})}/></label>
            </div>
            <div className="flex gap-2 pt-2">
              {edit.tt_id && (
                <button onClick={removeCell} className="nb-btn nb-btn-danger px-3">Clear</button>
              )}
              <button onClick={() => setEdit(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={save} className="nb-btn flex-1 justify-center" data-testid="tt-edit-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function StudentAttendanceOverview() {
  const TODAY = new Date().toISOString().slice(0,10);
  const [grade, setGrade] = useState("");
  const [sec, setSec] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(TODAY.slice(0,7));
  const [students, setStudents] = useState([]);
  const [monthDocs, setMonthDocs] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [settings, setSettings] = useState({grades:[], sections:[], holidays:[]});
  const [overrideActive, setOverrideActive] = useState(false);
  const [overrideExp, setOverrideExp] = useState(null);
  useEffect(()=>{
    api.get("/settings").then(r=>setSettings(r.data));
    api.get("/overrides/my").then(r=>{
      setOverrideActive(!!r.data?.student_attendance?.active);
      setOverrideExp(r.data?.student_attendance?.expires_at || null);
    }).catch(()=>{});
  }, []);
  useEffect(()=>{
    (async () => {
      if (!grade || !sec) { setStudents([]); setMonthDocs([]); return; }
      const [s, m] = await Promise.all([
        api.get("/students", { params: { class_name: grade, section: sec }}),
        api.get("/attendance/month", { params: { class_name: grade, section: sec, month: selectedMonth }}),
      ]);
      setStudents(s.data); setMonthDocs(m.data);
      api.get("/attendance/month-status", { params: { class_name: grade, section: sec, month: selectedMonth, scope: "student" }})
        .then(r=>setStatusMap(r.data?.statuses || {})).catch(()=>setStatusMap({}));
    })();
  }, [grade, sec, selectedMonth]);

  const days = [];
  const d0 = new Date(selectedMonth + "-01");
  const m0 = d0.getMonth();
  while (d0.getMonth() === m0) { days.push(d0.toISOString().slice(0,10)); d0.setDate(d0.getDate()+1); }
  const monthLabel = new Date(selectedMonth + "-01").toLocaleString("en-US", { month: "short", year: "numeric" }).replace(" ", "");
  const shiftMonth = (delta) => {
    const d = new Date(selectedMonth + "-01");
    d.setMonth(d.getMonth() + delta);
    setSelectedMonth(d.toISOString().slice(0,7));
  };
  const docsByDate = {};
  monthDocs.forEach(x => { docsByDate[x.date] = x.records || {}; });
  const holidayMap = {};
  (settings.holidays || []).forEach(h => { holidayMap[h.date] = h.name; });
  const isUnrecorded = (d) => statusMap[d]?.status === "unrecorded";
  const unrecordedDays = days.filter(isUnrecorded);
  const cellStatus = (student_id, date) => {
    if (date in docsByDate) {
      const v = docsByDate[date][student_id];
      if (v === true) return "present";
      if (v === false) return "absent";
    }
    if (date <= TODAY) return "present";
    return null;
  };
  const cellEditable = (date) => {
    if (date > TODAY) return false;
    if (date === TODAY) return true;
    return overrideActive;
  };
  const flipStatus = (curr) => curr === "present" ? "absent" : "present";
  const saveCell = async (date, student_id, newStatus) => {
    const currRecords = docsByDate[date] || {};
    const rec = { ...currRecords };
    students.forEach(st => { if (!(st.student_id in rec)) rec[st.student_id] = true; });
    rec[student_id] = newStatus === "present";
    try {
      await api.post("/attendance", { class_name: grade, section: sec, date, records: rec });
      const m = await api.get("/attendance/month", { params: { class_name: grade, section: sec, month: selectedMonth }});
      setMonthDocs(m.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Locked"); }
  };
  const cellClick = (date, student_id, currStatus, isSun, holiday) => {
    if (holiday) { toast.info(`Holiday: ${holiday}`); return; }
    if (isSun) { toast.info("Sunday — no classes"); return; }
    if (date > TODAY) { toast.info("Future dates are locked"); return; }
    if (date < TODAY && !overrideActive) {
      toast.info("Locked — ask Global Admin to enable Student Attendance override");
      return;
    }
    saveCell(date, student_id, flipStatus(currStatus));
  };
  const bulkMarkPresent = async () => {
    if (!grade || !sec) return toast.error("Pick grade & section");
    if (!window.confirm(`Mark ALL students Present for every eligible day in ${monthLabel}?`)) return;
    try {
      for (const d of days) {
        if (d > TODAY) continue;
        if (d < TODAY && !overrideActive) continue;
        const rec = {};
        students.forEach(st => { rec[st.student_id] = true; });
        await api.post("/attendance", {class_name: grade, section: sec, date: d, records: rec});
      }
      toast.success("Bulk-marked Present for eligible days");
      const m = await api.get("/attendance/month", { params: { class_name: grade, section: sec, month: selectedMonth }});
      setMonthDocs(m.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5">
      {overrideActive && (
        <div className="nb-card p-3 bg-[#FDE047] flex flex-wrap items-center gap-3" data-testid="stu-att-override-banner">
          <Clock size={16}/>
          <div className="text-sm font-bold">
            Historical student-attendance override is ACTIVE — past cells are bulk-editable here.
            Expires {(overrideExp||"").slice(0,16).replace("T"," ")} UTC.
          </div>
        </div>
      )}
      <div className="nb-card p-5 bg-[#FDE047] flex flex-wrap gap-3 items-end">
        <div><label className="nb-label">Grade</label>
          <select className="nb-select mt-1" value={grade} onChange={e=>setGrade(e.target.value)}
                  data-testid="stu-att-grade-filter">
            <option value="">--</option>{settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
          </select></div>
        <div><label className="nb-label">Section</label>
          <select className="nb-select mt-1" value={sec} onChange={e=>setSec(e.target.value)}
                  data-testid="stu-att-sec-filter">
            <option value="">--</option>{settings.sections?.map(s=><option key={s} value={s}>{s}</option>)}
          </select></div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={()=>shiftMonth(-1)} className="nb-btn nb-btn-ghost px-2 py-1"
                  data-testid="stu-att-month-prev"><ChevronLeft size={16}/></button>
          <span className="display text-xl font-bold" data-testid="stu-att-month-label">{monthLabel}</span>
          <button onClick={()=>shiftMonth(1)} className="nb-btn nb-btn-ghost px-2 py-1"
                  data-testid="stu-att-month-next"><ChevronRight size={16}/></button>
        </div>
      </div>

      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-white flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="display text-lg font-bold">Student Attendance Log — {monthLabel} · {grade || "—"}-{sec || "—"}</h3>
            {unrecordedDays.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md bg-amber-500/20 text-amber-600 border border-amber-400/40"
                    data-testid="stu-att-unrecorded-pill">
                <AlertTriangle size={13}/> {unrecordedDays.length} Unrecorded {unrecordedDays.length===1?"Date":"Dates"} — attendance not saved yet
              </span>
            )}
          </div>
          <div className="text-xs flex items-center gap-3">
            <span>Default = <b className="text-emerald-700">P</b>. Tap to toggle <b className="text-rose-700">A</b>bsent.</span>
            <button onClick={bulkMarkPresent} disabled={!grade || !sec}
                    className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                    data-testid="stu-att-bulk-present">Mark All Present</button>
          </div>
        </div>
        <div className="overflow-auto max-h-[calc(100vh-260px)] relative">
          <table className="nb-table">
            <thead><tr>
              <th className="sticky left-0 top-0 z-30 bg-white shadow-sm border-b">Roll · Name</th>
              {days.map(d => {
                const isSun = new Date(d).getDay() === 0;
                const holiday = holidayMap[d];
                const unrec = isUnrecorded(d);
                // Iter23: holidays use grey styling + "HO" tag (was rose "HOL")
                const hdr = unrec ? "bg-amber-500/20 text-amber-600 border border-amber-400/40"
                  : isSun ? "bg-slate-200 text-slate-500" : holiday ? "bg-slate-200 text-slate-500" : d===TODAY?"bg-[#FDE047]":"bg-white";
                return <th key={d} className={`text-xs mono sticky top-0 z-20 shadow-sm border-b ${hdr}`}
                  title={unrec ? `Unrecorded — attendance not saved for ${d}` : holiday ? `Holiday (HO): ${holiday}` : (isSun ? "Sunday" : d)}>
                  {d.slice(8)}{isSun && <div className="text-[8px] font-bold">SUN</div>}
                  {holiday && <div className="text-[8px] font-bold">HO</div>}
                  {unrec && !holiday && !isSun && <div className="text-[8px] font-bold">⚠</div>}
                </th>;
              })}
              <th className="sticky top-0 z-20 bg-white shadow-sm border-b">P</th>
              <th className="sticky top-0 z-20 bg-white shadow-sm border-b">A</th>
            </tr></thead>
            <tbody>
              {students.map(s => {
                let p=0, a=0;
                return <tr key={s.student_id} data-testid={`stu-att-row-${s.student_id}`}>
                  <td className="sticky left-0 bg-white font-bold text-sm">
                    <span className="mono text-xs mr-1">{s.roll_no}</span>{s.name}
                  </td>
                  {days.map(d => {
                    const st = cellStatus(s.student_id, d);
                    const editable = cellEditable(d);
                    const isSun = new Date(d).getDay() === 0;
                    const holiday = holidayMap[d];
                    // Iter23 — 'HO' strictly for declared Holidays, 'H' remains reserved
                    // for staff Half-Day (student attendance has no half-day today).
                    // Holiday cells are excluded from Present / Absent tallies below.
                    if (!holiday && !isSun) {
                      if (st === "present") p++; else if (st === "absent") a++;
                    }
                    const ch = holiday ? "HO" : isSun ? "—" : st==="present"?"P":st==="absent"?"A":"·";
                    const cl = holiday ? "bg-slate-200 text-slate-500 font-bold" :
                               isSun ? "bg-slate-100 text-slate-400" :
                               st==="present"?"text-emerald-700 font-bold bg-emerald-50":
                               st==="absent"?"text-rose-700 font-bold bg-rose-50":"text-slate-300";
                    return <td key={d}
                      onClick={()=>cellClick(d, s.student_id, st, isSun, holiday)}
                      className={`text-center mono text-xs ${cl} ${editable && !isSun && !holiday ?"cursor-pointer hover:opacity-70":"cursor-not-allowed opacity-60"}`}
                      title={holiday ? `Holiday (HO): ${holiday}` : isSun ? "Sunday — off" : editable ? `${d} · ${st||"blank"} — click to toggle` : d > TODAY ? "future date locked" : "past date locked (needs override)"}
                      data-testid={`stu-att-cell-${s.student_id}-${d}`}>{ch}</td>;
                  })}
                  <td className="mono font-bold">{p}</td><td className="mono">{a}</td>
                </tr>;
              })}
              {students.length===0 && <tr><td colSpan={days.length+3} className="text-center py-6 text-slate-500">Pick a grade &amp; section</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function Academics() {
  const [settings, setSettings] = useState({grades:[], sections:[], test_types:[]});
  const [f, setF] = useState({test_type:"", class_name:"", section:""});
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]);
  const [decided, setDecided] = useState([]);
  const [detail, setDetail] = useState(null);
  // Iter19: Test Type Log approvals
  const [ttlRows, setTtlRows] = useState([]);
  const loadTtl = async () => {
    try {
      const r = await api.get("/marks/test-type-log/pending-approvals");
      setTtlRows(r.data || []);
    } catch { setTtlRows([]); }
  };
  const decideTtl = async (row, action) => {
    const note = action === "reject"
      ? (window.prompt("Rejection note (optional):") || "") : "";
    try {
      await api.post("/marks/test-type-log/decision", {
        test_type: row.test_type, class_name: row.class_name,
        section: row.section, action, note,
      });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      loadTtl();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const downloadTtlBulk = async (row) => {
    try {
      const endpoint = row.approved_kind === "report_card"
        ? "/report-cards/generate"
        : "/marks-cards/bulk";
      const body = row.approved_kind === "report_card"
        ? { class_name: row.class_name, section: row.section }
        : { test_type: row.test_type, class_name: row.class_name, section: row.section };
      const res = await api.post(endpoint, body, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${row.approved_kind === "report_card" ? "ReportCard" : "MarksCards"}_${row.class_name}${row.section}_${row.test_type}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const loadSubs = async () => {
    const [p, d] = await Promise.all([
      api.get("/marks/submissions", { params: { status: "pending" }}),
      api.get("/marks/submissions"),
    ]);
    setPending(p.data);
    setDecided(d.data.filter(x => x.status !== "pending").slice(0, 20));
  };
  useEffect(()=>{
    api.get("/settings").then(r=>{
      setSettings(r.data);
      setF(x=>({...x, test_type: r.data.test_types?.[0]||"", class_name:r.data.grades?.[0]||"", section:r.data.sections?.[0]||""}));
    });
    loadSubs();
    loadTtl();
  }, []);
  const downloadBulk = async () => {
    setBusy(true);
    try {
      const r = await api.post("/marks-cards/bulk", f, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `MarksCards_${f.class_name}${f.section}_${f.test_type}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Marks cards downloaded");
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  // Print Summary → the class-level grouped Scholastic/Non-Scholastic summary
  // sheet (same unified engine as the Educator console's Print Summary).
  const downloadSummary = async () => {
    setBusy(true);
    try {
      const r = await api.post("/marks/test-summary-pdf", f, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `MarksSummary_${f.class_name}-${f.section}_${f.test_type}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Marks summary downloaded");
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  // Iter50 — Bulk Reports Generator (comparative multi-test report cards).
  const downloadReports = async () => {
    setBusy(true);
    try {
      const r = await api.post("/report-cards/generate",
        { class_name: f.class_name, section: f.section },
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `ReportCards_${f.class_name}${f.section}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Report cards downloaded");
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const publish = async () => {
    setBusy(true);
    try {
      const r = await api.post("/marks-cards/publish", f);
      toast.success(`Published ${r.data.published_count} report cards to parents`);
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const openDetail = async (sub) => {
    const r = await api.get(`/marks/submissions/${sub.submission_id}`);
    setDetail(r.data);
  };
  const decide = async (id, action, defaultNote) => {
    if (action === "reject" && !window.confirm("Reject this submission? Educator will be notified.")) return;
    if (action === "approve" && !window.confirm("Approve & push these cards to parents now?")) return;
    const note = defaultNote || "";
    try {
      const r = await api.post(`/marks/submissions/${id}/${action}`, { note });
      if (action === "approve") {
        toast.success(`Approved · ${r.data.published_count} card(s) pushed to parents`);
      } else {
        toast.success("Rejected — educator will be notified");
      }
      setDetail(null); loadSubs();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return <div className="space-y-6">
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="nb-card p-6 bg-[#C4B5FD] space-y-3">
        <h3 className="display text-xl font-bold">Bulk Report Cards Generator</h3>
        <div><label className="nb-label">Test Type</label>
          <select required className="nb-select mt-1" value={f.test_type} onChange={e=>setF({...f,test_type:e.target.value})} data-testid="acad-test">
            {settings.test_types?.map(t=><option key={t} value={t}>{t}</option>)}
          </select></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="nb-label">Grade</label>
            <select required className="nb-select mt-1" value={f.class_name} onChange={e=>setF({...f,class_name:e.target.value})} data-testid="acad-grade">
              {settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
            </select></div>
          <div><label className="nb-label">Section</label>
            <select required className="nb-select mt-1" value={f.section} onChange={e=>setF({...f,section:e.target.value})} data-testid="acad-sec">
              {settings.sections?.map(s=><option key={s} value={s}>{s}</option>)}
            </select></div>
        </div>
        <button disabled={busy} onClick={downloadBulk} className="nb-btn w-full justify-center" data-testid="acad-download">
          <Download size={16}/>Print Marks Cards
        </button>
        {/* Iter50 — mirrors the Educator's Marks Cards tab (Print Summary + Bulk Reports Generator). */}
        <div className="grid grid-cols-2 gap-2">
          <button disabled={busy} onClick={downloadSummary}
                  className="nb-btn nb-btn-tertiary justify-center"
                  data-testid="acad-print-summary">
            <Download size={14}/>Print Summary
          </button>
          <button disabled={busy} onClick={downloadReports}
                  className="nb-btn nb-btn-tertiary justify-center"
                  data-testid="acad-bulk-reports">
            <Download size={14}/>Bulk Reports Generator
          </button>
        </div>
        <button disabled={busy} onClick={publish} className="nb-btn nb-btn-secondary w-full justify-center" data-testid="acad-publish">
          <Send size={16}/>Push to Parents (bypass approval)
        </button>
      </div>
      <div className="nb-card p-6 bg-white">
        <h3 className="display text-xl font-bold mb-3">How it works</h3>
        <ol className="list-decimal pl-5 text-sm space-y-2">
          <li>Educator enters marks in the <b>Marks Entry</b> tab of their console.</li>
          <li>Educator opens <b>Marks Cards</b> tab and clicks <b>Submit for Approval</b>.</li>
          <li>You review the batch below and <b>Approve</b>. Approved cards auto-publish to parents (SMS + Parent Portal).</li>
          <li>The Bulk PDF generator on the left works independently for printing physical report cards.</li>
        </ol>
      </div>
    </div>

    <div className="nb-card p-0 overflow-hidden" data-testid="ttl-approvals">
      <div className="px-6 py-3 border-b-2 border-black bg-[#FDBA74] flex flex-wrap justify-between items-center">
        <h3 className="display text-xl font-bold">Test Type Marks — Approvals ({ttlRows.filter(r=>r.approval_status==="pending_approval").length})</h3>
        <span className="text-xs">Educator-pushed Test Type logs. Approve to unlock bulk print.</span>
      </div>
      <table className="nb-table">
        <thead><tr><th>Pushed</th><th>Educator</th><th>Test Type</th><th>Class</th><th>Kind</th><th>Subjects</th><th>Status</th><th>Action</th></tr></thead>
        <tbody>
          {ttlRows.map(r => (
            <tr key={`${r.class_name}-${r.section}-${r.test_type}`} data-testid={`ttl-row-${r.test_type}-${r.class_name}${r.section}`}>
              <td className="mono text-xs">{(r.pushed_at||"").slice(0,10)}</td>
              <td className="text-xs">{r.updated_by || "—"}</td>
              <td className="font-bold">{r.test_type}</td>
              <td>{r.class_name}-{r.section}</td>
              <td>{r.approved_kind === "report_card" ? "Report Card" : "Marks Card"}</td>
              <td className="text-xs">{(r.subjects||[]).join(", ")}</td>
              <td><span className="nb-badge">{(r.approval_status||"").toUpperCase()}</span></td>
              <td className="space-x-1 whitespace-nowrap">
                {r.approval_status === "pending_approval" && (<>
                  <button onClick={()=>decideTtl(r, "approve")} className="nb-btn text-xs"
                          data-testid={`ttl-approve-${r.test_type}-${r.class_name}${r.section}`}>Approve</button>
                  <button onClick={()=>decideTtl(r, "reject")} className="nb-btn nb-btn-danger text-xs"
                          data-testid={`ttl-reject-${r.test_type}-${r.class_name}${r.section}`}>Reject</button>
                </>)}
                {r.approval_status === "approved" && (
                  <button onClick={()=>downloadTtlBulk(r)}
                          className="nb-btn nb-btn-secondary text-xs">
                    <Download size={12}/> Bulk PDF
                  </button>
                )}
              </td>
            </tr>
          ))}
          {ttlRows.length===0 && <tr><td colSpan={8} className="text-center py-6 text-slate-500">No Test Type pushes yet</td></tr>}
        </tbody>
      </table>
    </div>

    <div className="nb-card p-0 overflow-hidden" data-testid="acad-approvals">
      <div className="px-6 py-3 border-b-2 border-black bg-[#FDE047] flex flex-wrap justify-between items-center">
        <h3 className="display text-xl font-bold">Marks Cards — Pending Approvals ({pending.length})</h3>
        <span className="text-xs">Educator-submitted batches awaiting your approval</span>
      </div>
      <table className="nb-table">
        <thead><tr><th>Submitted</th><th>Educator</th><th>Class</th><th>Test</th><th>Students</th><th>Note</th><th></th></tr></thead>
        <tbody>
          {pending.map(s=>(
            <tr key={s.submission_id} data-testid={`sub-row-${s.submission_id}`}>
              <td className="mono text-xs">{new Date(s.submitted_at).toLocaleString()}</td>
              <td className="font-bold">{s.submitted_by_name}</td>
              <td><span className="nb-badge bg-[#C4B5FD]">{s.class_name}-{s.section}</span></td>
              <td><span className="nb-badge bg-[#86EFAC]">{s.test_type}</span></td>
              <td className="mono">{s.student_count}</td>
              <td className="text-xs">{s.note||"—"}</td>
              <td>
                <div className="flex gap-1">
                  <button onClick={()=>openDetail(s)} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                          data-testid={`sub-view-${s.submission_id}`}>Review</button>
                  <button onClick={()=>decide(s.submission_id, "approve")} className="nb-btn px-2 py-1 text-xs"
                          data-testid={`sub-approve-${s.submission_id}`}>Approve &amp; Push</button>
                  <button onClick={()=>decide(s.submission_id, "reject")} className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                          data-testid={`sub-reject-${s.submission_id}`}>Reject</button>
                </div>
              </td>
            </tr>
          ))}
          {pending.length===0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No pending submissions</td></tr>}
        </tbody>
      </table>
    </div>

    {decided.length > 0 && (
      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-white"><h3 className="display text-lg font-bold">Recent Decisions</h3></div>
        <table className="nb-table">
          <thead><tr><th>Decided</th><th>Educator</th><th>Class</th><th>Test</th><th>Status</th><th>Note</th></tr></thead>
          <tbody>
            {decided.map(s=>(
              <tr key={s.submission_id}>
                <td className="mono text-xs">{s.decided_at ? new Date(s.decided_at).toLocaleString() : "—"}</td>
                <td>{s.submitted_by_name}</td>
                <td>{s.class_name}-{s.section}</td>
                <td>{s.test_type}</td>
                <td><span className={`nb-badge ${s.status==="approved"?"bg-[#86EFAC]":"bg-[#FCA5A5]"}`}>{s.status.toUpperCase()}</span></td>
                <td className="text-xs">{s.decision_note||"—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {detail && (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={()=>setDetail(null)} data-testid="sub-detail-modal">
        <div className="nb-card p-6 bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto space-y-3" onClick={e=>e.stopPropagation()}>
          <div className="flex justify-between items-start">
            <div>
              <h3 className="display text-xl font-bold">Review — {detail.test_type} · {detail.class_name}-{detail.section}</h3>
              <p className="text-xs mono">Submitted by {detail.submitted_by_name} · {new Date(detail.submitted_at).toLocaleString()}</p>
              {detail.note && <p className="text-sm mt-1"><b>Educator note:</b> {detail.note}</p>}
            </div>
            <button onClick={()=>setDetail(null)} className="nb-btn nb-btn-ghost">×</button>
          </div>
          <table className="nb-table text-sm">
            <thead><tr><th>Roll</th><th>Name</th><th>Total</th><th>Max</th><th>%</th></tr></thead>
            <tbody>
              {detail.entries?.map(e=>(
                <tr key={e.student_id}>
                  <td className="mono">{e.roll_no}</td>
                  <td className="font-bold">{e.student_name}</td>
                  <td className="mono">{e.total}</td>
                  <td className="mono">{e.max_total}</td>
                  <td className="mono font-bold">{e.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {detail.status === "pending" && (
            <div className="flex gap-2 pt-2 border-t-2 border-black">
              <button onClick={()=>decide(detail.submission_id, "reject")} className="nb-btn nb-btn-danger flex-1 justify-center"
                      data-testid="sub-detail-reject">Reject</button>
              <button onClick={()=>decide(detail.submission_id, "approve")} className="nb-btn flex-1 justify-center"
                      data-testid="sub-detail-approve"><Send size={14}/>Approve &amp; Push to Parents</button>
            </div>
          )}
        </div>
      </div>
    )}
  </div>;
}

function CashFlow() {
  const [data, setData] = useState({inflows:{fees:0,donations:0,total:0}, outflows:{expenses:0,payroll:0,total:0}, net:0, rows:[]});
  // Iter32 — legacy single-select feeType removed; only multi-select fee filter retained
  const [feeTypes, setFeeTypes] = useState([]);
  const [expCats, setExpCats] = useState([]);
  // multi-select filters
  const [selFeeTypes, setSelFeeTypes] = useState([]);
  const [selExpCats, setSelExpCats] = useState([]);
  const [feeMenuOpen, setFeeMenuOpen] = useState(false);
  const [expMenuOpen, setExpMenuOpen] = useState(false);
  // Iter32 — donations toggle + staff multi-select
  const [showDonations, setShowDonations] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [selStaff, setSelStaff] = useState([]);
  const [staffMenuOpen, setStaffMenuOpen] = useState(false);
  const [fromDate, setFrom] = useState("");
  const [toDate, setTo] = useState("");
  const [month, setMonth] = useState("");
  const [divisionId, setDivisionId] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [includePayroll, setIncludePayroll] = useState(true);
  // Iter32 — Ledger picker disabled (kept in state for future re-enable)
  const [ledgers, setLedgers] = useState([]);
  const [ledgerId, setLedgerId] = useState("");
  const load = () => {
    const params = {};
    // Iter32.1 — Month overrides free-form date range on the GET query
    if (month) {
      params.from_date = `${month}-01`;
      // last day of month via next-month-day-0
      const [y, m] = month.split("-").map(Number);
      const last = new Date(y, m, 0).getDate();
      params.to_date = `${month}-${String(last).padStart(2,"0")}`;
    } else {
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
    }
    api.get("/cashflow", { params }).then(r=>setData(r.data));
  };
  useEffect(()=>{
    api.get("/settings").then(r=>{
      setFeeTypes(r.data.fee_types || []);
      setExpCats(r.data.expense_categories || []);
    });
    api.get("/finance-settings/divisions").then(r=>setDivisions(r.data)).catch(()=>{});
    api.get("/finance-settings/ledger-types").then(r=>setLedgers(r.data)).catch(()=>{});
    // Iter32 — pull staff roster for payroll multi-select
    api.get("/staff").then(r=>setStaffList(r.data)).catch(()=>{});
  }, []);
  useEffect(()=>{ load(); }, [fromDate, toDate, month]);   // eslint-disable-line

  // Iter32 — Report label & adaptive display based on active selections
  const hasInFilter = selFeeTypes.length > 0 || showDonations;
  const hasOutFilter = selExpCats.length > 0 || selStaff.length > 0 || includePayroll;
  const cashInOnly = hasInFilter && !hasOutFilter;
  const cashOutOnly = hasOutFilter && !hasInFilter;
  const reportLabel = cashInOnly ? "Cash-In Report"
    : cashOutOnly ? "Cash-Out Report"
    : "Departmental Cash Flow";

  const printSummary = async () => {
    try {
      const res = await api.post("/cashflow/print-summary",
        {month: month || null, division_id: divisionId || null,
         ledger_id: ledgerId || null,
         fee_types: selFeeTypes.length ? selFeeTypes : null,
         expense_categories: selExpCats.length ? selExpCats : null,
         staff_ids: selStaff.length ? selStaff : null,
         show_donations: showDonations,
         include_payroll: includePayroll},
        {responseType: "blob"});
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const label = reportLabel.replace(/\W+/g, "_");
      a.href = url; a.download = `${label}_${(month||'all')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const TYPE_STYLE = {
    FEE: "bg-[#86EFAC]", DONATION: "bg-[#FDE047]",
    EXPENSE: "bg-[#FCA5A5]", PAYROLL: "bg-[#C4B5FD]",
  };
  // Iter32 — Direct-selection filter logic (Ledger picker disabled)
  let inflowRows = data.rows.filter(r => r.type === "FEE" || r.type === "DONATION");
  let outflowRows = data.rows.filter(r => r.type === "EXPENSE" || r.type === "PAYROLL");
  // Fee-type multi-select
  if (selFeeTypes.length) {
    inflowRows = inflowRows.filter(r => {
      if (r.type === "DONATION") return selFeeTypes.includes("Donations");
      const keys = Object.keys(r.breakdown || {});
      if (keys.length === 0) return true;
      return keys.some(k => selFeeTypes.includes(k));
    });
  }
  // Show Donations toggle
  if (!showDonations) inflowRows = inflowRows.filter(r => r.type !== "DONATION");
  // Expense category multi-select
  if (selExpCats.length) {
    outflowRows = outflowRows.filter(r =>
      r.type === "PAYROLL" ? true : selExpCats.includes(r.category));
  }
  // Staff multi-select — restricts PAYROLL rows to selected staff
  if (selStaff.length) {
    outflowRows = outflowRows.filter(r =>
      r.type === "PAYROLL" ? selStaff.includes(r.beneficiary_id) : true);
  }
  // Include Payroll toggle
  if (!includePayroll) outflowRows = outflowRows.filter(r => r.type !== "PAYROLL");
  // Adaptive layout — hide fully-empty side if the other has filter intent
  if (cashInOnly) outflowRows = [];
  if (cashOutOnly) inflowRows = [];

  const toggle = (arr, setter) => (v) => {
    const s = new Set(arr); s.has(v) ? s.delete(v) : s.add(v);
    setter([...s]);
  };

  return <div className="space-y-6">
    <div className="nb-card p-5 bg-[#FDE047] flex flex-wrap gap-3 items-end"
         data-testid="cf-filter-bar">
      {/* Iter32.1 — Ledger picker re-enabled per user directive */}
      <div><label className="nb-label">Ledger / Cash Account</label>
        <select className="nb-select mt-1" value={ledgerId}
                onChange={e=>setLedgerId(e.target.value)}
                data-testid="cf-ledger-id">
          <option value="">All ledgers</option>
          {ledgers.map(l => {
            const badge = l.cash_in_types?.length && (l.cash_out_types?.length || l.payroll_staff_ids?.length)
              ? "Both" : l.cash_in_types?.length ? "Cash-In" : "Cash-Out";
            return <option key={l.ledger_id} value={l.ledger_id}>
              {l.name} · {badge}
            </option>;
          })}
        </select></div>
      <div><label className="nb-label">Fee Types (Cash-In)</label>
        <div className="relative">
          <button type="button" className="nb-select mt-1 text-left"
                  onClick={()=>setFeeMenuOpen(x=>!x)}
                  data-testid="cf-fee-types-toggle">
            {selFeeTypes.length === 0 ? "All" : `${selFeeTypes.length} selected`}
          </button>
          {feeMenuOpen && (
            <div className="absolute z-30 mt-1 nb-card bg-white p-2 max-h-60 overflow-y-auto min-w-[200px]"
                 data-testid="cf-fee-types-menu">
              {feeTypes.map(t => (
                <label key={t} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-100 p-1 rounded">
                  <input type="checkbox" checked={selFeeTypes.includes(t)}
                         onChange={()=>toggle(selFeeTypes, setSelFeeTypes)(t)}
                         data-testid={`cf-fee-type-cb-${t}`}/>
                  <span>{t}</span>
                </label>
              ))}
              <button onClick={()=>setFeeMenuOpen(false)} className="nb-btn nb-btn-ghost w-full mt-1 text-xs justify-center">Done</button>
            </div>
          )}
        </div>
      </div>
      {/* Iter31 — Expense Categories multi-select checkbox filter */}
      <div><label className="nb-label">Expense Cats (Cash-Out)</label>
        <div className="relative">
          <button type="button" className="nb-select mt-1 text-left"
                  onClick={()=>setExpMenuOpen(x=>!x)}
                  data-testid="cf-exp-cats-toggle">
            {selExpCats.length === 0 ? "All" : `${selExpCats.length} selected`}
          </button>
          {expMenuOpen && (
            <div className="absolute z-30 mt-1 nb-card bg-white p-2 max-h-60 overflow-y-auto min-w-[200px]"
                 data-testid="cf-exp-cats-menu">
              {expCats.map(t => (
                <label key={t} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-100 p-1 rounded">
                  <input type="checkbox" checked={selExpCats.includes(t)}
                         onChange={()=>toggle(selExpCats, setSelExpCats)(t)}
                         data-testid={`cf-exp-cat-cb-${t}`}/>
                  <span>{t}</span>
                </label>
              ))}
              <button onClick={()=>setExpMenuOpen(false)} className="nb-btn nb-btn-ghost w-full mt-1 text-xs justify-center">Done</button>
            </div>
          )}
        </div>
      </div>
      {/* Iter32 — Staff Roster multi-select (departmental payroll aggregation) */}
      <div><label className="nb-label">Staff Roster (Payroll)</label>
        <div className="relative">
          <button type="button" className="nb-select mt-1 text-left"
                  onClick={()=>setStaffMenuOpen(x=>!x)}
                  data-testid="cf-staff-toggle">
            {selStaff.length === 0 ? "All staff" : `${selStaff.length} selected`}
          </button>
          {staffMenuOpen && (
            <div className="absolute z-30 mt-1 nb-card bg-white p-2 max-h-72 overflow-y-auto min-w-[240px]"
                 data-testid="cf-staff-menu">
              {(staffList||[]).map(s => (
                <label key={s.beneficiary_id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-100 p-1 rounded">
                  <input type="checkbox" checked={selStaff.includes(s.beneficiary_id)}
                         onChange={()=>toggle(selStaff, setSelStaff)(s.beneficiary_id)}
                         data-testid={`cf-staff-cb-${s.beneficiary_id}`}/>
                  <span className="truncate">{s.name}{s.designation ? ` · ${s.designation}` : ""}</span>
                </label>
              ))}
              {(staffList||[]).length === 0 && <div className="text-xs text-slate-500 p-2">No staff records</div>}
              <button onClick={()=>setStaffMenuOpen(false)} className="nb-btn nb-btn-ghost w-full mt-1 text-xs justify-center">Done</button>
            </div>
          )}
        </div>
      </div>
      {/* Iter32 — Show Donations toggle */}
      <label className="flex items-center gap-2 text-xs mt-6" data-testid="cf-show-donations">
        <input type="checkbox" checked={showDonations}
               onChange={e=>setShowDonations(e.target.checked)}/>
        Show Donations
      </label>
      <div><label className="nb-label">Month</label>
        <input type="month" className="nb-input mt-1" value={month}
               onChange={e=>setMonth(e.target.value)} data-testid="cf-month"/></div>
      <div><label className="nb-label">Division</label>
        <select className="nb-select mt-1" value={divisionId}
                onChange={e=>setDivisionId(e.target.value)}
                data-testid="cf-division">
          <option value="">All divisions</option>
          {divisions.map(d => <option key={d.division_id} value={d.division_id}>{d.name}</option>)}
        </select></div>
      <div><label className="nb-label">From</label>
        <input type="date" className="nb-input mt-1" value={fromDate}
               onChange={e=>setFrom(e.target.value)} data-testid="cf-from"/></div>
      <div><label className="nb-label">To</label>
        <input type="date" className="nb-input mt-1" value={toDate}
               onChange={e=>setTo(e.target.value)} data-testid="cf-to"/></div>
      <label className="flex items-center gap-2 text-xs pb-2">
        <input type="checkbox" checked={includePayroll}
               onChange={e=>setIncludePayroll(e.target.checked)}
               data-testid="cf-include-payroll"/>
        Include approved payroll (Cash-Out)
      </label>
      <button className="nb-btn ml-auto" onClick={printSummary}
              data-testid="cf-print-summary">
        <Download size={14}/>Print Report
      </button>
      <div className="w-full text-right">
        <div className="nb-label">{reportLabel} · Net</div>
        <div className={`display text-3xl font-black ${data.net>=0?"text-emerald-700":"text-rose-700"}`}
             data-testid="cf-net">₹{(data.net||0).toLocaleString()}</div>
      </div>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="nb-card-sm p-3 bg-[#86EFAC]">
        <div className="nb-label text-[10px]">Fees Received (Inflow)</div>
        <div className="font-bold mono" data-testid="cf-fees">₹{data.inflows.fees.toLocaleString()}</div>
      </div>
      <div className="nb-card-sm p-3 bg-[#FDE047]">
        <div className="nb-label text-[10px]">Donations (Inflow)</div>
        <div className="font-bold mono" data-testid="cf-donations">₹{data.inflows.donations.toLocaleString()}</div>
      </div>
      <div className="nb-card-sm p-3 bg-[#FCA5A5]">
        <div className="nb-label text-[10px]">Approved Expenses (Outflow)</div>
        <div className="font-bold mono" data-testid="cf-expenses">₹{data.outflows.expenses.toLocaleString()}</div>
      </div>
      <div className="nb-card-sm p-3 bg-[#C4B5FD]">
        <div className="nb-label text-[10px]">Paid Payroll (Outflow)</div>
        <div className="font-bold mono" data-testid="cf-payroll">₹{data.outflows.payroll.toLocaleString()}</div>
      </div>
    </div>

    <div className="text-xs text-slate-600 nb-card-sm p-3 bg-white">
      <b>Salary advances are intentionally excluded</b> — they are settled through payroll deductions and do not represent independent cash flow events.
    </div>

    <div className={`grid gap-6 ${cashInOnly || cashOutOnly ? "grid-cols-1" : "lg:grid-cols-2"}`}
         data-testid="cf-panels">
      {!cashOutOnly && (
      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-[#86EFAC] flex justify-between items-center">
          <h3 className="display text-lg font-bold">Inflows ({inflowRows.length})</h3>
          <span className="mono font-bold">₹{data.inflows.total.toLocaleString()}</span>
        </div>
        <div className="max-h-[45vh] overflow-auto">
          <table className="nb-table">
            <thead><tr><th>Date</th><th>Type</th><th>Party</th><th>Division</th><th>Ref</th><th>Amount</th></tr></thead>
            <tbody>
              {inflowRows.map((r,i)=>(
                <tr key={`${r.ref}-${i}`} data-testid={`cf-in-${r.ref}`}>
                  <td className="mono text-xs">{r.date}</td>
                  <td><span className={`nb-badge ${TYPE_STYLE[r.type]}`}>{r.type}</span></td>
                  <td className="font-bold">{r.party}</td>
                  <td className="text-xs">{r.division || "—"}</td>
                  <td className="mono text-xs">{r.ref}</td>
                  <td className="mono font-bold">₹{r.amount.toLocaleString()}</td>
                </tr>
              ))}
              {inflowRows.length===0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No inflows in range</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {!cashInOnly && (
      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-[#FCA5A5] flex justify-between items-center">
          <h3 className="display text-lg font-bold">Outflows ({outflowRows.length})</h3>
          <span className="mono font-bold">₹{data.outflows.total.toLocaleString()}</span>
        </div>
        <div className="max-h-[45vh] overflow-auto">
          <table className="nb-table">
            <thead><tr><th>Date</th><th>Type</th><th>Party</th><th>Division</th><th>Ref</th><th>Amount</th></tr></thead>
            <tbody>
              {outflowRows.map((r,i)=>(
                <tr key={`${r.ref}-${i}`} data-testid={`cf-out-${r.ref}`}>
                  <td className="mono text-xs">{r.date}</td>
                  <td><span className={`nb-badge ${TYPE_STYLE[r.type]}`}>{r.type}</span></td>
                  <td className="font-bold">{r.party}</td>
                  <td className="text-xs">{r.division || "—"}</td>
                  <td className="mono text-xs">{r.ref}</td>
                  <td className="mono font-bold">₹{r.amount.toLocaleString()}</td>
                </tr>
              ))}
              {outflowRows.length===0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No outflows in range</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  </div>;
}

function Ledger() { return <CashFlow/>; }

function Donations() {
  // Legacy write-form retained (unrouted) — donations now live under Accountant.
  const [rows, setRows] = useState([]); const [f, setF] = useState({donor_name:"", donor_email:"", amount:"", message:""});
  const load = ()=> api.get("/donations").then(r=>setRows(r.data));
  useEffect(()=>{ load(); }, []);
  const submit = async (e) => { e.preventDefault();
    await api.post("/donations", {...f, amount:parseFloat(f.amount)});
    toast.success("Donation recorded"); setF({donor_name:"",donor_email:"",amount:"",message:""}); load();
  };
  const dl = async (id) => {
    const r = await api.get(`/donations/${id}/certificate`, {responseType:"blob"});
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  return <div className="grid lg:grid-cols-3 gap-6">
    <form onSubmit={submit} className="nb-card p-6 bg-[#FDE047] space-y-3 h-fit">
      <h3 className="display text-xl font-bold">Record Donation</h3>
      <input className="nb-input" placeholder="Donor name" required value={f.donor_name} onChange={e=>setF({...f,donor_name:e.target.value})}/>
      <input type="email" className="nb-input" placeholder="Email" value={f.donor_email} onChange={e=>setF({...f,donor_email:e.target.value})}/>
      <input type="number" className="nb-input" placeholder="Amount" required value={f.amount} onChange={e=>setF({...f,amount:e.target.value})}/>
      <textarea className="nb-textarea" placeholder="Message" value={f.message} onChange={e=>setF({...f,message:e.target.value})}/>
      <button className="nb-btn w-full justify-center"><Plus size={16}/>Save</button>
    </form>
    <div className="nb-card p-0 overflow-hidden lg:col-span-2">
      <table className="nb-table"><thead><tr><th>Donor</th><th>Amount</th><th>Message</th><th>Cert</th></tr></thead><tbody>
        {rows.map(d=>(<tr key={d.donation_id}><td className="font-bold">{d.donor_name}</td>
          <td className="mono">₹{d.amount}</td><td className="text-sm">{d.message}</td>
          <td><button onClick={()=>dl(d.donation_id)} className="nb-btn nb-btn-ghost"><Download size={14}/></button></td></tr>))}
      </tbody></table>
    </div>
  </div>;
}

/* Read-only Donations Log for School Admin. Donations are RECORDED by
   Accountant; Admin only reads them here. */
/* Iter16 — Historical Edit/Delete buttons gated by Global Admin override.
   overrideKey = "historical_donations" | "historical_expenses" | "historical_payroll" */
function useOverrideActive(overrideKey) {
  const [active, setActive] = useState(false);
  useEffect(() => {
    let cancel = false;
    api.get("/overrides/my").then(r => {
      if (cancel) return;
      const info = r.data?.[overrideKey];
      setActive(!!(info && info.active));
    }).catch(() => setActive(false));
    return () => { cancel = true; };
  }, [overrideKey]);
  return active;
}

function HistoricalActions({ overrideKey, row, editableFields, endpoint, onDone, testPrefix }) {
  const active = useOverrideActive(overrideKey);
  const [editing, setEditing] = useState(null);
  if (!active) return null;
  const startEdit = () => {
    const draft = {};
    editableFields.forEach(f => { draft[f] = row[f] ?? ""; });
    draft.audit_note = "";
    setEditing(draft);
  };
  const commit = async () => {
    if (!editing.audit_note.trim()) return toast.error("Audit note required");
    try {
      await api.patch(endpoint, editing);
      toast.success("Historical edit saved");
      setEditing(null); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    const note = window.prompt("Delete reason (audit note required):") || "";
    if (!note.trim()) return toast.error("Audit note required");
    try {
      await api.delete(endpoint, { params: { audit_note: note.trim() } });
      toast.success("Deleted"); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <>
      <button onClick={startEdit}
              className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
              data-testid={`${testPrefix}-edit-${row[Object.keys(row)[0]]}`}>
        Edit
      </button>
      <button onClick={del}
              className="nb-btn nb-btn-danger px-2 py-1 text-xs ml-1"
              data-testid={`${testPrefix}-del-${row[Object.keys(row)[0]]}`}>
        <Trash2 size={12}/>
      </button>
      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" data-testid={`${testPrefix}-edit-modal`}>
          <div className="nb-card p-6 bg-white w-full max-w-md space-y-3">
            <h3 className="display text-lg font-bold">Historical Edit</h3>
            <p className="text-[10px] text-slate-600">Every change is logged with your user ID and the audit note below.</p>
            {editableFields.map(f => (
              <label key={f} className="block"><span className="nb-label">{f}</span>
                <input className="nb-input mt-1" value={editing[f]}
                       onChange={e=>setEditing({...editing, [f]: e.target.value})}
                       data-testid={`${testPrefix}-edit-input-${f}`}/></label>
            ))}
            <label className="block"><span className="nb-label">Audit Note (required)</span>
              <textarea className="nb-input mt-1" rows={2} value={editing.audit_note}
                        onChange={e=>setEditing({...editing, audit_note: e.target.value})}
                        data-testid={`${testPrefix}-edit-note`}/></label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={()=>setEditing(null)} className="nb-btn nb-btn-ghost">Cancel</button>
              <button onClick={commit} className="nb-btn" data-testid={`${testPrefix}-edit-save`}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DonationsLog() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/donations").then(r=>setRows(r.data));
  useEffect(()=>{ load(); }, []);
  const dl = async (id) => {
    const r = await api.get(`/donations/${id}/certificate`, {responseType:"blob"});
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  const total = rows.reduce((a,x)=>a + (parseFloat(x.amount)||0), 0);
  return <div className="space-y-4">
    <div className="nb-card p-5 bg-[#FDE047] flex flex-wrap items-center gap-3">
      <Heart size={20}/>
      <div>
        <div className="display text-xl font-bold">Donations Log (read-only)</div>
        <p className="text-sm mt-1">All donations recorded through the Accountant console. Certificates can be re-downloaded from here.</p>
      </div>
      <div className="ml-auto text-right">
        <div className="nb-label">Total Received</div>
        <div className="display text-3xl font-black mono" data-testid="dl-total">₹{total.toLocaleString()}</div>
      </div>
    </div>
    <div className="nb-card p-0 overflow-hidden">
      <table className="nb-table">
        <thead><tr><th>Date</th><th>Donor</th><th>Mobile</th><th>Division</th><th>Amount</th><th>Message</th><th></th></tr></thead>
        <tbody>
          {rows.map(d=>(
            <tr key={d.donation_id} data-testid={`dl-row-${d.donation_id}`}>
              <td className="mono text-xs">{(d.created_at||"").slice(0,10)}</td>
              <td className="font-bold">{d.donor_name}</td>
              <td className="mono text-xs">{d.mobile_number || "—"}</td>
              <td className="text-xs">{d.division_name || "—"}</td>
              <td className="mono">₹{(d.amount||0).toLocaleString()}</td>
              <td className="text-sm">{d.message || "—"}</td>
              <td>
                <button onClick={()=>dl(d.donation_id)}
                        className="nb-btn nb-btn-secondary px-2 py-1 text-xs">
                  <Download size={12}/> Certificate
                </button>
                <HistoricalActions
                  overrideKey="historical_donations"
                  row={{donation_id: d.donation_id, amount: d.amount, donor_name: d.donor_name, message: d.message || ""}}
                  editableFields={["donor_name", "amount", "message"]}
                  endpoint={`/donations/${d.donation_id}`}
                  onDone={load}
                  testPrefix="donation"/>
              </td>
            </tr>
          ))}
          {rows.length===0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No donations recorded yet</td></tr>}
        </tbody>
      </table>
    </div>
  </div>;
}

function ExpenseLog() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [divisions, setDivisions] = useState([]);   // Iter31 — passed to ExpenseEditModal
  const [ledgers, setLedgers] = useState([]);       // Iter31
  const [monthFilter, setMonthFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const load = async () => {
    setRows((await api.get("/expenses")).data);
    try {
      const s = (await api.get("/settings")).data;
      setCats(s.expense_categories || []);
    } catch { /* ignore */ }
    try { setDivisions((await api.get("/finance-settings/divisions")).data); } catch { /* ignore */ }
    try { setLedgers((await api.get("/finance-settings/ledger-types")).data); } catch { /* ignore */ }
  };
  useEffect(()=>{ load(); }, []);
  const decide = async (id, action) => { await api.post(`/expenses/${id}/decision`, {action}); load(); };
  const filtered = rows.filter(r => {
    if (monthFilter && (r.created_at||"").slice(0,7) !== monthFilter) return false;
    if (catFilter && r.category !== catFilter) return false;
    if (statusFilter && r.status !== statusFilter) return false;
    return true;
  });
  const printSummary = async () => {
    try {
      const res = await api.post("/expenses/print-summary",
        {month: monthFilter || null, category: catFilter || null, status: statusFilter || null},
        {responseType: "blob"});
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `expense_summary_${(monthFilter||'all')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const totalFiltered = filtered.reduce((s,r)=>s + parseFloat(r.amount||0), 0);
  return <div className="space-y-3" data-testid="admin-expense-log">
    <div className="nb-card p-3 flex flex-wrap items-center gap-2 bg-white">
      <span className="nb-label">Filters:</span>
      <input type="month" className="nb-input text-xs w-40" value={monthFilter}
             onChange={e=>setMonthFilter(e.target.value)}
             data-testid="exp-log-month"/>
      <select className="nb-select text-xs" value={catFilter}
              onChange={e=>setCatFilter(e.target.value)}
              data-testid="exp-log-category">
        <option value="">All categories</option>
        {cats.map(c=><option key={c} value={c}>{c}</option>)}
      </select>
      <select className="nb-select text-xs" value={statusFilter}
              onChange={e=>setStatusFilter(e.target.value)}
              data-testid="exp-log-status">
        <option value="">All statuses</option>
        <option value="pending">Pending</option>
        <option value="approved">Approved</option>
        <option value="rejected">Rejected</option>
      </select>
      {(monthFilter||catFilter||statusFilter) &&
        <button className="nb-btn nb-btn-ghost text-xs"
                onClick={()=>{setMonthFilter("");setCatFilter("");setStatusFilter("");}}>Clear</button>}
      <span className="ml-auto text-xs mono">Filtered total: ₹{totalFiltered.toLocaleString()}</span>
      <button className="nb-btn" onClick={printSummary}
              data-testid="exp-log-print"><Download size={14}/>Print Summary</button>
    </div>
    <div className="nb-card p-0 overflow-hidden">
      <table className="nb-table"><thead><tr><th>Date</th><th>Category</th><th>By</th><th>Vendor</th><th>Amount</th><th>Status</th><th>Remarks</th><th>Action</th></tr></thead><tbody>
        {filtered.map(r=>(<tr key={r.expense_id} className={r.high_priority && r.status==="pending"?"bg-rose-50":""}>
          <td className="mono text-xs">{r.created_at.slice(0,10)}</td>
          <td>{r.category}{r.high_priority && <span className="nb-badge bg-[#FCA5A5] ml-2">HIGH</span>}</td>
          <td className="text-sm">{r.created_by_name}</td>
          <td className="text-xs">{r.vendor_name || "—"}</td>
          <td className="mono">₹{r.amount}</td>
          <td><span className={`nb-badge ${r.status==="approved"?"bg-[#86EFAC]":r.status==="rejected"?"bg-[#FCA5A5]":"bg-[#FDE047]"}`}>{r.status}</span></td>
          <td className="text-xs">{r.remarks || r.description || "—"}</td>
          <td>
            {r.status==="pending" && <>
              <button onClick={()=>decide(r.expense_id,"approve")} className="nb-btn nb-btn-secondary">Approve</button>
              <button onClick={()=>decide(r.expense_id,"reject")} className="nb-btn nb-btn-danger ml-1">Reject</button>
            </>}
            <ExpenseEditModal expense={r} cats={cats} divisions={divisions} ledgers={ledgers} onDone={load}/>
          </td></tr>))}
        {filtered.length===0 && <tr><td colSpan={8} className="text-center py-6 text-slate-500">No expenses match the filters</td></tr>}
      </tbody></table>
    </div>
  </div>;
}

/* Iter31 — Full-schema expense edit modal (parity with Accountant's Log Expense) */
function ExpenseEditModal({ expense, cats=[], divisions=[], ledgers=[], onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    category: expense.category || "",
    amount: expense.amount ?? "",
    vendor_name: expense.vendor_name || expense.vendor || "",
    payee_mobile: expense.payee_mobile || "",
    division_id: expense.division_id || "",
    ledger_id: expense.ledger_id || "",
    description: expense.description || "",
    remarks: expense.remarks || "",
    revision_remarks: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.revision_remarks.trim()) return toast.error("Revision remarks required");
    setSaving(true);
    try {
      await api.patch(`/expenses/${expense.expense_id}`, {
        ...f, amount: parseFloat(f.amount) || 0,
      });
      toast.success("Expense updated"); setOpen(false); onDone && onDone();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed";
      toast.error(msg);
    }
    setSaving(false);
  };
  return (<>
    <button onClick={()=>setOpen(true)} className="nb-btn nb-btn-secondary text-xs ml-1"
            data-testid={`expense-edit-${expense.expense_id}`}>Edit</button>
    {open && (
      <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={()=>setOpen(false)}>
        <div className="nb-card p-6 bg-white max-w-lg w-full space-y-2 max-h-[90vh] overflow-y-auto"
             onClick={e=>e.stopPropagation()} data-testid="expense-edit-modal">
          <h3 className="display text-xl font-bold">Edit Expense (Historical Override)</h3>
          <p className="text-xs text-slate-600">Full form schema matches the Accountant&apos;s Log Expense screen. Revision Remarks are mandatory and persisted to the audit trail.</p>
          <select className="nb-select" value={f.category} onChange={e=>setF({...f,category:e.target.value})}
                  data-testid="exp-edit-category">
            <option value="">— Category —</option>
            {cats.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="number" step="0.01" className="nb-input" placeholder="Amount"
                 value={f.amount} onChange={e=>setF({...f,amount:e.target.value})}
                 data-testid="exp-edit-amount"/>
          <input className="nb-input" placeholder="Vendor / Subcontractor Name"
                 value={f.vendor_name} onChange={e=>setF({...f,vendor_name:e.target.value})}
                 data-testid="exp-edit-vendor"/>
          <input className="nb-input" placeholder="Payee Mobile Number"
                 value={f.payee_mobile} onChange={e=>setF({...f,payee_mobile:e.target.value})}
                 data-testid="exp-edit-mobile"/>
          <select className="nb-select" value={f.division_id}
                  onChange={e=>setF({...f,division_id:e.target.value})}
                  data-testid="exp-edit-division">
            <option value="">— Division / Branch —</option>
            {divisions.map(d => <option key={d.division_id} value={d.division_id}>{d.name}</option>)}
          </select>
          <select className="nb-select" value={f.ledger_id||""}
                  onChange={e=>setF({...f,ledger_id:e.target.value})}
                  data-testid="exp-edit-ledger">
            <option value="">— Ledger (optional) —</option>
            {ledgers.map(l => <option key={l.ledger_id} value={l.ledger_id}>{l.name}</option>)}
          </select>
          <textarea className="nb-textarea" placeholder="Description"
                    value={f.description} onChange={e=>setF({...f,description:e.target.value})}/>
          <input className="nb-input" placeholder="Remarks"
                 value={f.remarks} onChange={e=>setF({...f,remarks:e.target.value})}
                 data-testid="exp-edit-remarks"/>
          <div>
            <label className="nb-label text-red-700">Revision Remarks (mandatory — appended to audit log)</label>
            <textarea className="nb-textarea" rows={2}
                      placeholder="Why is this being edited?"
                      value={f.revision_remarks}
                      onChange={e=>setF({...f, revision_remarks:e.target.value})}
                      data-testid="exp-edit-revision"/>
          </div>
          <div className="flex gap-2">
            <button onClick={()=>setOpen(false)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
            <button onClick={save} disabled={saving} className="nb-btn flex-1 justify-center"
                    data-testid="exp-edit-save">{saving ? "Saving…" : "Save & Log Revision"}</button>
          </div>
        </div>
      </div>
    )}
  </>);
}


/* ============================================================
   Iter16 — ID Cards Suite: template picker, roster + doc upload, batch PDF
============================================================ */
export function IdCards() {
  const [layouts, setLayouts] = useState([]);
  const [palette, setPalette] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [settings, setSettings] = useState({grades:[], sections:[]});
  const [tab, setSubTab] = useState("students");
  const [busy, setBusy] = useState(false);
  const [printLayout, setPrintLayout] = useState("a4_grid");
  const [outputFormat, setOutputFormat] = useState("pdf");
  const loadedRef = useRef(false);

  useEffect(()=>{
    if (loadedRef.current) return;   // guard StrictMode double-run overwriting user edits
    loadedRef.current = true;
    Promise.all([
      api.get("/id-cards/layouts"),
      api.get("/id-cards/config"),
      api.get("/settings"),
    ]).then(([t, c, s]) => {
      setLayouts(t.data.layouts || []);
      setPalette(t.data.palette || []);
      setCfg(c.data);
      setSettings(s.data);
      if (c.data?.print_layout) setPrintLayout(c.data.print_layout);
    });
  }, []);
  const saveCfg = async () => {
    setBusy(true);
    try { await api.put("/id-cards/config", {...cfg, print_layout: printLayout}); toast.success("Template settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const generate = async (kind, filters = {}) => {
    try {
      const res = await api.post("/id-cards/generate",
        { kind, selected_layout: cfg.selected_layout, accent_color: cfg.accent_color,
          print_layout: printLayout, output_format: outputFormat, ...filters },
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const ext = outputFormat === "png" ? (res.data.type.includes("zip") ? "zip" : "png") : "pdf";
      const a = document.createElement("a");
      a.href = url; a.download = `idcards_${kind}_${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!cfg) return <div className="mono">Loading…</div>;
  const accent = cfg.accent_color || "#1E3A8A";
  return (
    <div className="space-y-5" data-testid="idcards-panel">
      <div className="nb-card p-5 bg-[#C4B5FD] flex flex-wrap gap-3 items-end">
        <div>
          <div className="display text-xl font-bold">ID Card Template Customizer</div>
          <p className="text-sm mt-1">Choose a layout + accent colour, tweak visible fields, then generate CR-80 cards for any student or staff cohort.</p>
        </div>
        <button onClick={saveCfg} disabled={busy} className="ml-auto nb-btn"
                data-testid="idc-save-cfg">Save template settings</button>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="nb-card p-5 space-y-4">
          <div>
            <h4 className="display text-lg font-bold">1. Choose Layout Structure</h4>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {layouts.map(t => (
                <button key={t.id}
                        onClick={()=>setCfg({...cfg, selected_layout: t.id})}
                        className={`nb-card-sm p-2 text-left transition-all ${cfg.selected_layout===t.id?"outline outline-4 outline-[#3D1461] bg-[#EDE9FE]":""}`}
                        data-testid={`idc-layout-${t.id}`}>
                  <IdTemplatePreview t={t} accent={accent}/>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bold text-xs">{t.name}</span>
                    {cfg.selected_layout===t.id
                      ? <span className="text-[9px] mono px-1 rounded bg-[#3D1461] text-white">SELECTED</span>
                      : <span className="text-[9px] mono px-1 rounded bg-slate-200">{t.orientation==="landscape"?"▭":"▯"}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <h4 className="display text-lg font-bold">Card Accent Color</h4>
            <div className="flex flex-wrap gap-2 mt-2 items-center" data-testid="idc-swatches">
              {palette.map(p => (
                <button key={p.hex} title={p.name}
                        onClick={()=>setCfg({...cfg, accent_color: p.hex})}
                        className={`w-9 h-9 rounded-full border-2 border-black ${accent.toLowerCase()===p.hex.toLowerCase()?"ring-4 ring-offset-1 ring-[#3D1461]":""}`}
                        style={{background:p.hex}}
                        data-testid={`idc-swatch-${p.hex.replace('#','')}`}/>
              ))}
              <label className="flex items-center gap-1 text-xs ml-1">
                <span className="nb-label">Custom</span>
                <input type="color" value={accent}
                       onChange={e=>setCfg({...cfg, accent_color: e.target.value})}
                       className="w-9 h-9 rounded border-2 border-black cursor-pointer"
                       data-testid="idc-accent-custom"/>
                <input type="text" value={accent}
                       onChange={e=>setCfg({...cfg, accent_color: e.target.value})}
                       className="nb-input w-24 mono text-xs" data-testid="idc-accent-hex"/>
              </label>
            </div>
          </div>
        </div>
        <div className="nb-card p-5 space-y-2">
          <h4 className="display text-lg font-bold">2. Visible Fields</h4>
          {[
            ["show_barcode", "Barcode (Code-128)"],
            ["show_logo", "School Logo"],
            ["show_rfid", "RFID UID text (below barcode)"],
            ["show_dob", "Date of Birth"],
            ["show_blood_group", "Blood Group"],
            ["show_emergency_contact", "Emergency Contact"],
            ["show_parent_name", "Parent Name (students only)"],
            ["show_parent_mobile", "Parent Mobile (students only)"],
            ["show_address", "School Address"],
          ].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!cfg[k]}
                     onChange={e=>setCfg({...cfg, [k]: e.target.checked})}
                     data-testid={`idc-fld-${k}`}/>
              {lbl}
            </label>
          ))}
          <label className="block pt-1"><span className="nb-label">Validity (years from today)</span>
            <input type="number" className="nb-input mt-1 w-32 mono" min="1" max="10"
                   value={cfg.validity_years || 4}
                   onChange={e=>setCfg({...cfg, validity_years: parseInt(e.target.value)||4})}
                   data-testid="idc-validity"/></label>
        </div>
      </div>
      <div className="nb-card p-5 space-y-3">
        <h4 className="display text-lg font-bold">3. Print Layout / Media</h4>
        <div className="flex flex-wrap gap-4 items-end">
          <div><label className="nb-label">Layout / Media</label>
            <select className="nb-select mt-1 w-72" value={printLayout}
                    onChange={e=>setPrintLayout(e.target.value)} data-testid="idc-print-layout">
              <option value="a4_grid">A4 Grid (cards per sheet, trim lines)</option>
              <option value="epson_2up">Epson Tray (2-Up PVC Cards · 147×247mm)</option>
              <option value="single_cr80">Single Card CR-80 (54×85.6mm, thermal PVC)</option>
            </select></div>
          <div><label className="nb-label">File Format</label>
            <select className="nb-select mt-1 w-40" value={outputFormat}
                    onChange={e=>setOutputFormat(e.target.value)} data-testid="idc-output-format">
              <option value="pdf">PDF</option>
              <option value="png">PNG (external print)</option>
            </select></div>
          <p className="text-xs text-slate-600 flex-1 min-w-[12rem]">
            Epson tray places cards at exact PVC slot coordinates. PNG exports one image per card (zipped for a batch) for any external printer.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        {[["students","Students"],["staff","Staff"]].map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)}
                  className={`nb-btn ${tab===id?"":"nb-btn-ghost"}`}
                  data-testid={`idc-tab-${id}`}>{label}</button>
        ))}
      </div>
      {tab==="students" && <IdCardStudentRoster settings={settings} onGenerate={generate}/>}
      {tab==="staff" && <IdCardStaffRoster settings={settings} onGenerate={generate}/>}
    </div>
  );
}

export function IdTemplatePreview({ t, accent }) {
  const land = t.orientation === "landscape";
  const band = accent || t.band_color || "#1E3A8A";
  const box = { background: "#f8f7f2", border: "2px solid #111",
    width: land ? 86 : 54, height: land ? 54 : 86, borderRadius: 6,
    position: "relative", overflow: "hidden" };
  return (
    <div className="flex items-center justify-center h-24 bg-slate-50 rounded">
      <div style={box} data-testid={`idc-tpl-preview-${t.id}`}>
        {t.id === "layout_vertical_spine" && <div style={{position:"absolute",left:0,top:0,bottom:0,width:"22%",background:band}}/>}
        {t.id === "layout_vertical_wave" && <div style={{position:"absolute",left:0,right:0,top:0,height:"42%",background:band,borderBottomLeftRadius:"60% 40%",borderBottomRightRadius:"50% 30%"}}/>}
        {t.id === "layout_landscape_horizon" && <><div style={{position:"absolute",left:0,right:0,top:0,height:"18%",background:band}}/><div style={{position:"absolute",left:0,right:0,bottom:0,height:"12%",background:band}}/></>}
        {t.id === "layout_vertical_pill" && <div style={{position:"absolute",left:0,right:0,top:0,height:"26%",background:band,borderRadius:"6px 6px 0 0"}}/>}
        <div style={{position:"absolute", left: land?"58%":"30%", top:"30%", width:land?"30%":"46%", height:"34%",
          background:"#cbd5e1", borderRadius: t.photo_style==="circle"?"50%": t.photo_style==="rounded"?6:2, border:"1px solid #111"}}/>
        <div style={{position:"absolute", left:"15%", right:"15%", bottom:"8%", height:"10%", background:"repeating-linear-gradient(90deg,#111 0 2px,transparent 2px 4px)"}}/>
      </div>
    </div>
  );
}

export function IdCardStudentRoster({ settings, onGenerate }) {
  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("");
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [aadFocus, setAadFocus] = useState(false);
  const load = () => {
    const p = {};
    if (cls) p.class_name = cls;
    if (sec) p.section = sec;
    api.get("/students", { params: p }).then(r => setRows(r.data));
  };
  useEffect(()=>{ load(); }, [cls, sec]); // eslint-disable-line
  return (
    <div className="nb-card p-4 space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="nb-label">Grade</label>
          <select className="nb-select mt-1 w-32" value={cls} onChange={e=>setCls(e.target.value)}
                  data-testid="idc-stu-grade">
            <option value="">All</option>
            {settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
          </select></div>
        <div><label className="nb-label">Section</label>
          <select className="nb-select mt-1 w-32" value={sec} onChange={e=>setSec(e.target.value)}
                  data-testid="idc-stu-section">
            <option value="">All</option>
            {settings.sections?.map(s=><option key={s} value={s}>{s}</option>)}
          </select></div>
        <div className="ml-auto flex gap-2">
          <button onClick={()=>onGenerate("student", {class_name: cls || null, section: sec || null})}
                  disabled={rows.length===0}
                  className="nb-btn" data-testid="idc-gen-batch">
            Generate for {rows.length} student{rows.length===1?"":"s"}
          </button>
        </div>
      </div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table text-sm">
          <thead><tr>
            <th>Roll</th><th>Name</th><th>Photo</th><th>Birth Cert</th><th>Gov ID</th><th>Action</th>
          </tr></thead>
          <tbody>
            {rows.map(s => {
              const d = s.docs || {};
              const dot = (v) => <span className={`inline-block w-2 h-2 rounded-full mr-1 ${v?"bg-emerald-500":"bg-rose-500"}`}/>;
              return (
                <tr key={s.student_id} data-testid={`idc-stu-row-${s.student_id}`}>
                  <td className="mono">{s.roll_no}</td>
                  <td className="font-bold">{s.name}</td>
                  <td>{dot(d.photo_data_url || s.student_photo_url)}{(d.photo_data_url||s.student_photo_url)?"Uploaded":"Missing"}</td>
                  <td>{dot(d.birth_cert_data_url || s.birth_certificate_url)}{(d.birth_cert_data_url||s.birth_certificate_url)?"Uploaded":"Missing"}</td>
                  <td>{dot(d.gov_id_data_url || s.government_id_url)}{(d.gov_id_data_url||s.government_id_url)?"Uploaded":"Missing"}</td>
                  <td className="space-x-1">
                    <button onClick={()=>setEdit(s)}
                            className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                            data-testid={`idc-stu-upload-${s.student_id}`}>Upload</button>
                    <button onClick={()=>onGenerate("student", {ids:[s.student_id]})}
                            className="nb-btn px-2 py-1 text-xs"
                            data-testid={`idc-stu-print-${s.student_id}`}>Print</button>
                  </td>
                </tr>
              );
            })}
            {rows.length===0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No students match — pick another grade/section</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" data-testid="idc-stu-upload-modal">
          <div className="nb-card p-6 bg-white w-full max-w-md space-y-3 max-h-[92vh] overflow-y-auto">
            <h3 className="display text-lg font-bold">Upload Documents — {edit.name}</h3>
            <p className="text-xs text-slate-600">Fields below print on the ID card.</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="nb-label">Date of Birth</span>
                <input type="date" className="nb-input mt-1" value={edit.dob || ""}
                       onChange={e=>setEdit({...edit, dob: e.target.value})}
                       data-testid="idc-stu-dob"/></label>
              <label className="block"><span className="nb-label">Blood Group</span>
                <select className="nb-select mt-1" value={edit.blood_group || ""}
                        onChange={e=>setEdit({...edit, blood_group: e.target.value})}
                        data-testid="idc-stu-blood">
                  <option value="">—</option>
                  {["A+","A-","B+","B-","O+","O-","AB+","AB-"].map(b=><option key={b} value={b}>{b}</option>)}
                </select></label>
            </div>
            <label className="block"><span className="nb-label">Home Address</span>
              <textarea rows={2} className="nb-input mt-1" value={edit.address || ""}
                        onChange={e=>setEdit({...edit, address: e.target.value})}
                        data-testid="idc-stu-address"/></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="nb-label">Aadhaar Number</span>
                <input inputMode="numeric" className="nb-input mt-1"
                       value={aadFocus ? (edit.aadhaar_number || "")
                         : (edit.aadhaar_number ? "•••• •••• " + String(edit.aadhaar_number).slice(-4) : "")}
                       onFocus={()=>setAadFocus(true)} onBlur={()=>setAadFocus(false)}
                       onChange={e=>setEdit({...edit, aadhaar_number: e.target.value.replace(/\D/g,"").slice(0,12)})}
                       placeholder="12-digit number" data-testid="idc-stu-aadhaar"/>
                {edit.aadhaar_number && edit.aadhaar_number.length !== 12 &&
                  <span className="text-xs text-rose-600">Must be 12 digits</span>}
              </label>
              <label className="block"><span className="nb-label">SATS Number</span>
                <input className="nb-input mt-1" value={edit.sats_number || ""}
                       onChange={e=>setEdit({...edit, sats_number: e.target.value})}
                       placeholder="Optional" data-testid="idc-stu-sats"/></label>
            </div>
            <label className="block"><span className="nb-label">PEN Number</span>
              <input className="nb-input mt-1" value={edit.pen_number || ""}
                     onChange={e=>setEdit({...edit, pen_number: e.target.value})}
                     placeholder="Optional" data-testid="idc-stu-pen"/></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="nb-label">RFID / Smart-Card UID</span>
                <input className="nb-input mt-1 mono uppercase" value={edit.rfid_uid || ""}
                       onChange={e=>setEdit({...edit, rfid_uid: e.target.value.replace(/[^0-9a-fA-F]/g,"").toUpperCase().slice(0,14)})}
                       placeholder="e.g. B9DFC12BF1" data-testid="idc-stu-rfid"/>
                <span className="text-[10px] text-slate-500">Encoded in the card barcode/QR. Falls back to Student ID if blank.</span>
              </label>
              <label className="block"><span className="nb-label">Name (local script, optional)</span>
                <input className="nb-input mt-1" value={edit.name_local || ""}
                       onChange={e=>setEdit({...edit, name_local: e.target.value})}
                       placeholder="e.g. आरव / آراف" data-testid="idc-stu-name-local"/></label>
            </div>
            <button type="button"
                    onClick={async () => {
                      if (edit.aadhaar_number && edit.aadhaar_number.length !== 12)
                        return toast.error("Aadhaar must be exactly 12 digits");
                      try {
                        await api.patch(`/students/${edit.student_id}`, {
                          dob: edit.dob || null, blood_group: edit.blood_group || null,
                          address: edit.address || null,
                          aadhaar_number: edit.aadhaar_number || null,
                          sats_number: edit.sats_number || null,
                          pen_number: edit.pen_number || null,
                          rfid_uid: edit.rfid_uid || "",
                          name_local: edit.name_local || "",
                        });
                        toast.success("Profile fields saved"); load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                    }}
                    className="nb-btn nb-btn-secondary w-full justify-center text-xs"
                    data-testid="idc-stu-save-fields">
              Save Profile & Government ID Fields
            </button>
            <div className="border-t-2 border-black pt-2 space-y-3">
              <p className="text-xs text-slate-600">Photos are compressed to 85% JPEG and stored securely (no raw image data in the database).</p>
              {[
                ["student_photo", "Student Photo", "student_photo_url"],
                ["birth_certificate", "Birth Certificate", "birth_certificate_url"],
                ["government_id", "Government ID", "government_id_url"],
              ].map(([field, lbl, urlKey]) => (
                <StudentIdCapture key={field} studentId={edit.student_id} fieldName={field}
                  label={lbl} value={edit[urlKey]} schoolId={settings?.school_id}
                  onUploaded={async (fn, url) => {
                    try {
                      await api.patch(`/students/${edit.student_id}`, { [urlKey]: url });
                      setEdit((s) => ({ ...s, [urlKey]: url })); load();
                    } catch (e) { toast.error("Could not save document URL"); throw e; }
                  }} />
              ))}
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={()=>setEdit(null)} className="nb-btn nb-btn-ghost">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IdCardStaffRoster({ settings, onGenerate }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/staff").then(r => setRows(r.data)).catch(()=>setRows([]));
  useEffect(()=>{ load(); }, []);
  const hasPhoto = (s) => (s.docs?.photo_data_url || s.staff_photo_url);
  return (
    <div className="nb-card p-4 space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-700">
          Roster pulled from Staff &amp; Attendance (including Educators). Capture photo + Gov ID for each member.
        </div>
        <button onClick={()=>onGenerate("staff")} disabled={rows.length===0}
                className="nb-btn" data-testid="idc-staff-gen">
          Generate for {rows.length} staff
        </button>
      </div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table text-sm">
          <thead><tr><th>Name</th><th>Role</th><th>Photo</th><th>Gov ID</th><th>Action</th></tr></thead>
          <tbody>
            {rows.map(s => {
              const d = s.docs || {};
              const dot = (v) => <span className={`inline-block w-2 h-2 rounded-full mr-1 ${v?"bg-emerald-500":"bg-rose-500"}`}/>;
              return (
                <tr key={s.beneficiary_id} data-testid={`idc-staff-row-${s.beneficiary_id}`}>
                  <td className="font-bold">{s.name}</td>
                  <td className="text-xs">{s.designation}</td>
                  <td>{dot(hasPhoto(s))}{hasPhoto(s)?"Uploaded":"Missing"}</td>
                  <td>{dot(d.gov_id_data_url || s.government_id_url)}{(d.gov_id_data_url||s.government_id_url)?"Uploaded":"Missing"}</td>
                  <td className="space-x-1">
                    <button onClick={()=>setEdit(s)} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                            data-testid={`idc-staff-upload-${s.beneficiary_id}`}>Upload</button>
                    <button onClick={()=>onGenerate("staff", {ids:[s.beneficiary_id]})} className="nb-btn px-2 py-1 text-xs"
                            data-testid={`idc-staff-print-${s.beneficiary_id}`}>Print</button>
                  </td>
                </tr>
              );
            })}
            {rows.length===0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No staff yet</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" data-testid="idc-staff-upload-modal">
          <div className="nb-card p-6 bg-white w-full max-w-md space-y-3 max-h-[92vh] overflow-y-auto">
            <h3 className="display text-lg font-bold">Documents — {edit.name}</h3>
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="nb-label">Date of Birth</span>
                <input type="date" className="nb-input mt-1" value={edit.dob || ""}
                       onChange={e=>setEdit({...edit, dob: e.target.value})}
                       data-testid="idc-staff-dob"/></label>
              <label className="block"><span className="nb-label">Blood Group</span>
                <select className="nb-select mt-1" value={edit.blood_group || ""}
                        onChange={e=>setEdit({...edit, blood_group: e.target.value})}
                        data-testid="idc-staff-blood">
                  <option value="">—</option>
                  {["A+","A-","B+","B-","O+","O-","AB+","AB-"].map(b=><option key={b} value={b}>{b}</option>)}
                </select></label>
            </div>
            <label className="block"><span className="nb-label">Home Address</span>
              <textarea rows={2} className="nb-input mt-1" value={edit.address || ""}
                        onChange={e=>setEdit({...edit, address: e.target.value})}
                        data-testid="idc-staff-address"/></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="nb-label">Emergency Contact</span>
                <input className="nb-input mt-1" value={edit.emergency_contact || ""}
                       onChange={e=>setEdit({...edit, emergency_contact: e.target.value})}
                       data-testid="idc-staff-emergency"/></label>
              <label className="block"><span className="nb-label">RFID / Smart-Card UID</span>
                <input className="nb-input mt-1 mono uppercase" value={edit.rfid_uid || ""}
                       onChange={e=>setEdit({...edit, rfid_uid: e.target.value.replace(/[^0-9a-fA-F]/g,"").toUpperCase().slice(0,14)})}
                       placeholder="e.g. B9DFC12BF1" data-testid="idc-staff-rfid"/></label>
            </div>
            <button type="button"
                    onClick={async () => {
                      try {
                        await api.patch(`/staff/${edit.beneficiary_id}`, {
                          dob: edit.dob || null, blood_group: edit.blood_group || null,
                          address: edit.address || null,
                          emergency_contact: edit.emergency_contact || null,
                          rfid_uid: edit.rfid_uid || "",
                        });
                        toast.success("Profile fields saved"); load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                    }}
                    className="nb-btn nb-btn-secondary w-full justify-center text-xs"
                    data-testid="idc-staff-save-fields">
              Save Profile Fields
            </button>
            <div className="border-t-2 border-black pt-2 space-y-3">
              <p className="text-xs text-slate-600">Photos are compressed and stored securely (no raw image data in the database).</p>
              {[
                ["staff_photo", "Staff Photo", "staff_photo_url"],
                ["government_id", "Government ID", "government_id_url"],
              ].map(([field, lbl, urlKey]) => (
                <StudentIdCapture key={field} studentId={edit.beneficiary_id} fieldName={field}
                  label={lbl} value={edit[urlKey]} schoolId={settings?.school_id}
                  uploadPath={`/staff/${edit.beneficiary_id}/id-document`}
                  onUploaded={async (fn, url) => {
                    try {
                      await api.patch(`/staff/${edit.beneficiary_id}`, { [urlKey]: url });
                      setEdit((s) => ({ ...s, [urlKey]: url })); load();
                    } catch (e) { toast.error("Could not save document URL"); throw e; }
                  }} />
              ))}
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={()=>setEdit(null)} className="nb-btn nb-btn-ghost">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Iter20 — Academic Analytics (shared across Admin/Facilitator/Founder)
============================================================ */
export function AcademicAnalytics() {
  const [testTypes, setTestTypes] = useState([]);
  const [testType, setTestType] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  useEffect(()=>{
    api.get("/settings").then(r => {
      const tt = r.data?.test_types || [];
      setTestTypes(tt);
      setTestType(tt[0] || "");
    });
  }, []);
  const load = async (tt) => {
    setBusy(true);
    try {
      const r = await api.get("/analytics/academic", { params: tt ? { test_type: tt } : {} });
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setRows([]); }
    setBusy(false);
  };
  useEffect(()=>{ if (testType) load(testType); }, [testType]); // eslint-disable-line
  return (
    <div className="space-y-5" data-testid="acad-analytics-panel">
      <div className="nb-card p-5 bg-[#C4B5FD] flex flex-wrap items-end gap-3">
        <div>
          <div className="display text-xl font-bold">Academic Analytics</div>
          <p className="text-sm mt-1">Cross-section class performance for the selected Test Type. Percentages honour the school Grading Scale.</p>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <div><label className="nb-label">Test Type</label>
            <select className="nb-select mt-1 w-40" value={testType}
                    onChange={e=>setTestType(e.target.value)}
                    data-testid="aa-test-type">
              <option value="">All Test Types</option>
              {testTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select></div>
          <button onClick={()=>load(testType)} className="nb-btn" disabled={busy}
                  data-testid="aa-refresh">{busy?"Loading…":"Refresh"}</button>
        </div>
      </div>
      {rows.length === 0 && !busy && (
        <div className="nb-card p-6 text-center text-slate-500">
          No marks logged yet for this Test Type.
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map(r => {
          const key = `${r.class_name}-${r.section}`;
          const open = expanded === key;
          return (
            <div key={key} className="nb-card p-5 space-y-3"
                 data-testid={`aa-card-${key}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="display text-lg font-bold">{r.class_name} · Section {r.section}</div>
                  <div className="text-xs text-slate-600">{r.students_count} students · {testType || "All types"}</div>
                </div>
                <div className="text-right">
                  <div className="mono font-bold text-3xl">{r.avg_pct}%</div>
                  <div className="text-[10px]">CLASS AVERAGE</div>
                </div>
              </div>
              <div className="nb-card-sm p-3 bg-[#FDE047]">
                <div className="nb-label">Best Subject</div>
                <div className="font-bold text-sm">{r.best_subject || "—"} · {r.best_subject_avg || 0}% avg</div>
              </div>
              <details open={open}>
                <summary onClick={e=>{e.preventDefault(); setExpanded(open?null:key);}}
                         className="cursor-pointer text-xs font-bold uppercase"
                         data-testid={`aa-toggle-${key}`}>
                  {open ? "▾ Hide" : "▸ Show"} highest score per subject & top 10 students
                </summary>
                {open && (
                  <div className="pt-3 space-y-3">
                    <div>
                      <div className="nb-label mb-1">Highest Score per Subject</div>
                      <table className="w-full text-xs">
                        <thead><tr className="text-left border-b border-slate-300">
                          <th className="py-1">Subject</th><th>Top Marks</th><th>Student</th>
                        </tr></thead>
                        <tbody>
                          {r.subject_bests.map(sb => (
                            <tr key={sb.subject} className="border-b border-slate-200">
                              <td className="py-1">{sb.subject}</td>
                              <td className="mono">{sb.top_marks}/{sb.top_max}</td>
                              <td>{sb.top_student} ({sb.roll_no})</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <div className="nb-label mb-1">Top 10 Students</div>
                      <table className="w-full text-xs">
                        <thead><tr className="text-left border-b border-slate-300">
                          <th className="py-1">Rank</th><th>Name</th><th>Roll</th><th>%</th><th>Grade</th>
                        </tr></thead>
                        <tbody>
                          {r.top_students.map((s, i) => (
                            <tr key={s.student_id} className="border-b border-slate-200"
                                data-testid={`aa-top-${key}-${i+1}`}>
                              <td className="py-1 font-bold">{i+1}</td>
                              <td>{s.name}</td>
                              <td className="mono">{s.roll_no}</td>
                              <td className="mono">{s.pct}%</td>
                              <td><span className="nb-badge">{s.grade}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </details>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function RosterRow({user, onPatch, onDelete, onResetPw, testPrefix}) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({name: user.name||"", email: user.email||"", mobile: user.mobile||""});
  const save = async () => { await onPatch(user.user_id, form); setEdit(false); };
  if (edit) {
    return <div className="flex items-center gap-1 bg-yellow-50 border-2 border-black px-2 py-1 rounded text-xs"
                data-testid={`${testPrefix}-edit-${user.email}`}>
      <input className="nb-input py-0.5 px-1 text-xs flex-1" placeholder="Name" value={form.name}
             onChange={e=>setForm({...form, name:e.target.value})}/>
      <input className="nb-input py-0.5 px-1 text-xs flex-1" placeholder="Email" type="email" value={form.email}
             onChange={e=>setForm({...form, email:e.target.value})}/>
      <input className="nb-input py-0.5 px-1 text-xs w-24" placeholder="Mobile" value={form.mobile}
             onChange={e=>setForm({...form, mobile:e.target.value})}/>
      <button onClick={save} className="nb-btn nb-btn-secondary px-2 py-0.5 text-[10px]"
              data-testid={`${testPrefix}-save-${user.email}`}>Save</button>
      <button onClick={()=>setEdit(false)} className="nb-btn nb-btn-ghost px-2 py-0.5 text-[10px]">Cancel</button>
    </div>;
  }
  return <div className="flex items-center gap-2 bg-white border-2 border-black px-2 py-1 rounded text-xs"
              data-testid={`${testPrefix}-row-${user.email}`}>
    <span className="font-bold truncate">{user.name}</span>
    <span className="mono truncate flex-1">{user.email}</span>
    <button type="button" onClick={()=>setEdit(true)}
            className="nb-btn nb-btn-secondary px-2 py-0.5 text-[10px]"
            data-testid={`${testPrefix}-edit-btn-${user.email}`}>Edit</button>
    <button type="button" onClick={()=>onResetPw(user.user_id, user.email)}
            className="nb-btn px-2 py-0.5 text-[10px] bg-[#FCA5A5]"
            data-testid={`${testPrefix}-reset-${user.email}`}>Reset PW</button>
    <button type="button" onClick={()=>onDelete(user.user_id, user.email)}
            className="nb-btn nb-btn-danger px-2 py-0.5 text-[10px]"
            data-testid={`${testPrefix}-del-${user.email}`}>Remove</button>
  </div>;
}

function FinanceSettingsSection({settings, onChange}) {
  const [ledgers, setLedgers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [staff, setStaff] = useState([]);
  const [coords, setCoords] = useState([]);
  const [newLedger, setNewLedger] = useState({name:"", cash_in_types:[],
                                              cash_out_types:[], payroll_staff_ids:[]});
  const [editingLedgerId, setEditingLedgerId] = useState(null);   // Iter31 — inline edit
  const [newDiv, setNewDiv] = useState({name:"", kind:"physical", grades:[], sections:[]});
  const [editingDivisionId, setEditingDivisionId] = useState(null); // Iter31 — inline edit
  const [newCoord, setNewCoord] = useState({name:"", email:"", mobile:"", division_id:"", password:"coord123"});
  const load = async () => {
    try { setLedgers((await api.get("/finance-settings/ledger-types")).data); } catch { /* silent */ }
    try { setDivisions((await api.get("/finance-settings/divisions")).data); } catch { /* silent */ }
    try { setStaff((await api.get("/staff")).data); } catch { /* silent */ }
    try { setCoords((await api.get("/coordinators")).data); } catch { /* silent */ }
  };
  useEffect(()=>{ load(); }, []);
  const toggleArrayItem = (obj, setter, field, item) => {
    const has = (obj[field]||[]).includes(item);
    setter({...obj, [field]: has ? obj[field].filter(x=>x!==item) : [...(obj[field]||[]), item]});
  };
  const addLedger = async () => {
    if (!newLedger.name.trim()) return toast.error("Name required");
    // Auto-derive kind for backend metadata (informational only)
    const hasIn = (newLedger.cash_in_types||[]).length > 0;
    const hasOut = (newLedger.cash_out_types||[]).length > 0
                    || (newLedger.payroll_staff_ids||[]).length > 0;
    const kind = hasIn && hasOut ? "generic" : hasIn ? "cash_in" : hasOut ? "cash_out" : "generic";
    try {
      if (editingLedgerId) {
        await api.patch(`/finance-settings/ledger-types/${editingLedgerId}`, {...newLedger, kind});
        toast.success("Ledger updated");
      } else {
        await api.post("/finance-settings/ledger-types", {...newLedger, kind});
        toast.success("Ledger saved");
      }
      setNewLedger({name:"", cash_in_types:[], cash_out_types:[], payroll_staff_ids:[]});
      setEditingLedgerId(null);
      load();
    } catch (e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const editLedger = (l) => {
    setEditingLedgerId(l.ledger_id);
    setNewLedger({name: l.name,
                  cash_in_types: l.cash_in_types || [],
                  cash_out_types: l.cash_out_types || [],
                  payroll_staff_ids: l.payroll_staff_ids || []});
  };
  const cancelLedgerEdit = () => {
    setEditingLedgerId(null);
    setNewLedger({name:"", cash_in_types:[], cash_out_types:[], payroll_staff_ids:[]});
  };
  const delLedger = async (id) => {
    if (!window.confirm("Delete ledger?")) return;
    await api.delete(`/finance-settings/ledger-types/${id}`); load();
  };
  const addDivision = async () => {
    if (!newDiv.name.trim()) return toast.error("Name required");
    try {
      if (editingDivisionId) {
        await api.patch(`/finance-settings/divisions/${editingDivisionId}`, newDiv);
        toast.success("Division updated");
      } else {
        await api.post("/finance-settings/divisions", newDiv);
        toast.success("Division added");
      }
      setNewDiv({name:"", kind:"physical", grades:[], sections:[]});
      setEditingDivisionId(null);
      load();
    } catch (e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const editDivision = (d) => {
    setEditingDivisionId(d.division_id);
    setNewDiv({name: d.name, kind: d.kind || "physical",
               grades: d.grades || [], sections: d.sections || []});
  };
  const cancelDivisionEdit = () => {
    setEditingDivisionId(null);
    setNewDiv({name:"", kind:"physical", grades:[], sections:[]});
  };
  const delDivision = async (id) => {
    if (!window.confirm("Delete division?")) return;
    await api.delete(`/finance-settings/divisions/${id}`); load();
  };
  const addCoord = async () => {
    if (!newCoord.name.trim() || !newCoord.email || !newCoord.division_id)
      return toast.error("Name, Email and Division are required");
    try { await api.post("/coordinators", newCoord);
      setNewCoord({name:"", email:"", mobile:"", division_id:"", password:"coord123"});
      load(); toast.success("Coordinator provisioned");
    } catch (e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delCoord = async (uid, email) => {
    if (!window.confirm(`Remove coordinator ${email}?`)) return;
    await api.delete(`/coordinators/${uid}`); load();
  };
  const patchCoord = async (uid, patch) => {
    try { await api.patch(`/coordinators/${uid}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resetPw = async (uid, email) => {
    const pw = window.prompt(`Reset password for ${email} — enter new password (min 6 chars):`, "");
    if (!pw || pw.length < 6) return;
    try { await api.post(`/users/${uid}/reset-password`, {new_password: pw});
          toast.success(`Password reset for ${email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleGrade = (obj, setter, g) => toggleArrayItem(obj, setter, "grades", g);
  const toggleSection = (obj, setter, sec) => toggleArrayItem(obj, setter, "sections", sec);
  const save = async (e) => { e.preventDefault(); await api.put("/settings", settings);
    toast.success("Finance settings saved"); };
  const feeTypes = settings.fee_types || [];
  const expCats = settings.expense_categories || [];
  return <div className="nb-card p-6 bg-[#FEF3C7] space-y-4" data-testid="finance-settings">
    <h3 className="display text-2xl font-bold">Finance Settings</h3>
    <p className="text-xs">Centralize the financial-side configuration: Ledger types (with Cash-In / Cash-Out mapping),
      Divisions/Branches (with Coordinators), Expense Categories, and Fee Types.</p>

    <div className="grid md:grid-cols-2 gap-4">
      {/* Ledger Types with Cash-In/Cash-Out mapping */}
      <div className="nb-card-sm p-4 bg-white" data-testid="fs-ledger-types">
        <div className="display font-bold mb-2">1. Ledger Types &mdash; Reporting Ledgers</div>
        <div className="text-[11px] mb-2">Map one ledger to multiple Cash-In (Fee Types from sub-section 4) and/or Cash-Out (Expense Categories from sub-section 3) sources for granular cash flow analysis.</div>
        <div className="space-y-1 mb-2 max-h-40 overflow-y-auto">
          {ledgers.map(l => <div key={l.ledger_id}
                className="text-xs border border-slate-300 rounded p-2 bg-slate-50">
            <div className="flex items-center gap-2">
              <span className="font-bold flex-1">{l.name}</span>
              <span className="nb-badge text-[10px]">{l.kind}</span>
              <button onClick={()=>editLedger(l)} className="nb-btn nb-btn-secondary px-1 py-0.5 text-[10px]"
                      data-testid={`fs-ledger-edit-${l.name}`}>Edit</button>
              <button onClick={()=>delLedger(l.ledger_id)} className="nb-btn nb-btn-danger px-1 py-0.5 text-[10px]"
                      data-testid={`fs-ledger-del-${l.name}`}>×</button>
            </div>
            {(l.cash_in_types?.length > 0 || l.cash_out_types?.length > 0 || l.payroll_staff_ids?.length > 0) && (
              <div className="mt-1 text-[10px] space-y-0.5">
                {l.cash_in_types?.length > 0 && <div><b className="text-emerald-700">IN:</b> {l.cash_in_types.join(", ")}</div>}
                {l.cash_out_types?.length > 0 && <div><b className="text-rose-700">OUT:</b> {l.cash_out_types.join(", ")}</div>}
                {l.payroll_staff_ids?.length > 0 && <div><b className="text-violet-700">PAYROLL:</b> {l.payroll_staff_ids.length} staff</div>}
              </div>
            )}
          </div>)}
          {ledgers.length===0 && <div className="text-xs text-slate-400 italic">No ledgers yet</div>}
        </div>
        <div className="space-y-2 border-t-2 border-slate-200 pt-2">
          <input className="nb-input text-xs" placeholder="Ledger Name (e.g. Bank Account 1)"
                 value={newLedger.name}
                 onChange={e=>setNewLedger({...newLedger, name:e.target.value})}
                 data-testid="fs-ledger-name"/>
          <div>
            <label className="nb-label text-[10px]">Cash-In Stream (Fee Types &amp; Donations)</label>
            <select multiple size={Math.min(6, Math.max(3, feeTypes.length + 1))}
                    className="nb-select text-xs w-full"
                    value={newLedger.cash_in_types}
                    onChange={e=>{
                      const vals = Array.from(e.target.selectedOptions).map(o=>o.value);
                      setNewLedger({...newLedger, cash_in_types: vals});
                    }}
                    data-testid="fs-ledger-cash-in-dropdown">
              {feeTypes.length===0 && <option disabled>Add Fee Types in sub-section 4 first</option>}
              {feeTypes.map(ft => <option key={ft} value={ft}>{ft}</option>)}
              <option value="Donations">Donations</option>
            </select>
            <div className="text-[10px] text-slate-500 mt-1">Hold Ctrl/Cmd to select multiple</div>
            {newLedger.cash_in_types.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {newLedger.cash_in_types.map(v => (
                  <span key={v} className="nb-badge bg-[#86EFAC] text-[10px]"
                        data-testid={`fs-ledger-in-chip-${v}`}>{v}</span>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="nb-label text-[10px]">Cash-Out Stream &mdash; Expenses (Categories)</label>
            <select multiple size={Math.min(6, Math.max(3, expCats.length))}
                    className="nb-select text-xs w-full"
                    value={newLedger.cash_out_types}
                    onChange={e=>{
                      const vals = Array.from(e.target.selectedOptions).map(o=>o.value);
                      setNewLedger({...newLedger, cash_out_types: vals});
                    }}
                    data-testid="fs-ledger-cash-out-dropdown">
              {expCats.length===0 && <option disabled>Add Expense Categories in sub-section 3 first</option>}
              {expCats.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <div className="text-[10px] text-slate-500 mt-1">Hold Ctrl/Cmd to select multiple</div>
            {newLedger.cash_out_types.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {newLedger.cash_out_types.map(v => (
                  <span key={v} className="nb-badge bg-[#FCA5A5] text-[10px]"
                        data-testid={`fs-ledger-out-chip-${v}`}>{v}</span>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="nb-label text-[10px]">Cash-Out Stream &mdash; Payroll (select Staff records)</label>
            <select multiple size={Math.min(6, Math.max(3, staff.length))}
                    className="nb-select text-xs w-full"
                    value={newLedger.payroll_staff_ids}
                    onChange={e=>{
                      const vals = Array.from(e.target.selectedOptions).map(o=>o.value);
                      setNewLedger({...newLedger, payroll_staff_ids: vals});
                    }}
                    data-testid="fs-ledger-payroll-dropdown">
              {staff.length===0 && <option disabled>No staff added yet</option>}
              {staff.map(s => <option key={s.staff_id} value={s.staff_id}>{s.name} · {s.designation}</option>)}
            </select>
            <div className="text-[10px] text-slate-500 mt-1">Payroll of only these staff members will roll up under this ledger. Hold Ctrl/Cmd to select multiple.</div>
            {newLedger.payroll_staff_ids.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {newLedger.payroll_staff_ids.map(sid => {
                  const s = staff.find(x=>x.staff_id===sid);
                  return <span key={sid} className="nb-badge bg-[#C4B5FD] text-[10px]"
                                data-testid={`fs-ledger-payroll-chip-${sid}`}>{s?.name || sid}</span>;
                })}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={addLedger} className="nb-btn flex-1 justify-center"
                    data-testid="fs-ledger-add">
              <Plus size={12}/>{editingLedgerId ? "Update Ledger" : "Save Ledger"}
            </button>
            {editingLedgerId && (
              <button onClick={cancelLedgerEdit} className="nb-btn nb-btn-ghost"
                      data-testid="fs-ledger-cancel">Cancel</button>
            )}
          </div>
        </div>
      </div>

      {/* Divisions with Coordinators */}
      <div className="nb-card-sm p-4 bg-white" data-testid="fs-divisions">
        <div className="display font-bold mb-2">2. Divisions / Branches &amp; Coordinators</div>
        <div className="text-[11px] mb-2">Map Grades and Sections under each branch. Provision a Division Coordinator per branch.</div>
        <div className="space-y-1 mb-2 max-h-40 overflow-y-auto">
          {divisions.map(d => {
            const divCoords = coords.filter(c => c.division_id === d.division_id);
            return <div key={d.division_id}
                  className="text-xs border border-slate-300 rounded p-2 bg-slate-50">
              <div className="flex items-center gap-2">
                <span className="font-bold flex-1">{d.name}</span>
                <span className="nb-badge text-[10px]">{d.kind}</span>
                <span className="mono text-slate-500 text-[10px]">
                  G:{(d.grades||[]).length} · S:{(d.sections||[]).length}
                </span>
                <button onClick={()=>editDivision(d)} className="nb-btn nb-btn-secondary px-1 py-0.5 text-[10px]"
                        data-testid={`fs-div-edit-${d.name}`}>Edit</button>
                <button onClick={()=>delDivision(d.division_id)} className="nb-btn nb-btn-danger px-1 py-0.5 text-[10px]"
                        data-testid={`fs-div-del-${d.name}`}>×</button>
              </div>
              {divCoords.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {divCoords.map(c => (
                    <RosterRow key={c.user_id} user={c}
                      onPatch={patchCoord} onDelete={delCoord} onResetPw={resetPw}
                      testPrefix={`coord-${d.name}`}/>
                  ))}
                </div>
              )}
            </div>;
          })}
          {divisions.length===0 && <div className="text-xs text-slate-400 italic">No divisions yet</div>}
        </div>
        <div className="space-y-2 border-t-2 border-slate-200 pt-2">
          <div className="flex gap-1">
            <input className="nb-input text-xs" style={{flex:"1 1 50%", minWidth:0}}
                   placeholder="Division name (e.g. North Campus)"
                   value={newDiv.name}
                   onChange={e=>setNewDiv({...newDiv, name:e.target.value})}
                   data-testid="fs-div-name"/>
            <select className="nb-select text-xs" style={{width:"90px", flex:"0 0 90px"}}
                    value={newDiv.kind}
                    onChange={e=>setNewDiv({...newDiv, kind:e.target.value})}>
              <option value="physical">Physical</option>
              <option value="digital">Digital</option>
            </select>
            <button onClick={addDivision} className="nb-btn"
                    data-testid="fs-div-add">
              {editingDivisionId ? <Pencil size={12}/> : <Plus size={12}/>}
            </button>
            {editingDivisionId && (
              <button onClick={cancelDivisionEdit} className="nb-btn nb-btn-ghost text-[10px]"
                      data-testid="fs-div-cancel">Cancel</button>
            )}
          </div>
          <div className="flex flex-wrap gap-1 text-[10px]">
            <span className="nb-label w-full">Grades:</span>
            {(settings.grades||[]).map(g =>
              <label key={g} className={`nb-btn px-1 py-0.5 text-[10px] cursor-pointer ${(newDiv.grades||[]).includes(g)?"":"nb-btn-ghost"}`}>
                <input type="checkbox" className="hidden" checked={(newDiv.grades||[]).includes(g)}
                       onChange={()=>toggleGrade(newDiv, setNewDiv, g)}/>{g}
              </label>)}
          </div>
          <div className="flex flex-wrap gap-1 text-[10px]">
            <span className="nb-label w-full">Sections:</span>
            {(settings.sections||[]).map(sec =>
              <label key={sec} className={`nb-btn px-1 py-0.5 text-[10px] cursor-pointer ${(newDiv.sections||[]).includes(sec)?"":"nb-btn-ghost"}`}>
                <input type="checkbox" className="hidden" checked={(newDiv.sections||[]).includes(sec)}
                       onChange={()=>toggleSection(newDiv, setNewDiv, sec)}/>{sec}
              </label>)}
          </div>
          <div className="border-t border-slate-200 pt-2">
            <div className="nb-label text-[10px] mb-1">Provision Division Coordinator</div>
            <div className="grid grid-cols-2 gap-1">
              <input className="nb-input text-xs" placeholder="Name" value={newCoord.name}
                     onChange={e=>setNewCoord({...newCoord, name:e.target.value})}
                     data-testid="fs-coord-name"/>
              <input className="nb-input text-xs" placeholder="Email" type="email"
                     value={newCoord.email}
                     onChange={e=>setNewCoord({...newCoord, email:e.target.value})}
                     data-testid="fs-coord-email"/>
              <input className="nb-input text-xs" placeholder="Mobile" value={newCoord.mobile}
                     onChange={e=>setNewCoord({...newCoord, mobile:e.target.value})}/>
              <select className="nb-select text-xs" value={newCoord.division_id}
                      onChange={e=>setNewCoord({...newCoord, division_id:e.target.value})}
                      data-testid="fs-coord-division">
                <option value="">— Division —</option>
                {divisions.map(d => <option key={d.division_id} value={d.division_id}>{d.name}</option>)}
              </select>
              <input className="nb-input text-xs" placeholder="Password (default coord123)"
                     value={newCoord.password}
                     onChange={e=>setNewCoord({...newCoord, password:e.target.value})}/>
              <button onClick={addCoord} className="nb-btn"
                      data-testid="fs-coord-add"><Plus size={12}/> Add Coordinator</button>
            </div>
          </div>
        </div>
      </div>

      {/* Expense Categories (moved sub-section) */}
      <div className="nb-card-sm p-4 bg-white" data-testid="fs-expense-cats">
        <div className="display font-bold mb-2">3. Expense Categories</div>
        <div className="text-[11px] mb-2">Used by Accountant when logging an expense &mdash; and also drives Staff Payroll Type tagging.</div>
        <ListEditor value={settings.expense_categories||[]}
                    onChange={v=>onChange({...settings, expense_categories:v})}
                    placeholder="Add category" testid="expense-cat"/>
        <button onClick={save} className="nb-btn nb-btn-secondary mt-2 w-full justify-center text-xs"
                data-testid="fs-exp-save">Save Changes</button>
      </div>

      {/* Fee Types (moved sub-section) */}
      <div className="nb-card-sm p-4 bg-white" data-testid="fs-fee-types">
        <div className="display font-bold mb-2">4. Fee Types</div>
        <div className="text-[11px] mb-2">Available fee categories on the Fees tab (Admission, Tuition, Transport…).</div>
        <ListEditor value={settings.fee_types||[]}
                    onChange={v=>onChange({...settings, fee_types:v})}
                    placeholder="Add fee type" testid="fee-type"/>
        <button onClick={save} className="nb-btn nb-btn-secondary mt-2 w-full justify-center text-xs"
                data-testid="fs-fee-save">Save Changes</button>
      </div>

      {/* Iter31 — Expense Approval Limit */}
      <div className="nb-card-sm p-4 bg-white" data-testid="fs-approval-limit">
        <div className="display font-bold mb-2">5. Expense Approval Limit</div>
        <div className="text-[11px] mb-2">
          Expenses <b>at or below</b> this amount are auto-approved and posted instantly to the Expense Log, Cash Flow, and Founder log. Amounts <b>above</b> this cap route to Founder &amp; Admin for approval.
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-xs mono">₹</span>
          <input type="number" min="0" step="100" className="nb-input"
                 value={settings.expense_approval_limit ?? settings.expense_threshold ?? 0}
                 onChange={e=>onChange({...settings, expense_approval_limit: parseFloat(e.target.value)||0})}
                 data-testid="fs-approval-limit-input"/>
          <button onClick={save} className="nb-btn nb-btn-secondary text-xs"
                  data-testid="fs-approval-limit-save">Save</button>
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          Current cap: ₹{Number(settings.expense_approval_limit ?? settings.expense_threshold ?? 0).toLocaleString("en-IN")}
        </div>
      </div>
    </div>
  </div>;
}

function SubscriptionSection() {
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [reqPlan, setReqPlan] = useState("");
  const [remark, setRemark] = useState("");
  const load = () => api.get("/schools/my-subscription").then(r => setSub(r.data)).catch(() => setSub(null));
  useEffect(() => { load(); api.get("/schools/plans").then(r => setPlans(r.data)).catch(() => {}); }, []);
  const submit = async () => {
    if (!reqPlan) { toast.error("Choose a plan"); return; }
    try {
      await api.post("/schools/request-plan-change", { requested_plan_key: reqPlan, remark });
      toast.success("Request sent to the platform team");
      setOpen(false); setReqPlan(""); setRemark(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!sub) return null;
  const plan = sub.plan || {};
  const cap = sub.max_students || plan.max_students || 0;
  const used = sub.active_students || 0;
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const bl = sub.billing_lifecycle || {};
  const stUsed = sub.storage_used_bytes || 0;
  const stLimit = sub.storage_limit_bytes || 0;
  const stPct = stLimit ? Math.min(100, Math.round((stUsed / stLimit) * 100)) : 0;
  const gb = (b) => (b / 1073741824).toFixed(b >= 1073741824 ? 1 : 2);
  const camp = sub.campus || {};
  const nearCap = cap && pct >= 90;
  return (
    <div className="nb-card p-6 bg-[#93C5FD] space-y-3" data-testid="admin-subscription-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="display text-xl font-bold">Subscription</h3>
        <button className="nb-btn" onClick={() => setOpen(true)} data-testid="request-plan-change-btn">Request Plan Upgrade / Change</button>
      </div>
      {nearCap && (
        <div className="nb-card-sm p-3 bg-[#FCA5A5] border-2 border-black text-sm font-bold flex items-center justify-between gap-2" data-testid="sub-capacity-alert">
          <span>⚠ You're at {pct}% of your student seat capacity. Consider upgrading your plan.</span>
          <button className="nb-btn text-xs" onClick={() => setOpen(true)} data-testid="sub-alert-upgrade">Request Upgrade</button>
        </div>
      )}
      <div className="grid sm:grid-cols-4 gap-3">
        <div className="nb-card-sm p-3 bg-white"><div className="nb-label text-[10px]">Current Plan</div>
          <div className="display text-xl font-black" data-testid="sub-plan-name">{plan.name || sub.plan_key || "—"}</div></div>
        <div className="nb-card-sm p-3 bg-white"><div className="nb-label text-[10px]">Student Seats</div>
          <div className="display text-xl font-black mono" data-testid="sub-students">{used}{cap ? ` / ${cap}` : ""}</div>
          {cap ? <div className="h-2 mt-1 border-2 border-black bg-white"><div className={`h-full ${pct >= 90 ? "bg-[#FCA5A5]" : "bg-[#86EFAC]"}`} style={{ width: `${pct}%` }} /></div> : null}</div>
        <div className="nb-card-sm p-3 bg-white"><div className="nb-label text-[10px]">Cloud Storage</div>
          <div className="display text-lg font-black mono" data-testid="sub-storage">{gb(stUsed)} / {gb(stLimit)} GB</div>
          {stLimit ? <div className="h-2 mt-1 border-2 border-black bg-white"><div className={`h-full ${stPct >= 90 ? "bg-[#FCA5A5]" : "bg-[#86EFAC]"}`} style={{ width: `${stPct}%` }} /></div> : null}</div>
        <div className="nb-card-sm p-3 bg-white"><div className="nb-label text-[10px]">Billing</div>
          <div className="display text-lg font-black" data-testid="sub-status">{bl.status || "—"}</div>
          {bl.trial_end && <div className="text-[10px] mono opacity-70">trial ends {String(bl.trial_end).slice(0, 10)}</div>}</div>
      </div>
      {(camp.city || camp.postal_code || camp.address) && (
        <div className="nb-card-sm p-3 bg-white" data-testid="sub-campus-card">
          <div className="nb-label text-[10px]">Campus & Location</div>
          <div className="text-sm">{camp.address || [camp.city, camp.district, camp.state].filter(Boolean).join(", ")}</div>
          <div className="text-xs mono opacity-70">{[camp.country_code, camp.postal_code].filter(Boolean).join(" · ")}</div>
        </div>
      )}
      {sub.pending_request && (
        <div className="nb-card-sm p-2 bg-[#FDE047] text-xs" data-testid="sub-pending">
          Pending request → <b>{sub.pending_request.requested_plan}</b> (awaiting platform approval)
        </div>
      )}
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div className="nb-card bg-white p-5 max-w-md w-full space-y-3" onClick={e => e.stopPropagation()}>
            <h4 className="display text-lg font-bold">Request Plan Change</h4>
            <select className="nb-select" value={reqPlan} onChange={e => setReqPlan(e.target.value)} data-testid="req-plan-select">
              <option value="">Select desired plan…</option>
              {plans.map(p => <option key={p.plan_key} value={p.plan_key}>{p.name} — up to {p.max_students} students</option>)}
            </select>
            <textarea className="nb-input" rows="3" placeholder="Optional remark" value={remark} onChange={e => setRemark(e.target.value)} data-testid="req-plan-remark" />
            <div className="flex gap-2 justify-end">
              <button className="nb-btn nb-btn-ghost text-xs" onClick={() => setOpen(false)}>Cancel</button>
              <button className="nb-btn text-xs" onClick={submit} data-testid="req-plan-submit">Submit Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Settings() {
  const [s, setS] = useState(null);
  const [accs, setAccs] = useState([]);
  const [masterSubjects, setMasterSubjects] = useState([]);
  const [newAcc, setNewAcc] = useState({name:"", email:"", mobile:"", password:"acc123"});
  const [facs, setFacs] = useState([]);
  const [newFac, setNewFac] = useState({name:"", email:"", mobile:"", password:"fac123"});
  const fileRef = useRef(null);
  const ALL_GRADES = ["Nursery","LKG","UKG","G01","G02","G03","G04","G05","G06","G07","G08","G09","G10","G11","G12"];
  const ALL_SECTIONS = ["A","B","C","D","E","F"];
  const load = async () => {
    setS((await api.get("/settings")).data);
    setAccs((await api.get("/accountants")).data);
    try { setMasterSubjects((await api.get("/global/lms/subjects")).data); } catch { setMasterSubjects([]); }
    try { setFacs((await api.get("/facilitators")).data); } catch { setFacs([]); }
  };
  useEffect(()=>{ load(); }, []);
  const save = async (e) => { e.preventDefault(); await api.put("/settings", s); toast.success("Saved"); };
  const addAcc = async (e) => { e.preventDefault();
    await api.post("/accountants", newAcc); toast.success("Accountant created");
    setNewAcc({name:"",email:"",mobile:"",password:"acc123"}); load();
  };
  const addFac = async (e) => { e.preventDefault();
    try {
      await api.post("/facilitators", newFac); toast.success("Facilitator created");
      setNewFac({name:"",email:"",mobile:"",password:"fac123"}); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const delFac = async (uid, email) => {
    if (!window.confirm(`Remove facilitator ${email}?`)) return;
    try { await api.delete(`/facilitators/${uid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delAcc = async (uid, email) => {
    if (!window.confirm(`Remove accountant ${email}?`)) return;
    try { await api.delete(`/accountants/${uid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const patchAcc = async (uid, patch) => {
    try { await api.patch(`/accountants/${uid}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const patchFac = async (uid, patch) => {
    try { await api.patch(`/facilitators/${uid}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resetPw = async (uid, email) => {
    const pw = window.prompt(`Reset password for ${email} — enter new password (min 6 chars):`, "");
    if (!pw || pw.length < 6) return;
    try { await api.post(`/users/${uid}/reset-password`, {new_password: pw});
          toast.success(`Password reset for ${email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 500000) { toast.error("Logo must be < 500KB"); return; }
    const r = new FileReader();
    r.onload = () => setS({...s, logo_data_url: r.result});
    r.readAsDataURL(f);
  };
  const toggle = (key, val) => {
    const cur = s[key] || [];
    const next = cur.includes(val) ? cur.filter(x=>x!==val) : [...cur, val];
    setS({...s, [key]: next});
  };
  if (!s) return <div className="mono">Loading…</div>;
  return <div className="space-y-6">
    <SubscriptionSection/>
    <FinanceSettingsSection settings={s} onChange={setS}/>
    <div className="grid lg:grid-cols-2 gap-6">
    <form onSubmit={save} className="nb-card p-6 bg-[#C4B5FD] space-y-3">
      <h3 className="display text-xl font-bold">School Profile</h3>
      <div className="flex gap-3 items-center">
        {s.logo_data_url
          ? <img src={s.logo_data_url} alt="logo" className="w-16 h-16 object-contain border-2 border-black bg-white rounded"/>
          : <div className="w-16 h-16 border-2 border-black rounded bg-white grid place-items-center text-xs font-bold">NO LOGO</div>}
        <div className="flex-1">
          <input ref={fileRef} type="file" accept="image/*" onChange={onLogo} className="hidden" id="logo-upload" data-testid="logo-upload-input"/>
          <button type="button" onClick={()=>fileRef.current?.click()} className="nb-btn nb-btn-tertiary" data-testid="logo-upload-btn">Upload Logo</button>
          {s.logo_data_url && <button type="button" onClick={()=>setS({...s, logo_data_url:""})} className="nb-btn nb-btn-ghost ml-2">Remove</button>}
        </div>
      </div>
      <input className="nb-input" placeholder="School name" value={s.name||""} onChange={e=>setS({...s,name:e.target.value})}/>
      <textarea className="nb-textarea" placeholder="Address" value={s.address||""} onChange={e=>setS({...s,address:e.target.value})}/>
      <input className="nb-input" placeholder="Phone" value={s.phone||""} onChange={e=>setS({...s,phone:e.target.value})}/>
      <input className="nb-input" placeholder="Email" value={s.email||""} onChange={e=>setS({...s,email:e.target.value})}/>
      <input className="nb-input" placeholder="Facebook URL" value={s.facebook||""} onChange={e=>setS({...s,facebook:e.target.value})}/>
      <input className="nb-input" placeholder="Twitter URL" value={s.twitter||""} onChange={e=>setS({...s,twitter:e.target.value})}/>
      <input className="nb-input" placeholder="Instagram URL" value={s.instagram||""} onChange={e=>setS({...s,instagram:e.target.value})}/>
      <input type="number" className="nb-input" placeholder="Expense threshold ₹" value={s.expense_threshold||0} onChange={e=>setS({...s,expense_threshold:parseFloat(e.target.value)})}/>
      <div>
        <div className="nb-label mb-1">Available Grades</div>
        <div className="flex flex-wrap gap-2">
          {ALL_GRADES.map(g=>(
            <label key={g} className={`nb-btn ${s.grades?.includes(g)?"":"nb-btn-ghost"} cursor-pointer px-3 py-1`}>
              <input type="checkbox" className="hidden" checked={s.grades?.includes(g)||false} onChange={()=>toggle("grades",g)} data-testid={`grade-${g}`}/>
              {g}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="nb-label mb-1">Available Sections</div>
        <div className="flex flex-wrap gap-2">
          {ALL_SECTIONS.map(sec=>(
            <label key={sec} className={`nb-btn ${s.sections?.includes(sec)?"":"nb-btn-ghost"} cursor-pointer px-3 py-1`}>
              <input type="checkbox" className="hidden" checked={s.sections?.includes(sec)||false} onChange={()=>toggle("sections",sec)} data-testid={`section-${sec}`}/>
              {sec}
            </label>
          ))}
        </div>
      </div>
      {(s.school_boards || []).length > 0 && (
        <div data-testid="section-board-map">
          <div className="nb-label mb-1">Section → Board Mapping</div>
          <p className="text-[10px] text-slate-600 mb-1">Assign each section letter to one of your school&apos;s boards. Unmapped sections default to the Primary board ({s.primary_board || (s.school_boards||[])[0]}). No rosters, marks or timetables are affected.</p>
          <div className="grid grid-cols-2 gap-2">
            {(s.sections || []).map(sec => (
              <div key={sec} className="flex items-center gap-2">
                <span className="font-bold text-sm w-16">Sec {sec}</span>
                <select className="nb-select text-sm flex-1"
                        value={(s.section_boards || {})[sec] || (s.primary_board || (s.school_boards||[])[0] || "")}
                        onChange={e=>setS({...s, section_boards: {...(s.section_boards||{}), [sec]: e.target.value}})}
                        data-testid={`section-board-${sec}`}>
                  {(s.school_boards || []).map(b => <option key={b} value={b}>{b}{b===(s.primary_board||(s.school_boards||[])[0])?" (Primary)":""}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <ListEditor value={s.test_types||[]} onChange={v=>setS({...s, test_types:v})} placeholder="Add test type (e.g. FA1)" testid="test-type"/>
      </div>
      <div>
        <div className="nb-label mb-1">Part-A: Scholastic Subjects</div>
        <p className="text-[10px] text-slate-600 mb-1">Academic subjects counted in the Report Card &quot;Part A - Scholastic&quot; block. Order matters — drag or use ↑/↓ to reorder.</p>
        <SubjectPartEditor value={s.subjects_part_a||[]}
                            onChange={v=>setS({...s, subjects_part_a:v})}
                            otherPartValue={s.subjects_part_b||[]}
                            otherOnChange={v=>setS({...s, subjects_part_b:v})}
                            testid="subj-a" moveLabel="Move to Part B"/>
      </div>
      <div>
        <div className="nb-label mb-1">Part-B: Non-Scholastic Subjects</div>
        <p className="text-[10px] text-slate-600 mb-1">Co-curricular (Yoga, Music, PE, Art, etc.) shown separately in Report Cards.</p>
        <SubjectPartEditor value={s.subjects_part_b||[]}
                            onChange={v=>setS({...s, subjects_part_b:v})}
                            otherPartValue={s.subjects_part_a||[]}
                            otherOnChange={v=>setS({...s, subjects_part_a:v})}
                            testid="subj-b" moveLabel="Move to Part A"/>
      </div>
      <div data-testid="subject-master-map">
        <div className="nb-label mb-1">Link Subjects → Global Master Subjects (optional)</div>
        <p className="text-[10px] text-slate-600 mb-1">Map a school subject to a Global Master Subject. Dropdowns show only masters matching the subject type (Part A → Scholastic, Part B → Non-Scholastic) whose boards overlap your school&apos;s boards. Existing subject names, teacher assignments, weights and grades are untouched.</p>
        {["Scholastic","Non-Scholastic"].map(stype => {
          const subs = stype==="Scholastic" ? (s.subjects_part_a||[]) : (s.subjects_part_b||[]);
          const schoolBoards = s.school_boards || [];
          const opts = masterSubjects.filter(m => (m.subject_type||"Scholastic")===stype &&
            (!(m.applicable_boards||[]).length || (m.applicable_boards||[]).some(b=>schoolBoards.includes(b))));
          if (!subs.length) return null;
          return (
            <div key={stype} className="mb-2">
              <div className="text-[11px] font-bold mb-1">{stype} (Part {stype==="Scholastic"?"A":"B"})</div>
              <div className="grid grid-cols-1 gap-1.5">
                {subs.map(sub => (
                  <div key={sub} className="flex items-center gap-2">
                    <span className="text-sm flex-1 truncate">{sub}</span>
                    <select className="nb-select text-xs flex-1"
                            value={(s.subject_master_map||{})[sub] || ""}
                            onChange={e=>{ const m={...(s.subject_master_map||{})}; if(e.target.value){ m[sub]=e.target.value; } else { delete m[sub]; } setS({...s, subject_master_map: m}); }}
                            data-testid={`subject-master-${sub}`}>
                      <option value="">— Not linked —</option>
                      {opts.map(m => <option key={m.subject_id} value={m.subject_id}>{m.name} ({m.code})</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div>
        <div className="nb-label mb-1">Legacy &apos;Available Subjects&apos; (auto-derived)</div>
        <p className="text-[10px] text-slate-600 mb-1">Kept for backward compatibility with existing marks entries. Combined Part-A ∪ Part-B is the canonical list.</p>
        <ListEditor value={s.subjects||[]} onChange={v=>setS({...s, subjects:v})} placeholder="Add subject" testid="subject"/>
      </div>
      <LegacySubjectMigrator settings={s} onChange={setS}/>
      <button className="nb-btn w-full justify-center" data-testid="settings-save">Save Settings</button>
    </form>
    <div className="space-y-4">
      <form onSubmit={addAcc} className="nb-card p-6 bg-[#86EFAC] space-y-3" data-testid="add-accountant-form">
        <h3 className="display text-xl font-bold">Add Accountant</h3>
        <input className="nb-input" placeholder="Name" required value={newAcc.name} onChange={e=>setNewAcc({...newAcc,name:e.target.value})} data-testid="new-acc-name"/>
        <input type="email" className="nb-input" placeholder="Email" required value={newAcc.email} onChange={e=>setNewAcc({...newAcc,email:e.target.value})} data-testid="new-acc-email"/>
        <input className="nb-input" placeholder="Mobile" value={newAcc.mobile} onChange={e=>setNewAcc({...newAcc,mobile:e.target.value})}/>
        <input className="nb-input" placeholder="Default password" value={newAcc.password} onChange={e=>setNewAcc({...newAcc,password:e.target.value})}/>
        <button className="nb-btn w-full justify-center" data-testid="new-acc-submit"><Plus size={16}/>Create Accountant</button>
        {accs.length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto pt-2 border-t-2 border-black/20">
            {accs.map(a => (
              <RosterRow key={a.user_id} user={a}
                onPatch={patchAcc} onDelete={delAcc} onResetPw={resetPw}
                testPrefix="acc"/>
            ))}
          </div>
        )}
      </form>
      <form onSubmit={addFac} className="nb-card p-6 bg-[#FDE047] space-y-3" data-testid="add-facilitator-form">
        <h3 className="display text-xl font-bold">Add Facilitator</h3>
        <p className="text-xs">Facilitators coordinate staff attendance, timetable substitutions, and class-wise analytics.</p>
        <input className="nb-input" placeholder="Name" required value={newFac.name} onChange={e=>setNewFac({...newFac,name:e.target.value})} data-testid="new-fac-name"/>
        <input type="email" className="nb-input" placeholder="Email" required value={newFac.email} onChange={e=>setNewFac({...newFac,email:e.target.value})} data-testid="new-fac-email"/>
        <input className="nb-input" placeholder="Mobile" value={newFac.mobile} onChange={e=>setNewFac({...newFac,mobile:e.target.value})}/>
        <input className="nb-input" placeholder="Default password" value={newFac.password} onChange={e=>setNewFac({...newFac,password:e.target.value})}/>
        <button className="nb-btn w-full justify-center" data-testid="new-fac-submit"><Plus size={16}/>Create Facilitator</button>
        {facs.length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto pt-2 border-t-2 border-black/20">
            {facs.map(f => (
              <RosterRow key={f.user_id} user={f}
                onPatch={patchFac} onDelete={delFac} onResetPw={resetPw}
                testPrefix="fac"/>
            ))}
          </div>
        )}
      </form>
      <HolidayEditor value={s.holidays||[]} onChange={(v)=>setS({...s, holidays:v})} onSave={save}/>
      <div className="nb-card p-6 bg-[#A7F3D0] space-y-3" data-testid="working-days-editor">
        <h3 className="display text-xl font-bold">Instructional Working Days</h3>
        <p className="text-xs">Days attendance is expected. Unmarked past working days show an amber “unrecorded” indicator on the attendance log.</p>
        <div className="flex flex-wrap gap-2">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d=>{
            const on = (s.working_days || ["Mon","Tue","Wed","Thu","Fri","Sat"]).includes(d);
            return <button key={d} type="button" data-testid={`working-day-${d}`}
              onClick={()=>{ const cur=s.working_days || ["Mon","Tue","Wed","Thu","Fri","Sat"];
                setS({...s, working_days: on ? cur.filter(x=>x!==d) : [...cur, d]}); }}
              className={`px-3 py-1.5 rounded-md border-2 border-black text-sm font-bold ${on?"bg-black text-white":"bg-white"}`}>{d}</button>;
          })}
        </div>
        <button onClick={save} className="nb-btn px-3 py-1.5 text-sm" data-testid="working-days-save">Save Working Days</button>
      </div>
      <div className="nb-card p-6 bg-[#FBCFE8] space-y-3" data-testid="fee-gate-editor">
        <h3 className="display text-xl font-bold">Fee Notification Authorisation</h3>
        <p className="text-xs">Restrict who may initiate manual fee reminders. Leave all unchecked to allow any admin (gate disabled). Automated payment receipts are always exempt.</p>
        {accs.length === 0 && <p className="text-xs opacity-70">No accountants yet — add one above to assign.</p>}
        <div className="space-y-1">
          {accs.map(a=>{
            const on = (s.fee_notification_authorised_ids || []).includes(a.user_id);
            return <label key={a.user_id} className="flex items-center gap-2 text-sm" data-testid={`fee-gate-${a.user_id}`}>
              <input type="checkbox" checked={on}
                onChange={()=>{ const cur=s.fee_notification_authorised_ids || [];
                  setS({...s, fee_notification_authorised_ids: on ? cur.filter(x=>x!==a.user_id) : [...cur, a.user_id]}); }}/>
              {a.name} <span className="opacity-60 text-xs">({a.email})</span>
            </label>;
          })}
        </div>
        <button onClick={save} className="nb-btn px-3 py-1.5 text-sm" data-testid="fee-gate-save">Save Authorised List</button>
      </div>
      <HolidayRangeEditor
        title="Annual Holidays (Academic)"
        subtitle="Long breaks (e.g. Winter Vacation). BLOCKS student attendance across the range."
        color="bg-[#FCA5A5]"
        value={s.annual_holidays || []}
        onChange={(v)=>setS({...s, annual_holidays:v})}
        onSave={save}
        testid="annual-holidays"/>
      <HolidayRangeEditor
        title="Mid-Term Vacation Periods"
        subtitle="Short mid-term breaks. BLOCKS student attendance across the range."
        color="bg-[#FDE047]"
        value={s.midterm_vacations || []}
        onChange={(v)=>setS({...s, midterm_vacations:v})}
        onSave={save}
        testid="midterm-vacations"/>
      <HolidayEditor
        title="Staff Holidays (Point Dates)"
        subtitle="Dates when NO staff should be present. Sundays remain workable for staff."
        color="bg-[#93C5FD]"
        value={s.staff_holidays || []}
        onChange={(v)=>setS({...s, staff_holidays:v})}
        onSave={save}
        testid="staff-holidays"/>
      <HolidayRangeEditor
        title="Staff Holiday Ranges"
        subtitle="Longer team breaks (independent of Academic breaks — teams may stay back for results/planning)."
        color="bg-[#C4B5FD]"
        value={s.staff_holiday_ranges || []}
        onChange={(v)=>setS({...s, staff_holiday_ranges:v})}
        onSave={save}
        testid="staff-holiday-ranges"/>
      <SalarySlabsEditor
        value={s.salary_slabs || []}
        onChange={(v)=>setS({...s, salary_slabs:v})}
        onSave={save}/>
      <GradingScaleEditor
        value={s.grading_scale || []}
        onChange={(v)=>setS({...s, grading_scale:v})}
        onSave={save}/>
      <TestCycleDatesEditor
        value={s.test_cycle_dates || []}
        testTypes={s.test_types || []}
        onChange={(v)=>setS({...s, test_cycle_dates:v})}
        onSave={save}/>
      <div className="nb-card p-6 bg-[#93C5FD] space-y-3" data-testid="educator-payroll-visibility">
        <h3 className="display text-xl font-bold">Educator Payroll Visibility</h3>
        <p className="text-xs">Controls whether the Educator console shows the <b>My Payrolls</b> tab. Turn OFF to hide payslip access from all educators globally.</p>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox"
                 checked={s.educator_payroll_visible !== false}
                 onChange={e=>setS({...s, educator_payroll_visible: e.target.checked})}
                 data-testid="educator-payroll-visible-toggle"/>
          <span className="font-bold">Show &quot;My Payrolls&quot; tab in Educator console</span>
        </label>
        <button type="button" onClick={save} className="nb-btn" data-testid="educator-payroll-save">Save</button>
      </div>
      <div className="nb-card p-4 bg-[#FDE047] text-sm">
        <b>Note:</b> All staff (Principal, Educators, Accountant, Non-Teaching) attendance &amp; salary are managed in the <b>Staff &amp; Attendance</b> tab. Logs are stored per Academic Year and feed the Accountant&apos;s payroll engine.
      </div>
    </div>
  </div>
  </div>;
}

function PayrollApprovals() {
  const [runs, setRuns] = useState([]);
  const [tab, setSubTab] = useState("pending");
  const load = async () => {
    setRuns((await api.get("/payroll")).data);
  };
  useEffect(()=>{ load(); }, []);
  const approve = async (pid) => {
    try { await api.post(`/payroll/${pid}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reject = async (pid) => {
    const note = window.prompt("Reason for rejection (optional):") || "";
    try { await api.post(`/payroll/${pid}/reject`, { note }); toast.success("Rejected — back to accountant"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const download = async (pid, name, month) => {
    try {
      const res = await api.get(`/payroll/${pid}/payslip-pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `payslip_${(name||"staff").replace(/\s+/g,"_")}_${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error("PDF unavailable"); }
  };
  const filtered = runs.filter(r => {
    if (tab==="pending") return r.status === "pending_approval";
    if (tab==="approved") return r.status === "approved";
    if (tab==="paid") return r.status === "paid";
    return true;
  });
  return (
    <div className="space-y-4" data-testid="payroll-approvals-panel">
      <div className="flex gap-2 flex-wrap">
        {[["pending","Pending Approval"],["approved","Approved"],["paid","Paid"],["all","All"]].map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)}
                  className={`nb-btn ${tab===id?"":"nb-btn-ghost"}`}
                  data-testid={`pay-approve-tab-${id}`}>{label}</button>
        ))}
      </div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Staff</th><th>Month</th><th>Gross</th><th>Deductions</th><th>Net</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
          {filtered.map(r => (
            <tr key={r.payroll_id} data-testid={`pay-appr-row-${r.payroll_id}`}>
              <td>{r.beneficiary_name || r.educator_name}</td>
              <td className="mono">{r.month}</td>
              <td className="mono">₹{(r.gross||0).toLocaleString()}</td>
              <td className="mono">₹{(r.total_deductions||0).toLocaleString()}</td>
              <td className="mono font-bold">₹{(r.net||0).toLocaleString()}</td>
              <td><span className="nb-badge">{(r.status||"").toUpperCase()}</span></td>
              <td className="space-x-1 whitespace-nowrap">
                {r.status === "pending_approval" && (
                  <>
                    <button onClick={()=>approve(r.payroll_id)}
                            className="nb-btn text-xs" data-testid={`pay-approve-${r.payroll_id}`}>
                      Approve
                    </button>
                    <button onClick={()=>reject(r.payroll_id)}
                            className="nb-btn nb-btn-danger text-xs" data-testid={`pay-reject-${r.payroll_id}`}>
                      Reject
                    </button>
                  </>
                )}
                {(r.status==="approved" || r.status==="paid") && (
                  <button onClick={()=>download(r.payroll_id, r.beneficiary_name || r.educator_name, r.month)}
                          className="nb-btn nb-btn-secondary text-xs"
                          data-testid={`pay-dl-${r.payroll_id}`}>
                    <Download size={12}/> PDF
                  </button>
                )}
                <HistoricalActions
                  overrideKey="historical_payroll"
                  row={{payroll_id: r.payroll_id, bonus: r.bonus || 0, other_deduction: r.other_deduction || 0}}
                  editableFields={["bonus", "other_deduction"]}
                  endpoint={`/payroll/${r.payroll_id}`}
                  onDone={load}
                  testPrefix="payroll"/>
              </td>
            </tr>
          ))}
          {filtered.length===0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No records</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GradingScaleEditor({ value, onChange, onSave }) {
  const scale = value && value.length > 0 ? value : [
    {grade:"A+", min_pct:90, max_pct:100},
    {grade:"A",  min_pct:70, max_pct:89.99},
    {grade:"B+", min_pct:50, max_pct:69.99},
    {grade:"B",  min_pct:40, max_pct:49.99},
    {grade:"C+", min_pct:35, max_pct:39.99},
    {grade:"C",  min_pct:30, max_pct:34.99},
    {grade:"F",  min_pct:0,  max_pct:29.99},
  ];
  const set = (i, field, v) => {
    const next = scale.map((b, idx) => idx === i ? { ...b, [field]: v } : b);
    onChange(next);
  };
  const add = () => onChange([...scale, {grade:"", min_pct:0, max_pct:0}]);
  const remove = (i) => onChange(scale.filter((_, idx) => idx !== i));
  return (
    <div className="nb-card p-6 bg-[#FDE047] space-y-3" data-testid="grading-scale-editor">
      <h3 className="display text-xl font-bold">Grading System</h3>
      <p className="text-xs">Letter grade boundaries used across <b>Marks Cards</b> and <b>Report Cards</b>. Bands are inclusive on both ends.</p>
      <div className="space-y-1">
        {scale.map((b, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`grade-row-${b.grade || i}`}>
            <input className="nb-input w-20 mono uppercase" value={b.grade}
                   onChange={e=>set(i, "grade", e.target.value.toUpperCase())}
                   data-testid={`grade-letter-${i}`}/>
            <input type="number" step="0.01" className="nb-input w-24 mono" value={b.min_pct}
                   onChange={e=>set(i, "min_pct", parseFloat(e.target.value)||0)}
                   data-testid={`grade-min-${i}`}/>
            <span className="text-xs">to</span>
            <input type="number" step="0.01" className="nb-input w-24 mono" value={b.max_pct}
                   onChange={e=>set(i, "max_pct", parseFloat(e.target.value)||0)}
                   data-testid={`grade-max-${i}`}/>
            <span className="text-xs">%</span>
            <button type="button" onClick={()=>remove(i)}
                    className="nb-btn nb-btn-danger px-2 py-0.5 text-[10px]"
                    data-testid={`grade-del-${i}`}>×</button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-2 border-t-2 border-black">
        <button type="button" onClick={add} className="nb-btn flex-1 justify-center" data-testid="grade-add">
          <Plus size={14}/> Add Band
        </button>
        <button type="button" onClick={onSave} className="nb-btn nb-btn-tertiary" data-testid="grade-save">
          Save Grading Scale
        </button>
      </div>
    </div>
  );
}

function TestCycleDatesEditor({ value, testTypes, onChange, onSave }) {
  const rows = value || [];
  const set = (i, field, v) => {
    const next = rows.map((r, idx) => idx === i ? { ...r, [field]: v } : r);
    onChange(next);
  };
  const add = () => onChange([...rows, {test_type: testTypes?.[0] || "FA1", start_date: "", end_date: ""}]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="nb-card p-6 bg-[#93C5FD] space-y-3" data-testid="cycle-dates-editor">
      <h3 className="display text-xl font-bold">Test Cycle Date Ranges</h3>
      <p className="text-xs">Assign a start &amp; end date per Test Type. Attendance on Marks Cards is aggregated over this range.</p>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`cycle-row-${r.test_type}`}>
            <select className="nb-select w-28" value={r.test_type}
                    onChange={e=>set(i, "test_type", e.target.value)}
                    data-testid={`cycle-type-${i}`}>
              {(testTypes||[]).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input type="date" className="nb-input" value={r.start_date || ""}
                   onChange={e=>set(i, "start_date", e.target.value)}
                   data-testid={`cycle-start-${i}`}/>
            <span className="text-xs">→</span>
            <input type="date" className="nb-input" value={r.end_date || ""}
                   onChange={e=>set(i, "end_date", e.target.value)}
                   data-testid={`cycle-end-${i}`}/>
            <button type="button" onClick={()=>remove(i)}
                    className="nb-btn nb-btn-danger px-2 py-0.5 text-[10px]"
                    data-testid={`cycle-del-${i}`}>×</button>
          </div>
        ))}
        {rows.length===0 && <div className="text-xs italic">No cycles configured yet. Add one for each Test Type.</div>}
      </div>
      <div className="flex gap-2 pt-2 border-t-2 border-black">
        <button type="button" onClick={add} className="nb-btn flex-1 justify-center" data-testid="cycle-add">
          <Plus size={14}/> Add Cycle
        </button>
        <button type="button" onClick={onSave} className="nb-btn nb-btn-tertiary" data-testid="cycle-save">
          Save Cycle Dates
        </button>
      </div>
    </div>
  );
}


function SalarySlabsEditor({ value, onChange, onSave }) {
  const empty = {name:"", basic:0, da:0, hra:0, ta:0, pf_pct:0, gratuity_pct:0, allowed_paid_leaves_month:1.5};
  const [f, setF] = useState(empty);
  const add = () => {
    if (!f.name.trim()) return toast.error("Slab name required");
    if ((value||[]).some(s => s.name === f.name)) return toast.error("Slab name already exists");
    const norm = {
      name: f.name.trim(),
      basic: parseFloat(f.basic)||0, da: parseFloat(f.da)||0,
      hra: parseFloat(f.hra)||0, ta: parseFloat(f.ta)||0,
      pf_pct: parseFloat(f.pf_pct)||0, gratuity_pct: parseFloat(f.gratuity_pct)||0,
      allowed_paid_leaves_month: parseFloat(f.allowed_paid_leaves_month)||1.5,
    };
    onChange([...(value||[]), norm]);
    setF(empty);
  };
  const remove = (idx) => onChange((value||[]).filter((_,i)=>i!==idx));
  const gross = (parseFloat(f.basic)||0) + (parseFloat(f.da)||0) + (parseFloat(f.hra)||0) + (parseFloat(f.ta)||0);
  const pfAmt = (parseFloat(f.basic)||0) * (parseFloat(f.pf_pct)||0) / 100;
  const grAmt = (parseFloat(f.basic)||0) * (parseFloat(f.gratuity_pct)||0) / 100;
  const net = gross - pfAmt - grAmt;
  return (
    <div className="nb-card p-6 bg-[#86EFAC] space-y-3" data-testid="salary-slabs-editor">
      <h3 className="display text-xl font-bold">Salary Slabs</h3>
      <p className="text-xs">Configure the Additions (Basic, DA, HRA, TA) and Deductions (PF %, Gratuity %) plus paid-leave allowance per slab. Assign slabs to staff on the <b>Staff &amp; Attendance</b> tab — the Accountant&apos;s Payroll engine uses these values.</p>
      <div className="grid grid-cols-2 gap-2">
        <label><div className="nb-label">Slab Name</div>
          <input className="nb-input" placeholder="e.g. Grade A" value={f.name}
                 onChange={e=>setF({...f, name:e.target.value})}
                 data-testid="slab-name"/></label>
        <label><div className="nb-label">Basic ₹</div>
          <input type="number" className="nb-input mono" value={f.basic}
                 onChange={e=>setF({...f, basic:e.target.value})}
                 data-testid="slab-basic"/></label>
        <label><div className="nb-label">DA ₹</div>
          <input type="number" className="nb-input mono" value={f.da}
                 onChange={e=>setF({...f, da:e.target.value})}
                 data-testid="slab-da"/></label>
        <label><div className="nb-label">HRA ₹</div>
          <input type="number" className="nb-input mono" value={f.hra}
                 onChange={e=>setF({...f, hra:e.target.value})}
                 data-testid="slab-hra"/></label>
        <label><div className="nb-label">TA ₹</div>
          <input type="number" className="nb-input mono" value={f.ta}
                 onChange={e=>setF({...f, ta:e.target.value})}
                 data-testid="slab-ta"/></label>
        <label><div className="nb-label">PF %</div>
          <input type="number" step="0.01" className="nb-input mono" value={f.pf_pct}
                 onChange={e=>setF({...f, pf_pct:e.target.value})}
                 data-testid="slab-pf"/></label>
        <label><div className="nb-label">Gratuity %</div>
          <input type="number" step="0.01" className="nb-input mono" value={f.gratuity_pct}
                 onChange={e=>setF({...f, gratuity_pct:e.target.value})}
                 data-testid="slab-gratuity"/></label>
        <label><div className="nb-label">Paid Leaves / Month</div>
          <input type="number" step="0.5" className="nb-input mono" value={f.allowed_paid_leaves_month}
                 onChange={e=>setF({...f, allowed_paid_leaves_month:e.target.value})}
                 data-testid="slab-paid-leaves"/></label>
      </div>
      <div className="nb-card-sm p-2 bg-white text-xs mono">
        Preview → Gross ₹{gross.toLocaleString()} · PF ₹{pfAmt.toFixed(2)} · Gratuity ₹{grAmt.toFixed(2)} · Net ₹{net.toFixed(2)}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={add} className="nb-btn flex-1 justify-center" data-testid="slab-add">
          <Plus size={16}/> Add Slab
        </button>
        <button type="button" onClick={onSave} className="nb-btn nb-btn-tertiary" data-testid="slab-save-all">
          Save
        </button>
      </div>
      {(value||[]).length > 0 && (
        <div className="space-y-1 max-h-64 overflow-y-auto pt-2 border-t-2 border-black/20">
          {(value||[]).map((sl, i) => (
            <div key={`${sl.name}-${i}`}
                 className="flex flex-col bg-white border-2 border-black px-2 py-1 rounded text-xs"
                 data-testid={`slab-row-${sl.name}`}>
              <div className="flex items-center gap-2">
                <span className="font-bold flex-1">{sl.name}</span>
                <span className="mono">Basic ₹{(sl.basic||0).toLocaleString()}</span>
                <button type="button" onClick={()=>remove(i)}
                        className="nb-btn nb-btn-danger px-2 py-0.5 text-[10px]"
                        data-testid={`slab-del-${sl.name}`}>Remove</button>
              </div>
              <div className="mono text-[10px] text-slate-600">
                DA ₹{sl.da||0} · HRA ₹{sl.hra||0} · TA ₹{sl.ta||0} · PF {sl.pf_pct||0}% · Gratuity {sl.gratuity_pct||0}% · Paid Leaves {sl.allowed_paid_leaves_month||1.5}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HolidayRangeEditor({ title, subtitle, color, value, onChange, onSave, testid }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [name, setName] = useState("");
  const add = () => {
    if (!from || !to || !name) { toast.error("From, To, Name required"); return; }
    if (from > to) { toast.error("`From` date must be ≤ `To` date"); return; }
    onChange([...(value||[]), {from, to, name}].sort((a,b)=>a.from.localeCompare(b.from)));
    setFrom(""); setTo(""); setName("");
  };
  const remove = (idx) => onChange((value||[]).filter((_,i)=>i!==idx));
  return (
    <div className={`nb-card p-6 ${color} space-y-3`} data-testid={testid}>
      <h3 className="display text-xl font-bold">{title}</h3>
      <p className="text-xs">{subtitle}</p>
      <div className="grid grid-cols-2 gap-2">
        <label><div className="nb-label">From</div>
          <input type="date" className="nb-input" value={from} onChange={e=>setFrom(e.target.value)} data-testid={`${testid}-from`}/></label>
        <label><div className="nb-label">To</div>
          <input type="date" className="nb-input" value={to} onChange={e=>setTo(e.target.value)} data-testid={`${testid}-to`}/></label>
      </div>
      <label className="block"><div className="nb-label">Name</div>
        <input className="nb-input" placeholder="e.g. Winter Break" value={name}
               onChange={e=>setName(e.target.value)} data-testid={`${testid}-name`}/></label>
      <button type="button" onClick={add} className="nb-btn w-full justify-center" data-testid={`${testid}-add`}>
        <Plus size={16}/> Add Range
      </button>
      {(value||[]).length > 0 && (
        <div className="space-y-1 max-h-56 overflow-y-auto">
          {value.map((h, i) => (
            <div key={i} className="flex items-center justify-between gap-2 bg-white border-2 border-black px-3 py-1.5 rounded">
              <span className="mono text-xs">{h.from} → {h.to}</span>
              <span className="text-sm flex-1 truncate">{h.name}</span>
              <button type="button" onClick={()=>remove(i)}
                      className="nb-btn nb-btn-danger px-2 py-0.5 text-xs"
                      data-testid={`${testid}-del-${i}`}>Remove</button>
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={(e)=>onSave(e)}
              className="nb-btn w-full justify-center"
              data-testid={`${testid}-save`}>Save</button>
    </div>
  );
}

function HolidayEditor({ title="Holidays", subtitle, color="bg-[#FCA5A5]", value, onChange, onSave, testid="holiday-editor" }) {
  const [d, setD] = useState("");
  const [n, setN] = useState("");
  const add = () => {
    if (!d || !n) { toast.error("Date & name required"); return; }
    if ((value||[]).some(h => h.date === d)) { toast.error("Date already added"); return; }
    const next = [...(value||[]), {date: d, name: n}].sort((a,b)=>a.date.localeCompare(b.date));
    onChange(next); setD(""); setN("");
  };
  const remove = (date) => onChange((value||[]).filter(h => h.date !== date));
  return (
    <div className={`nb-card p-6 ${color} space-y-3`} data-testid={testid}>
      <h3 className="display text-xl font-bold">{title}</h3>
      <p className="text-xs">{subtitle || "Marked red on attendance grids. Students cannot be marked present on holidays. Sundays are auto-marked separately."}</p>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="flex-1 min-w-[140px]"><div className="nb-label">Date</div>
          <input type="date" className="nb-input" value={d} onChange={e=>setD(e.target.value)} data-testid={`${testid}-date`}/></label>
        <label className="flex-[2] min-w-[160px]"><div className="nb-label">Name</div>
          <input className="nb-input" placeholder="e.g. Diwali" value={n} onChange={e=>setN(e.target.value)} data-testid={`${testid}-name`}/></label>
        <button type="button" onClick={add} className="nb-btn" data-testid={`${testid}-add`}><Plus size={16}/>Add</button>
      </div>
      {(value||[]).length === 0 ? (
        <div className="text-sm text-slate-700">No holidays configured yet.</div>
      ) : (
        <div className="space-y-1 max-h-56 overflow-y-auto">
          {value.map(h => (
            <div key={h.date} className="flex items-center justify-between gap-2 bg-white border-2 border-black px-3 py-1.5 rounded">
              <span className="mono text-xs">{h.date}</span>
              <span className="text-sm flex-1 truncate">{h.name}</span>
              <button type="button" onClick={()=>remove(h.date)} className="nb-btn nb-btn-danger px-2 py-0.5 text-xs" data-testid={`${testid}-del-${h.date}`}>Remove</button>
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={(e)=>onSave(e)} className="nb-btn w-full justify-center" data-testid={`${testid}-save`}>Save Holidays</button>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(()=>{ api.get("/admin/stats").then(r=>setS(r.data)); }, []);
  if (!s) return <div className="mono">Loading…</div>;
  const cards = [
    {label:"Educators", val:s.educators, bg:"bg-[#C4B5FD]"},
    {label:"Students", val:s.students, bg:"bg-[#86EFAC]"},
    {label:"Revenue (₹)", val:s.revenue.toFixed(2), bg:"bg-[#FDE047]"},
    {label:"Pending Invoices", val:s.pending_invoices, bg:"bg-[#FCA5A5]"},
    {label:"Paid Invoices", val:s.paid_invoices, bg:"bg-[#93C5FD]"},
    {label:"New Enquiries", val:s.new_enquiries, bg:"bg-[#C4B5FD]"},
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {cards.map((c)=>(
        <div key={c.label} className={`nb-card p-6 ${c.bg}`} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g,'-')}`}>
          <div className="nb-label">{c.label}</div>
          <div className="display text-4xl font-black mt-2">{c.val}</div>
        </div>
      ))}
    </div>
  );
}

function Educators() {
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState({grades:[], sections:[]});
  const [editing, setEditing] = useState(null);  // educator object being edited
  const empty = {name:"", email:"", mobile:"", subject:"", class_name:"", section:"", password:"edu123"};
  const [f, setF] = useState(empty);
  const load = async () => {
    const [e, s] = await Promise.all([
      api.get("/educators"), api.get("/settings"),
    ]);
    setRows(e.data); setSettings(s.data);
  };
  useEffect(()=>{ load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/educators", f); toast.success("Educator added"); setF(empty); load(); }
    catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { await api.delete(`/educators/${id}`); load(); };
  // Iter50 — grant / extend / revoke temporary class-access override.
  const [overrideOpen, setOverrideOpen] = useState(null); // {educator_id, name, current_expires_at}
  const [overrideExpiry, setOverrideExpiry] = useState("");
  const [overrideBusy, setOverrideBusy] = useState(false);
  const openOverride = (r) => {
    setOverrideOpen({educator_id: r.educator_id, name: r.name,
                     current_expires_at: r.override_expires_at || null});
    // Prefill with the current expiry (if any) so the admin can extend it easily.
    if (r.override_expires_at) {
      try { setOverrideExpiry(new Date(r.override_expires_at).toISOString().slice(0,16)); }
      catch { setOverrideExpiry(""); }
    } else {
      // Default: end of today.
      const d = new Date(); d.setHours(23,59,0,0);
      setOverrideExpiry(d.toISOString().slice(0,16));
    }
  };
  const submitOverride = async (revoke=false) => {
    if (!overrideOpen) return;
    setOverrideBusy(true);
    try {
      const body = revoke ? {} :
        { expires_at: new Date(overrideExpiry).toISOString() };
      await api.post(`/educators/${overrideOpen.educator_id}/override-access`, body);
      toast.success(revoke ? "Override revoked" : "Override granted");
      setOverrideOpen(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setOverrideBusy(false); }
  };
  const saveEdit = async () => {
    if (!editing) return;
    const patch = {
      name: editing.name, email: editing.email, mobile: editing.mobile,
      subject: editing.subject, class_name: editing.class_name, section: editing.section,
      salary_slab: editing.salary_slab || null,
    };
    try {
      await api.patch(`/educators/${editing.educator_id}`, patch);
      toast.success("Educator updated"); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const resetPw = async (r) => {
    const pw = window.prompt(`New password for ${r.email} (min 6 chars):`);
    if (!pw) return;
    try {
      await api.post(`/users/${r.user_id}/reset-password`, {new_password: pw});
      toast.success(`Password reset for ${r.email}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
  };
  return (
    <div className="space-y-6">
      <div className="nb-card p-5 bg-[#93C5FD]">
        <div className="nb-label">Educators — Class Assignment</div>
        <div className="font-bold">This tab assigns Educators to a class + section. Attendance, salary
          (basic pay) and non-teaching staff have moved to the <b>Staff &amp; Attendance</b> tab.</div>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={create} className="nb-card p-6 bg-[#FDE047] space-y-3 h-fit">
          <h3 className="display text-xl font-bold">Add Educator</h3>
          <input data-testid="edu-name" className="nb-input" placeholder="Name" required value={f.name} onChange={e=>setF({...f,name:e.target.value})}/>
          <input data-testid="edu-email" className="nb-input" placeholder="Email" type="email" required value={f.email} onChange={e=>setF({...f,email:e.target.value})}/>
          <input data-testid="edu-mobile" className="nb-input" placeholder="Mobile" required value={f.mobile} onChange={e=>setF({...f,mobile:e.target.value})}/>
          <input data-testid="edu-subject" className="nb-input" placeholder="Subject" required value={f.subject} onChange={e=>setF({...f,subject:e.target.value})}/>
          <div className="grid grid-cols-2 gap-2">
            <select required className="nb-select" data-testid="edu-class" value={f.class_name} onChange={e=>setF({...f,class_name:e.target.value})}>
              <option value="">Grade</option>
              {settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
            </select>
            <select required className="nb-select" data-testid="edu-section" value={f.section} onChange={e=>setF({...f,section:e.target.value})}>
              <option value="">Sec</option>
              {settings.sections?.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <input data-testid="edu-password" className="nb-input" placeholder="Default password" value={f.password} onChange={e=>setF({...f,password:e.target.value})}/>
          <button data-testid="edu-create-btn" className="nb-btn w-full justify-center"><Plus size={16}/>Add</button>
        </form>
        <div className="nb-card p-0 overflow-hidden lg:col-span-2">
          <div className="px-6 py-3 border-b-2 border-black bg-[#C4B5FD] font-bold">Educators — Class Assignments</div>
          <table className="nb-table">
            <thead><tr><th>Name</th><th>Subject</th><th>Assignment</th><th>Class-Lock Override</th><th></th></tr></thead>
            <tbody>
            {rows.map(r => {
              const expIso = r.override_expires_at;
              const active = expIso && new Date(expIso) > new Date();
              return (
              <tr key={r.educator_id} data-testid={`edu-row-${r.educator_id}`}>
                <td className="font-bold">{r.name}</td><td>{r.subject}</td>
                <td><span className="nb-badge bg-[#C4B5FD]">{r.class_name||"—"}-{r.section||"—"}</span></td>
                <td className="text-xs">
                  {active ? (
                    <span className="nb-badge bg-[#86EFAC]" data-testid={`edu-override-active-${r.educator_id}`}>
                      unlocked until {new Date(expIso).toLocaleString(undefined,{month:"short",day:"2-digit",hour:"2-digit",minute:"2-digit"})}
                    </span>
                  ) : (
                    <span className="text-slate-500">locked</span>
                  )}
                </td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    <button onClick={()=>setEditing({...r})}
                            className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                            data-testid={`edu-edit-${r.educator_id}`}>
                      <Pencil size={12}/> Edit
                    </button>
                    <button onClick={()=>openOverride(r)}
                            className="nb-btn nb-btn-tertiary px-2 py-1 text-xs"
                            data-testid={`edu-override-${r.educator_id}`}
                            title="Temporarily unlock the Grade & Section dropdowns for this educator">
                      <Clock size={12}/> {active ? "Extend / Revoke" : "Grant Access"}
                    </button>
                    <button onClick={()=>resetPw(r)}
                            className="nb-btn nb-btn-tertiary px-2 py-1 text-xs"
                            data-testid={`edu-reset-pw-${r.educator_id}`}>
                      <KeyRound size={12}/> PW
                    </button>
                    <button onClick={()=>remove(r.educator_id)}
                            className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                            data-testid={`edu-delete-${r.educator_id}`}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </td>
              </tr>
            );})}
            {rows.length===0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No educators yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={()=>setEditing(null)} data-testid="edu-edit-modal">
          <div className="nb-card p-6 bg-white max-w-md w-full space-y-3"
               onClick={e=>e.stopPropagation()}>
            <h3 className="display text-xl font-bold">Edit Educator</h3>
            <label className="block"><span className="nb-label">Name</span>
              <input className="nb-input" value={editing.name||""}
                     onChange={e=>setEditing({...editing, name:e.target.value})}
                     data-testid="edu-edit-name"/></label>
            <label className="block"><span className="nb-label">Email</span>
              <input className="nb-input" type="email" value={editing.email||""}
                     onChange={e=>setEditing({...editing, email:e.target.value})}
                     data-testid="edu-edit-email"/></label>
            <label className="block"><span className="nb-label">Mobile</span>
              <input className="nb-input" value={editing.mobile||""}
                     onChange={e=>setEditing({...editing, mobile:e.target.value})}/></label>
            <label className="block"><span className="nb-label">Subject</span>
              <input className="nb-input" value={editing.subject||""}
                     onChange={e=>setEditing({...editing, subject:e.target.value})}
                     data-testid="edu-edit-subject"/></label>
            <div className="grid grid-cols-2 gap-2">
              <label><span className="nb-label">Class / Grade</span>
                <select className="nb-select" value={editing.class_name||""}
                        onChange={e=>setEditing({...editing, class_name:e.target.value})}
                        data-testid="edu-edit-class">
                  <option value="">—</option>
                  {settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
                </select></label>
              <label><span className="nb-label">Section</span>
                <select className="nb-select" value={editing.section||""}
                        onChange={e=>setEditing({...editing, section:e.target.value})}
                        data-testid="edu-edit-section">
                  <option value="">—</option>
                  {settings.sections?.map(s=><option key={s} value={s}>{s}</option>)}
                </select></label>
            </div>
            <label className="block"><span className="nb-label">Salary Slab</span>
              <select className="nb-select" value={editing.salary_slab||""}
                      onChange={e=>setEditing({...editing, salary_slab:e.target.value})}
                      data-testid="edu-edit-salary-slab">
                <option value="">— No slab (uses legacy Basic salary) —</option>
                {(settings.salary_slabs||[]).map(sl=>(
                  <option key={sl.name} value={sl.name}>
                    {sl.name} · Basic ₹{sl.basic||0} · PF {sl.pf_pct||0}% · Gratuity {sl.gratuity_pct||0}%
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-slate-600 mt-1">Slabs are configured in <b>Admin → Settings → Salary Slabs</b>.</div>
            </label>
            <div className="flex gap-2 pt-2">
              <button onClick={()=>setEditing(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={saveEdit} className="nb-btn flex-1 justify-center"
                      data-testid="edu-edit-save">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Iter50 — Class-Lock Override modal (School Admin can temporarily unlock
          the Grade/Section dropdowns in Attendance + Marks tabs for a
          specific educator, e.g. when they're covering a substitute class). */}
      {overrideOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={()=>setOverrideOpen(null)}
             data-testid="edu-override-modal">
          <div className="nb-card p-6 bg-white max-w-md w-full space-y-3"
               onClick={e=>e.stopPropagation()}>
            <h3 className="display text-xl font-bold">Class Access Override</h3>
            <p className="text-xs">
              Temporarily unlock the Grade &amp; Section dropdowns for <b>{overrideOpen.name}</b> in
              the Attendance and Marks Cards tabs. Auto-revokes after the expiry passes.
            </p>
            {overrideOpen.current_expires_at && new Date(overrideOpen.current_expires_at) > new Date() && (
              <div className="nb-card-sm bg-[#DBEAFE] p-2 text-xs">
                Currently unlocked until <b>{new Date(overrideOpen.current_expires_at).toLocaleString()}</b>.
                Update the expiry below to extend, or Revoke Now to end early.
              </div>
            )}
            <label className="block">
              <span className="nb-label">Expiry date &amp; time</span>
              <input type="datetime-local" className="nb-input"
                     value={overrideExpiry}
                     onChange={e=>setOverrideExpiry(e.target.value)}
                     data-testid="edu-override-expiry"/>
              <div className="flex gap-1 mt-1">
                {[
                  {label:"End of today", h:23, m:59, days:0},
                  {label:"+1 day",       h:23, m:59, days:1},
                  {label:"+3 days",      h:23, m:59, days:3},
                  {label:"+1 week",      h:23, m:59, days:7},
                ].map(p => (
                  <button key={p.label} type="button" className="nb-btn nb-btn-ghost text-[10px] px-2 py-0.5"
                    onClick={()=>{
                      const d = new Date(); d.setDate(d.getDate()+p.days);
                      d.setHours(p.h,p.m,0,0);
                      setOverrideExpiry(d.toISOString().slice(0,16));
                    }}
                    data-testid={`edu-override-preset-${p.days}`}>{p.label}</button>
                ))}
              </div>
            </label>
            <div className="flex gap-2 pt-2">
              <button onClick={()=>setOverrideOpen(null)}
                      className="nb-btn nb-btn-ghost flex-1 justify-center"
                      disabled={overrideBusy}>Cancel</button>
              {overrideOpen.current_expires_at && new Date(overrideOpen.current_expires_at) > new Date() && (
                <button onClick={()=>submitOverride(true)}
                        className="nb-btn nb-btn-danger flex-1 justify-center"
                        disabled={overrideBusy}
                        data-testid="edu-override-revoke">Revoke Now</button>
              )}
              <button onClick={()=>submitOverride(false)}
                      className="nb-btn flex-1 justify-center"
                      disabled={overrideBusy || !overrideExpiry}
                      data-testid="edu-override-save">
                {overrideBusy ? "Saving…" : (overrideOpen.current_expires_at ? "Update Expiry" : "Grant Access")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== Staff & Attendance (unified: educators + admin/accountant + non-teaching) ========== */
function StaffAttendance() {
  const TODAY = new Date().toISOString().slice(0,10);
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [log, setLog] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(TODAY.slice(0,7));  // "YYYY-MM"
  const [ay, setAy] = useState("");
  const [dept, setDept] = useState("");
  const [overrideActive, setOverrideActive] = useState(false);
  const [overrideExp, setOverrideExp] = useState(null);
  const empty = {name:"", email:"", mobile:"", designation:"Teacher (Non-Class)",
                 department:"Non-Teaching", monthly_salary:0, pf_number:"",
                 bank_account:"", date_of_joining:"", academic_year:"", payroll_type:""};
  const [f, setF] = useState(empty);
  const [staffSettings, setStaffSettings] = useState({holidays:[]});
  const [staffStatusMap, setStaffStatusMap] = useState({});
  const staffHolidayMap = React.useMemo(() => {
    const m = {};
    (staffSettings.holidays || []).forEach(h => { m[h.date] = h.name; });
    return m;
  }, [staffSettings]);

  const loadStatic = async () => {
    const [s, r, ovr, sett] = await Promise.all([
      api.get("/staff"),
      api.get("/staff/roles"),
      api.get("/overrides/my").catch(()=>({data:{}})),
      api.get("/settings").catch(()=>({data:{}})),
    ]);
    setStaff(s.data); setRoles(r.data.roles);
    setOverrideActive(!!ovr.data?.staff_attendance?.active);
    setOverrideExp(ovr.data?.staff_attendance?.expires_at || null);
    setStaffSettings(sett.data || {});
  };
  const loadLog = async () => {
    const att = await api.get("/staff-attendance", { params: { month: selectedMonth }});
    setLog(att.data);
    api.get("/attendance/month-status", { params: { month: selectedMonth, scope: "staff" }})
      .then(r=>setStaffStatusMap(r.data?.statuses || {})).catch(()=>setStaffStatusMap({}));
  };
  useEffect(()=>{ loadStatic(); }, []);
  useEffect(()=>{ loadLog(); }, [selectedMonth]);   // eslint-disable-line

  const filtered = staff.filter(s => !dept || s.department === dept);

  const addStaff = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff", {...f, monthly_salary: parseFloat(f.monthly_salary || 0),
                                 academic_year: f.academic_year || ay});
      toast.success("Staff added"); setF(empty); loadStatic();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const saveSalary = async (row, value) => {
    try {
      await api.post("/staff/salary", { kind: row.kind, id: row.beneficiary_id,
                                        monthly_salary: parseFloat(value||0) });
      toast.success(`Salary saved · ${row.name}`); loadStatic();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const markCell = async (row, date, status) => {
    try {
      await api.post("/staff-attendance",
        { kind: row.kind, id: row.beneficiary_id, date, status,
          academic_year: ay || undefined });
      loadLog();
    } catch (err) { toast.error(err?.response?.data?.detail || "Locked"); }
  };
  const deleteStaff = async (row) => {
    if (row.kind !== "staff") return toast.info("Only non-teaching / support staff can be removed here");
    if (!window.confirm(`Remove ${row.name}?`)) return;
    await api.delete(`/staff/${row.beneficiary_id}`); loadStatic();
  };
  const [editStaff, setEditStaff] = useState(null);
  const openEditStaff = (row) => {
    if (row.kind !== "staff") { toast.info("Only rows added here can be edited. Educators/Principal/Accountant are managed in their own tabs."); return; }
    setEditStaff({...row});
  };
  const saveEditStaff = async () => {
    if (!editStaff) return;
    try {
      await api.patch(`/staff/${editStaff.beneficiary_id}`, {
        name: editStaff.name, email: editStaff.email, mobile: editStaff.mobile,
        designation: editStaff.designation, department: editStaff.department,
        monthly_salary: parseFloat(editStaff.monthly_salary||0),
        pf_number: editStaff.pf_number, bank_account: editStaff.bank_account,
        date_of_joining: editStaff.date_of_joining, academic_year: editStaff.academic_year,
        salary_slab: editStaff.salary_slab || null,
      });
      toast.success("Staff updated"); setEditStaff(null); loadStatic();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  // Monthly grid
  const days = [];
  const d0 = new Date(selectedMonth + "-01");
  const m0 = d0.getMonth();
  while (d0.getMonth() === m0) { days.push(d0.toISOString().slice(0,10)); d0.setDate(d0.getDate()+1); }
  const logIdx = {};
  log.forEach(r => { logIdx[`${r.kind}|${r.beneficiary_id}|${r.date}`] = r.status; });
  const monthLabel = new Date(selectedMonth + "-01").toLocaleString("en-US", { month: "short", year: "numeric" }).replace(" ", "");
  const shiftMonth = (delta) => {
    const d = new Date(selectedMonth + "-01");
    d.setMonth(d.getMonth() + delta);
    setSelectedMonth(d.toISOString().slice(0,7));
  };
  const cellStatus = (row, d) => {
    const st = logIdx[`${row.kind}|${row.beneficiary_id}|${d}`];
    if (st) return st;
    // Prefill: default P (present) for dates from DOJ up to today
    if (d <= TODAY) return "present";
    return null;   // future — blank
  };
  const cellEditable = (d) => {
    if (d > TODAY) return false;           // never edit future
    if (d === TODAY) return true;           // today always editable
    return overrideActive;                  // past dates only when override active
  };
  const cycleStatus = (curr) => curr === "present" ? "half_day"
                              : curr === "half_day" ? "leave"
                              : curr === "leave" ? "absent"
                              : "present";
  const cellClick = (row, d, currStatus) => {
    if (d > TODAY) { toast.info("Future dates are locked"); return; }
    if (d < TODAY && !overrideActive) {
      toast.info("Locked — ask Global Admin to enable Staff Attendance override");
      return;
    }
    markCell(row, d, cycleStatus(currStatus));
  };
  const DEPT_OPTS = ["Teaching","Administration","Non-Teaching","Support"];

  return (
    <div className="space-y-6">
      <div className="nb-card p-5 bg-[#93C5FD] flex flex-wrap items-end gap-4">
        <div>
          <div className="display text-xl font-bold">Staff &amp; Attendance</div>
          <p className="text-sm mt-1">Centralised roster for Principal / School Admin, Accountant, Educators
            and Non-Teaching Staff. Attendance defaults to <b>Present</b> for past dates — tap a cell to change to Leave (L) or Absent (A).</p>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <label><div className="nb-label">Academic Year</div>
            <input className="nb-input w-32 mono" placeholder="2025-26"
                   value={ay} onChange={e=>setAy(e.target.value)}
                   data-testid="staff-ay"/></label>
          <label><div className="nb-label">Department</div>
            <select className="nb-select w-40" value={dept} onChange={e=>setDept(e.target.value)}
                    data-testid="staff-dept-filter">
              <option value="">All</option>
              {DEPT_OPTS.map(d=><option key={d} value={d}>{d}</option>)}
            </select></label>
        </div>
      </div>

      {overrideActive && (
        <div className="nb-card p-3 bg-[#FDE047] flex items-center gap-3" data-testid="staff-override-banner">
          <Clock size={18}/>
          <div className="font-bold text-sm">
            Historical staff-attendance override is ACTIVE — past-date cells are editable.
            Expires {(overrideExp||"").slice(0,16).replace("T"," ")} UTC.
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={addStaff} className="nb-card p-6 bg-[#FDE047] space-y-2 h-fit">
          <h3 className="display text-xl font-bold">Add Staff (Non-Teaching / Teacher-not-Class-Teacher)</h3>
          <input className="nb-input" placeholder="Full Name" required
                 value={f.name} onChange={e=>setF({...f, name:e.target.value})}
                 data-testid="staff-name"/>
          <input className="nb-input" placeholder="Email (optional)"
                 value={f.email} onChange={e=>setF({...f, email:e.target.value})}/>
          <input className="nb-input" placeholder="Mobile"
                 value={f.mobile} onChange={e=>setF({...f, mobile:e.target.value})}/>
          <div className="grid grid-cols-2 gap-2">
            <select className="nb-select" value={f.designation}
                    onChange={e=>setF({...f, designation:e.target.value})}
                    data-testid="staff-designation">
              {roles.map(r=><option key={r} value={r}>{r}</option>)}
            </select>
            <select className="nb-select" value={f.department}
                    onChange={e=>setF({...f, department:e.target.value})}>
              {DEPT_OPTS.map(d=><option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <input type="number" className="nb-input mono" placeholder="Basic Salary ₹/month"
                 value={f.monthly_salary}
                 onChange={e=>setF({...f, monthly_salary:e.target.value})}
                 data-testid="staff-salary"/>
          <div className="grid grid-cols-2 gap-2">
            <input className="nb-input mono" placeholder="PF #"
                   value={f.pf_number}
                   onChange={e=>setF({...f, pf_number:e.target.value})}/>
            <input className="nb-input mono" placeholder="Bank A/C"
                   value={f.bank_account}
                   onChange={e=>setF({...f, bank_account:e.target.value})}/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" className="nb-input"
                   value={f.date_of_joining}
                   onChange={e=>setF({...f, date_of_joining:e.target.value})}/>
            <input className="nb-input mono" placeholder="Academic Year 2025-26"
                   value={f.academic_year || ay}
                   onChange={e=>setF({...f, academic_year:e.target.value})}/>
          </div>
          <div>
            <div className="nb-label mb-1">Payroll Type / Department (Expense mapping)</div>
            <select className="nb-select" value={f.payroll_type||""}
                    onChange={e=>setF({...f, payroll_type:e.target.value})}
                    data-testid="staff-payroll-type">
              <option value="">— Not tagged —</option>
              {(staffSettings.expense_categories||[]).map(cat=><option key={cat} value={cat}>{cat}</option>)}
            </select>
            <div className="text-[10px] text-slate-600 mt-1">Payroll runs for this staff will be auto-tagged with this expense category so ledger roll-ups work.</div>
          </div>
          <button className="nb-btn w-full justify-center" data-testid="staff-add-btn">
            <Plus size={16}/> Add Staff
          </button>
        </form>

        <div className="nb-card p-0 overflow-hidden lg:col-span-2">
          <div className="px-6 py-3 border-b-2 border-black bg-[#C4B5FD] font-bold">
            Full Staff Roster ({filtered.length})
          </div>
          <div className="overflow-x-auto">
            <table className="nb-table">
              <thead><tr>
                <th>Name</th><th>Designation</th><th>Dept</th>
                <th>Basic Salary</th><th></th>
              </tr></thead>
              <tbody>
              {filtered.map(r=>{
                const key = `${r.kind}|${r.beneficiary_id}`;
                return (
                  <tr key={key} data-testid={`staff-row-${key}`}>
                    <td className="font-bold">{r.name}
                      {r.class_name && <span className="ml-1 text-xs nb-badge bg-[#86EFAC]">Class {r.class_name}-{r.section}</span>}
                    </td>
                    <td>{r.designation}</td>
                    <td>{r.department}</td>
                    <td>
                      <input type="number" min="0" defaultValue={r.monthly_salary||0}
                             className="nb-input w-28 text-sm mono"
                             data-testid={`staff-salary-${key}`}
                             onBlur={e=>saveSalary(r, e.target.value)}/>
                    </td>
                    <td>
                      {r.kind === "staff" ? (
                        <div className="flex gap-1">
                          <button onClick={()=>openEditStaff(r)}
                                  className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                                  data-testid={`staff-edit-${key}`}>
                            <Pencil size={12}/> Edit
                          </button>
                          <button onClick={()=>deleteStaff(r)}
                                  className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                                  data-testid={`staff-delete-${key}`}>
                            <Trash2 size={12}/>
                          </button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-400">system account</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length===0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No staff match filter</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-white flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={()=>shiftMonth(-1)} className="nb-btn nb-btn-ghost px-2 py-1"
                    data-testid="staff-month-prev"><ChevronLeft size={16}/></button>
            <h3 className="display text-xl font-bold" data-testid="staff-month-label">
              Attendance Log — {monthLabel}
            </h3>
            <button onClick={()=>shiftMonth(1)} className="nb-btn nb-btn-ghost px-2 py-1"
                    data-testid="staff-month-next"><ChevronRight size={16}/></button>
            {days.filter(d=>staffStatusMap[d]?.status==="unrecorded").length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md bg-amber-500/20 text-amber-600 border border-amber-400/40"
                    data-testid="staff-att-unrecorded-pill">
                <AlertTriangle size={13}/> {days.filter(d=>staffStatusMap[d]?.status==="unrecorded").length} Unrecorded — attendance not saved yet
              </span>
            )}
          </div>
          <div className="text-xs flex gap-3 items-center">
            <span>Default = <b className="text-emerald-700">P</b> · Tap to cycle P → L → A</span>
            <span>AY <b>{ay || "—"}</b></span>
          </div>
        </div>
        <div className="overflow-auto max-h-[calc(100vh-260px)] relative">
          <table className="nb-table">
            <thead><tr><th className="sticky left-0 top-0 z-30 bg-white shadow-sm border-b">Name</th>{days.map(d=>{
              const isSun = new Date(d).getDay() === 0;
              const holiday = staffHolidayMap[d];
              const unrec = staffStatusMap[d]?.status === "unrecorded";
              const hdr = unrec ? "bg-amber-500/20 text-amber-600 border border-amber-400/40"
                : isSun ? "bg-slate-200 text-slate-600" : holiday ? "bg-[#FCA5A5]" : d===TODAY?"bg-[#FDE047]":"bg-white";
              return <th key={d} className={`text-xs mono sticky top-0 z-20 shadow-sm border-b ${hdr}`}
                title={unrec ? `Unrecorded — attendance not saved for ${d}` : holiday ? `Holiday: ${holiday}` : (isSun ? "Sunday" : d)}>
                {d.slice(8)}{isSun && <div className="text-[8px] font-bold">SUN</div>}
                {holiday && <div className="text-[8px] font-bold">HOL</div>}
                {unrec && !holiday && !isSun && <div className="text-[8px] font-bold">⚠</div>}
              </th>;
            })}<th className="sticky top-0 z-20 bg-white shadow-sm border-b">P</th><th className="sticky top-0 z-20 bg-white shadow-sm border-b">L</th><th className="sticky top-0 z-20 bg-white shadow-sm border-b">A</th></tr></thead>
            <tbody>
              {filtered.map(r=>{
                const key = `${r.kind}|${r.beneficiary_id}`;
                let p=0,l=0,a=0;
                return <tr key={key} data-testid={`staff-att-row-${key}`}>
                  <td className="font-bold text-sm sticky left-0 bg-white">{r.name}</td>
                  {days.map(d=>{
                    const st = cellStatus(r, d);
                    const editable = cellEditable(d);
                    const isSun = new Date(d).getDay() === 0;
                    const holiday = staffHolidayMap[d];
                    if (st==="present") p++;
                    else if (st==="leave") l++;
                    else if (st==="half_day") l++;  // counts under partial-leave column
                    else if (st==="absent") a++;
                    const ch = st==="present"?"P": st==="half_day"?"H"
                             : st==="leave"?"L": st==="absent"?"A":"·";
                    // Staff CAN be marked on Sundays/holidays per spec — highlight background only
                    const cl = st==="present"?"text-emerald-700 font-bold bg-emerald-50":
                               st==="half_day"?"text-orange-700 font-bold bg-orange-50":
                               st==="leave"?"text-amber-700 font-bold bg-amber-50":
                               st==="absent"?"text-rose-700 font-bold bg-rose-50":
                               isSun || holiday ? "bg-slate-50 text-slate-300":"text-slate-300";
                    return <td key={d}
                      onClick={()=> cellClick(r, d, st)}
                      className={`text-center mono text-xs ${cl} ${editable?"cursor-pointer hover:opacity-70":"cursor-not-allowed opacity-60"}`}
                      title={holiday ? `${holiday} — mark if working` : isSun ? "Sunday — mark if working" : editable ? `${d} · ${st||"blank"} → click to cycle` : d > TODAY ? "future date locked" : "past date locked (needs override)"}
                      data-testid={`staff-cell-${key}-${d}`}>{ch}</td>;
                  })}
                  <td className="mono font-bold">{p}</td><td className="mono">{l}</td><td className="mono">{a}</td></tr>;
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editStaff && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={()=>setEditStaff(null)} data-testid="staff-edit-modal">
          <div className="nb-card p-6 bg-white max-w-lg w-full space-y-3"
               onClick={e=>e.stopPropagation()}>
            <h3 className="display text-xl font-bold">Edit Staff · {editStaff.name}</h3>
            <div className="grid grid-cols-2 gap-2">
              <label><span className="nb-label">Name</span>
                <input className="nb-input" value={editStaff.name||""}
                       onChange={e=>setEditStaff({...editStaff, name:e.target.value})}
                       data-testid="staff-edit-name"/></label>
              <label><span className="nb-label">Email</span>
                <input className="nb-input" value={editStaff.email||""}
                       onChange={e=>setEditStaff({...editStaff, email:e.target.value})}/></label>
              <label><span className="nb-label">Mobile</span>
                <input className="nb-input" value={editStaff.mobile||""}
                       onChange={e=>setEditStaff({...editStaff, mobile:e.target.value})}/></label>
              <label><span className="nb-label">Designation</span>
                <select className="nb-select" value={editStaff.designation||""}
                        onChange={e=>setEditStaff({...editStaff, designation:e.target.value})}>
                  {roles.map(r=><option key={r} value={r}>{r}</option>)}
                </select></label>
              <label><span className="nb-label">Department</span>
                <select className="nb-select" value={editStaff.department||""}
                        onChange={e=>setEditStaff({...editStaff, department:e.target.value})}>
                  {DEPT_OPTS.map(d=><option key={d} value={d}>{d}</option>)}
                </select></label>
              <label><span className="nb-label">Basic Salary ₹</span>
                <input type="number" className="nb-input mono" value={editStaff.monthly_salary||0}
                       onChange={e=>setEditStaff({...editStaff, monthly_salary:e.target.value})}
                       data-testid="staff-edit-salary"/></label>
              <label><span className="nb-label">PF #</span>
                <input className="nb-input mono" value={editStaff.pf_number||""}
                       onChange={e=>setEditStaff({...editStaff, pf_number:e.target.value})}/></label>
              <label><span className="nb-label">Bank A/C</span>
                <input className="nb-input mono" value={editStaff.bank_account||""}
                       onChange={e=>setEditStaff({...editStaff, bank_account:e.target.value})}/></label>
              <label><span className="nb-label">Date of Joining</span>
                <input type="date" className="nb-input" value={editStaff.date_of_joining||""}
                       onChange={e=>setEditStaff({...editStaff, date_of_joining:e.target.value})}/></label>
              <label><span className="nb-label">Academic Year</span>
                <input className="nb-input mono" value={editStaff.academic_year||""}
                       onChange={e=>setEditStaff({...editStaff, academic_year:e.target.value})}/></label>
              <label className="col-span-2"><span className="nb-label">Salary Slab</span>
                <select className="nb-select" value={editStaff.salary_slab||""}
                        onChange={e=>setEditStaff({...editStaff, salary_slab:e.target.value})}
                        data-testid="staff-edit-salary-slab">
                  <option value="">— No slab (uses Basic Salary above) —</option>
                  {(staffSettings.salary_slabs||[]).map(sl=>(
                    <option key={sl.name} value={sl.name}>
                      {sl.name} · Basic ₹{sl.basic||0} · PF {sl.pf_pct||0}% · Gratuity {sl.gratuity_pct||0}%
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex gap-2 pt-2 border-t-2 border-black">
              <button onClick={()=>setEditStaff(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={saveEditStaff} className="nb-btn flex-1 justify-center"
                      data-testid="staff-edit-save">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Students() {
  const DISCOUNT_PRESETS = [
    {type:"None", pct:0}, {type:"Full Payment", pct:5}, {type:"1st Sibling", pct:10},
    {type:"2nd Sibling", pct:15}, {type:"Orphan", pct:100}, {type:"Staff Child", pct:50},
    {type:"Special", pct:25},
  ];
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState({grades:[], sections:[]});
  const empty = {name:"", email:"", roll_no:"", class_name:"", section:"",
                 parent_name:"", parent_mobile:"", parent_email:"", password:"stu123",
                 discount_type:"None", discount_percent:0};
  const [f, setF] = useState(empty);
  const [filterGrade, setFilterGrade] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const load = async () => {
    setRows((await api.get("/students")).data);
    setSettings((await api.get("/settings")).data);
  };
  useEffect(()=>{ load(); }, []);
  const filteredRows = useMemo(() => rows
    .filter(r => !filterGrade || r.class_name === filterGrade)
    .filter(r => !filterSection || r.section === filterSection),
    [rows, filterGrade, filterSection]);
  const create = async (e)=>{ e.preventDefault();
    try { await api.post("/students", f); toast.success("Student added & SMS sent"); setF(empty); load(); }
    catch(err){ toast.error(err?.response?.data?.detail||"Failed"); }
  };
  const remove = async (id)=>{
    if (!window.confirm("Delete this student?")) return;
    await api.delete(`/students/${id}`); load();
  };
  const updateDiscount = async (id, presetType) => {
    const preset = DISCOUNT_PRESETS.find(p=>p.type===presetType) || DISCOUNT_PRESETS[0];
    await api.patch(`/students/${id}/discount`, {discount_type: preset.type, discount_percent: preset.pct});
    toast.success(`${preset.type} (${preset.pct}%) applied`); load();
  };
  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/students/${editing.student_id}`, {
        name: editing.name, email: editing.email, roll_no: editing.roll_no,
        class_name: editing.class_name, section: editing.section,
        parent_name: editing.parent_name, parent_mobile: editing.parent_mobile,
        parent_email: editing.parent_email || "",
      });
      toast.success("Student updated"); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail||"Failed"); }
  };

  const downloadTemplate = () => {
    // Use a same-origin anchor that hits the backend directly.
    // Session cookie is set httpOnly during login so the request authenticates.
    // Content-Disposition: attachment on the server forces the browser to save.
    const url = `${API}/students/csv-template`;
    const a = document.createElement("a");
    a.href = url;
    a.download = "students_template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Downloading template…");
  };

  const onBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkBusy(true); setBulkResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/students/bulk", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(r.data);
      toast.success(`Bulk upload: ${r.data.created_count} created, ${r.data.error_count} errors`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulkBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      <div className="nb-card p-6 bg-[#93C5FD] flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <h3 className="display text-xl font-bold flex items-center gap-2"><FileSpreadsheet size={20}/>Bulk Add Students</h3>
          <p className="text-sm mt-1">Download the CSV template, fill it in, and upload to create multiple students at once. Parent welcome SMS is mocked & logged.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={downloadTemplate} data-testid="bulk-download-template" className="nb-btn nb-btn-tertiary">
            <Download size={16}/>Download Template
          </button>
          <input ref={fileRef} type="file" accept=".csv" onChange={onBulkUpload}
                 className="hidden" data-testid="bulk-upload-input" id="bulk-upload-input"/>
          <button onClick={()=>fileRef.current?.click()} disabled={bulkBusy}
                  data-testid="bulk-upload-btn" className="nb-btn">
            <Upload size={16}/>{bulkBusy ? "Uploading..." : "Upload CSV"}
          </button>
        </div>
      </div>

      {bulkResult && (
        <div className="nb-card p-5" data-testid="bulk-result">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="nb-badge bg-[#86EFAC]">Created: {bulkResult.created_count}</span>
            <span className="nb-badge bg-[#FCA5A5]">Errors: {bulkResult.error_count}</span>
          </div>
          {bulkResult.errors?.length > 0 && (
            <div className="mt-3">
              <div className="nb-label">Errors</div>
              <ul className="text-sm mt-1 space-y-1">
                {bulkResult.errors.map((e,i)=>(
                  <li key={`${e.row}-${i}`} className="font-mono text-xs text-rose-700">Row {e.row}: {e.error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={create} className="nb-card p-6 bg-[#86EFAC] space-y-3 h-fit">
          <h3 className="display text-xl font-bold">Add Single Student</h3>
          <input data-testid="stu-name" className="nb-input" placeholder="Name" required value={f.name} onChange={e=>setF({...f,name:e.target.value})}/>
          <input data-testid="stu-email" className="nb-input" placeholder="Email" type="email" required value={f.email} onChange={e=>setF({...f,email:e.target.value})}/>
          <div className="grid grid-cols-3 gap-2">
            <input data-testid="stu-roll" className="nb-input" placeholder="Roll" required value={f.roll_no} onChange={e=>setF({...f,roll_no:e.target.value})}/>
            <select data-testid="stu-class" required className="nb-select" value={f.class_name} onChange={e=>setF({...f,class_name:e.target.value})}>
              <option value="">Grade</option>
              {settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
            </select>
            <select data-testid="stu-section" required className="nb-select" value={f.section} onChange={e=>setF({...f,section:e.target.value})}>
              <option value="">Sec</option>
              {settings.sections?.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <input data-testid="stu-parent-name" className="nb-input" placeholder="Parent Name" required value={f.parent_name} onChange={e=>setF({...f,parent_name:e.target.value})}/>
          <input data-testid="stu-parent-mobile" className="nb-input" placeholder="Parent Mobile (+91...)" required value={f.parent_mobile} onChange={e=>setF({...f,parent_mobile:e.target.value})}/>
          <div>
            <label className="nb-label">Discount</label>
            <select data-testid="stu-discount" className="nb-select mt-1"
                    value={f.discount_type}
                    onChange={e=>{
                      const p = DISCOUNT_PRESETS.find(x=>x.type===e.target.value);
                      setF({...f, discount_type:p.type, discount_percent:p.pct});
                    }}>
              {DISCOUNT_PRESETS.map(p=><option key={p.type} value={p.type}>{p.type} ({p.pct}%)</option>)}
            </select>
          </div>
          <button data-testid="stu-create-btn" className="nb-btn w-full justify-center"><Plus size={16}/>Add</button>
        </form>
        <div className="nb-card p-0 overflow-hidden lg:col-span-2">
          <div className="p-3 flex flex-wrap gap-2 border-b-2 border-black bg-[#FBF9F6]">
            <div className="flex-1"><b>Directory</b> <span className="text-xs text-slate-600 mono">({filteredRows.length}/{rows.length})</span></div>
            <select value={filterGrade} onChange={e=>setFilterGrade(e.target.value)}
                    className="nb-select text-xs py-1" data-testid="stu-filter-grade">
              <option value="">All Grades</option>
              {(settings.grades||[]).map(g=><option key={g} value={g}>{g}</option>)}
            </select>
            <select value={filterSection} onChange={e=>setFilterSection(e.target.value)}
                    className="nb-select text-xs py-1" data-testid="stu-filter-section">
              <option value="">All Sections</option>
              {(settings.sections||[]).map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
          <table className="nb-table">
            <thead><tr><th>Roll</th><th>Name</th><th>Class</th><th>Parent / Mobile</th><th>Discount</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {filteredRows.map(r=>(
                <tr key={r.student_id} data-testid={`stu-row-${r.student_id}`}>
                  <td className="mono">{r.roll_no}</td><td className="font-bold">{r.name}</td>
                  <td>{r.class_name}-{r.section}</td>
                  <td className="text-sm">{r.parent_name}<br/><span className="mono text-xs">{r.parent_mobile}</span></td>
                  <td>
                    <select data-testid={`stu-disc-${r.student_id}`}
                            className="nb-select text-xs py-1"
                            value={r.discount_type||"None"}
                            onChange={e=>updateDiscount(r.student_id, e.target.value)}>
                      {DISCOUNT_PRESETS.map(p=><option key={p.type} value={p.type}>{p.type} ({p.pct}%)</option>)}
                    </select>
                  </td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button onClick={()=>setEditing({...r})}
                              className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                              data-testid={`stu-edit-${r.student_id}`}
                              title="Edit Student Details">
                        <Pencil size={12}/>
                      </button>
                      <button onClick={()=>remove(r.student_id)}
                              className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                              data-testid={`stu-delete-${r.student_id}`}>
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredRows.length===0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No students match the current filters</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={()=>setEditing(null)}>
          <div className="nb-card p-6 bg-white max-w-lg w-full space-y-3 max-h-[90vh] overflow-y-auto"
               onClick={e=>e.stopPropagation()} data-testid="stu-edit-modal">
            <h3 className="display text-xl font-bold">Edit Student Details</h3>
            <div className="grid grid-cols-2 gap-2">
              <label><div className="nb-label">Roll No</div>
                <input className="nb-input" value={editing.roll_no||""}
                       onChange={e=>setEditing({...editing, roll_no:e.target.value})}
                       data-testid="stu-edit-roll"/></label>
              <label><div className="nb-label">Name</div>
                <input className="nb-input" value={editing.name||""}
                       onChange={e=>setEditing({...editing, name:e.target.value})}
                       data-testid="stu-edit-name"/></label>
            </div>
            <label className="block"><div className="nb-label">Email</div>
              <input className="nb-input" type="email" value={editing.email||""}
                     onChange={e=>setEditing({...editing, email:e.target.value})}
                     data-testid="stu-edit-email"/></label>
            <div className="grid grid-cols-2 gap-2">
              <label><div className="nb-label">Grade</div>
                <select className="nb-select" value={editing.class_name||""}
                        onChange={e=>setEditing({...editing, class_name:e.target.value})}>
                  <option value="">—</option>
                  {(settings.grades||[]).map(g=><option key={g} value={g}>{g}</option>)}
                </select></label>
              <label><div className="nb-label">Section</div>
                <select className="nb-select" value={editing.section||""}
                        onChange={e=>setEditing({...editing, section:e.target.value})}>
                  <option value="">—</option>
                  {(settings.sections||[]).map(s=><option key={s} value={s}>{s}</option>)}
                </select></label>
            </div>
            <label className="block"><div className="nb-label">Parent Name</div>
              <input className="nb-input" value={editing.parent_name||""}
                     onChange={e=>setEditing({...editing, parent_name:e.target.value})}/></label>
            <div className="grid grid-cols-2 gap-2">
              <label><div className="nb-label">Parent Mobile</div>
                <input className="nb-input" value={editing.parent_mobile||""}
                       onChange={e=>setEditing({...editing, parent_mobile:e.target.value})}/></label>
              <label><div className="nb-label">Parent Email</div>
                <input className="nb-input" type="email" value={editing.parent_email||""}
                       onChange={e=>setEditing({...editing, parent_email:e.target.value})}/></label>
            </div>
            <div className="flex gap-2">
              <button onClick={()=>setEditing(null)}
                      className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={saveEdit}
                      className="nb-btn flex-1 justify-center"
                      data-testid="stu-edit-save">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* --- Bulk templates: student fee profile + historical receipts CSV upload --- */
function BulkTemplates({ isHistoricalActive, onDone }) {
  const fpRef = useRef(null);
  const hrRef = useRef(null);
  const [fpResult, setFpResult] = useState(null);
  const [hrResult, setHrResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const upload = async (url, ref, setter) => {
    const f = ref.current?.files?.[0];
    if (!f) return toast.error("Choose a CSV file first");
    setBusy(true);
    const form = new FormData(); form.append("file", f);
    try {
      const r = await api.post(url, form, { headers: {"Content-Type": "multipart/form-data"}});
      setter(r.data);
      toast.success(`Processed. ${r.data.updated ?? r.data.created ?? 0} rows applied`);
      onDone && onDone();
      if (ref.current) ref.current.value = "";
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="fees-bulk-templates">
      {/* Student fee profile */}
      <div className="nb-card p-5 bg-[#93C5FD] space-y-3">
        <h3 className="display text-lg font-bold flex items-center gap-2">
          <FileSpreadsheet size={18}/>Bulk — Student Fee Profiles
        </h3>
        <p className="text-xs">Download the CSV pre-filled with every current student. Edit the per-student columns
          (historical dues · admission / tuition / transport / hostel / other fees · discount % · pay cycle), save
          as CSV and upload — matched by <b>roll_no</b>.</p>
        <div className="flex flex-wrap gap-2">
          <a href={`${API_URL}/api/students/fee-profile-template`} target="_blank" rel="noreferrer"
             className="nb-btn nb-btn-secondary" data-testid="fp-template-dl">
            <Download size={14}/> Download Template
          </a>
          <input ref={fpRef} type="file" accept=".csv" className="nb-input flex-1 min-w-[180px]"
                 data-testid="fp-file"/>
          <button onClick={()=>upload("/students/fee-profile-upload", fpRef, setFpResult)}
                  disabled={busy} className="nb-btn" data-testid="fp-upload">
            <Upload size={14}/> Upload
          </button>
        </div>
        {fpResult && (
          <div className="text-xs bg-white p-2 border-2 border-black" data-testid="fp-result">
            Updated: <b>{fpResult.updated}</b>
            {fpResult.errors?.length > 0 && (
              <details className="mt-1"><summary className="text-rose-700 cursor-pointer">
                {fpResult.errors.length} error(s)</summary>
                <ul className="ml-4 mt-1">
                  {fpResult.errors.slice(0,10).map((e,i)=>(
                    <li key={i}>Row {e.row} ({e.roll_no}): {e.error}</li>
                  ))}
                </ul></details>
            )}
          </div>
        )}
      </div>

      {/* Historical receipts */}
      <div className="nb-card p-5 bg-[#FCA5A5] space-y-3">
        <h3 className="display text-lg font-bold flex items-center gap-2">
          <Clock size={18}/>Bulk — Historical Receipts
        </h3>
        <p className="text-xs">Insert prior offline fee receipts in bulk. Requires an <b>active Global-Admin
          override window</b>. Columns: roll_no, paid_on, method, admission, tuition, transport, hostel, others, note.</p>
        <div className="flex flex-wrap gap-2">
          <a href={`${API_URL}/api/receipts/historical-template`} target="_blank" rel="noreferrer"
             className="nb-btn nb-btn-secondary" data-testid="hr-template-dl">
            <Download size={14}/> Download Template
          </a>
          <input ref={hrRef} type="file" accept=".csv" className="nb-input flex-1 min-w-[180px]"
                 disabled={!isHistoricalActive}
                 data-testid="hr-file"/>
          <button onClick={()=>upload("/receipts/historical-upload", hrRef, setHrResult)}
                  disabled={busy || !isHistoricalActive}
                  className={`nb-btn ${!isHistoricalActive ? "opacity-50 cursor-not-allowed" : ""}`}
                  title={!isHistoricalActive ? "Ask Global Admin to enable Historical Receipt Insertion first" : ""}
                  data-testid="hr-upload">
            <Upload size={14}/> Upload
          </button>
        </div>
        {!isHistoricalActive && (
          <div className="text-xs italic">Locked — Global Admin has not authorised historical inserts.</div>
        )}
        {hrResult && (
          <div className="text-xs bg-white p-2 border-2 border-black" data-testid="hr-result">
            Created: <b>{hrResult.created}</b>
            {hrResult.receipt_nos?.length > 0 && (
              <div className="mono text-[10px] mt-1">{hrResult.receipt_nos.slice(0,5).join(", ")}
                {hrResult.receipt_nos.length > 5 ? "…" : ""}</div>
            )}
            {hrResult.errors?.length > 0 && (
              <details className="mt-1"><summary className="text-rose-700 cursor-pointer">
                {hrResult.errors.length} error(s)</summary>
                <ul className="ml-4 mt-1">
                  {hrResult.errors.slice(0,10).map((e,i)=>(
                    <li key={i}>Row {e.row} ({e.roll_no}): {e.error}</li>
                  ))}
                </ul></details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DiscountStructuresCard() {
  const CATS = ["Tuition Fee","Admission Fee","Transport Fee","Hostel Fee","Other Fees","All"];
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({name:"", fee_category:"Tuition Fee", kind:"percent", value:5, slab:""});
  const [editing, setEditing] = useState(null);
  const load = async () => {
    try { setRows((await api.get("/fee-discount-structures")).data); }
    catch { setRows([]); }
  };
  useEffect(()=>{ load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fee-discount-structures", f);
      toast.success("Discount created");
      setF({name:"", fee_category:"Tuition Fee", kind:"percent", value:5, slab:""}); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/fee-discount-structures/${editing.structure_id}`, {
        name: editing.name, fee_category: editing.fee_category,
        kind: editing.kind, value: parseFloat(editing.value)||0,
        slab: editing.slab || "",
      });
      toast.success("Updated"); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this discount structure? Historical receipts are unaffected.")) return;
    try { await api.delete(`/fee-discount-structures/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="nb-card p-6 bg-[#86EFAC] space-y-3" data-testid="discount-structures">
      <h3 className="display text-xl font-bold">Discount Structures</h3>
      <p className="text-xs">Reusable discount rules the Accountant can pick while generating a receipt. Editing here has NO effect on already-generated receipts.</p>
      <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2 items-end">
        <label><div className="nb-label">Name</div>
          <input className="nb-input" required value={f.name}
                 onChange={e=>setF({...f, name:e.target.value})}
                 placeholder="e.g. Merit-100"
                 data-testid="disc-new-name"/></label>
        <label><div className="nb-label">Fee Category</div>
          <select className="nb-select" value={f.fee_category}
                  onChange={e=>setF({...f, fee_category:e.target.value})}
                  data-testid="disc-new-cat">
            {CATS.map(c=><option key={c} value={c}>{c}</option>)}
          </select></label>
        <label><div className="nb-label">Type</div>
          <select className="nb-select" value={f.kind}
                  onChange={e=>setF({...f, kind:e.target.value})}
                  data-testid="disc-new-kind">
            <option value="percent">Percent (%)</option>
            <option value="amount">Flat ₹</option>
          </select></label>
        <label><div className="nb-label">Value</div>
          <input type="number" min="0" step="0.01" required className="nb-input mono"
                 value={f.value} onChange={e=>setF({...f, value:e.target.value})}
                 data-testid="disc-new-value"/></label>
        <label><div className="nb-label">Slab (optional)</div>
          <input className="nb-input" value={f.slab}
                 placeholder="e.g. 1st Sibling"
                 onChange={e=>setF({...f, slab:e.target.value})}
                 data-testid="disc-new-slab"/></label>
        <button className="nb-btn sm:col-span-2 lg:col-span-5 justify-center"
                data-testid="disc-new-submit">
          <Plus size={14}/> Add Discount Structure
        </button>
      </form>
      <div className="nb-card p-0 overflow-hidden bg-white">
        <table className="nb-table">
          <thead><tr><th>Name</th><th>Category</th><th>Kind</th><th>Value</th><th>Slab</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.structure_id} data-testid={`disc-row-${r.structure_id}`}>
                <td className="font-bold">{r.name}</td>
                <td className="text-sm">{r.fee_category}</td>
                <td className="text-xs mono">{r.kind}</td>
                <td className="mono">{r.kind==="percent" ? `${r.value}%` : `₹${r.value}`}</td>
                <td className="text-xs">{r.slab||""}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <button onClick={()=>setEditing({...r})}
                            className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                            data-testid={`disc-edit-${r.structure_id}`}>
                      <Pencil size={12}/>
                    </button>
                    <button onClick={()=>del(r.structure_id)}
                            className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                            data-testid={`disc-delete-${r.structure_id}`}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={6} className="text-center py-4 text-slate-500">No discount structures yet</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={()=>setEditing(null)}>
          <div className="nb-card p-6 bg-white max-w-md w-full space-y-3"
               onClick={e=>e.stopPropagation()} data-testid="disc-edit-modal">
            <h3 className="display text-xl font-bold">Edit Discount Structure</h3>
            <label className="block"><div className="nb-label">Name</div>
              <input className="nb-input" value={editing.name||""}
                     onChange={e=>setEditing({...editing, name:e.target.value})}
                     data-testid="disc-edit-name"/></label>
            <label className="block"><div className="nb-label">Fee Category</div>
              <select className="nb-select" value={editing.fee_category}
                      onChange={e=>setEditing({...editing, fee_category:e.target.value})}>
                {CATS.map(c=><option key={c} value={c}>{c}</option>)}
              </select></label>
            <div className="grid grid-cols-2 gap-2">
              <label><div className="nb-label">Type</div>
                <select className="nb-select" value={editing.kind}
                        onChange={e=>setEditing({...editing, kind:e.target.value})}>
                  <option value="percent">Percent (%)</option>
                  <option value="amount">Flat ₹</option>
                </select></label>
              <label><div className="nb-label">Value</div>
                <input type="number" min="0" step="0.01" className="nb-input mono"
                       value={editing.value}
                       onChange={e=>setEditing({...editing, value:e.target.value})}
                       data-testid="disc-edit-value"/></label>
            </div>
            <label className="block"><div className="nb-label">Slab (optional)</div>
              <input className="nb-input" value={editing.slab||""}
                     onChange={e=>setEditing({...editing, slab:e.target.value})}/></label>
            <div className="flex gap-2">
              <button onClick={()=>setEditing(null)}
                      className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={saveEdit} className="nb-btn flex-1 justify-center"
                      data-testid="disc-edit-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeesPrintSummary({settings}) {
  const [divisions, setDivisions] = useState([]);
  const [f, setF] = useState({division_id:"", fee_type:"", class_name:"", section:"", month:"", sort_by:"date"});
  useEffect(()=>{
    api.get("/finance-settings/divisions").then(r=>setDivisions(r.data)).catch(()=>{});
  }, []);
  const print = async () => {
    try {
      const res = await api.post("/fees/print-summary",
        {division_id: f.division_id || null, fee_type: f.fee_type || null,
         class_name: f.class_name || null, section: f.section || null,
         month: f.month || null, sort_by: f.sort_by || "date"},
        {responseType: "blob"});
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `fees_summary_${f.month||f.class_name||'all'}${f.section||''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return <div className="nb-card p-3 bg-white flex flex-wrap items-center gap-2"
              data-testid="fees-print-bar">
    <span className="nb-label">Filters:</span>
    <input type="month" className="nb-input text-xs" value={f.month}
           onChange={e=>setF({...f, month:e.target.value})}
           data-testid="fees-print-month"/>
    <select className="nb-select text-xs" value={f.division_id}
            onChange={e=>setF({...f, division_id:e.target.value})}
            data-testid="fees-print-division">
      <option value="">All divisions</option>
      {divisions.map(d => <option key={d.division_id} value={d.division_id}>{d.name}</option>)}
    </select>
    <select className="nb-select text-xs" value={f.fee_type}
            onChange={e=>setF({...f, fee_type:e.target.value})}
            data-testid="fees-print-fee-type">
      <option value="">All fee types</option>
      {(settings.fee_types||[]).map(ft => <option key={ft} value={ft}>{ft}</option>)}
    </select>
    <select className="nb-select text-xs" value={f.class_name}
            onChange={e=>setF({...f, class_name:e.target.value})}
            data-testid="fees-print-grade">
      <option value="">All grades</option>
      {(settings.grades||[]).map(g => <option key={g} value={g}>{g}</option>)}
    </select>
    <select className="nb-select text-xs" value={f.section}
            onChange={e=>setF({...f, section:e.target.value})}
            data-testid="fees-print-section">
      <option value="">All sections</option>
      {(settings.sections||[]).map(s => <option key={s} value={s}>{s}</option>)}
    </select>
    {/* Iter49 — Sort Order picker */}
    <span className="nb-label ml-2">Sort by:</span>
    <select className="nb-select text-xs" value={f.sort_by}
            onChange={e=>setF({...f, sort_by:e.target.value})}
            data-testid="fees-print-sort">
      <option value="date">Date</option>
      <option value="receipt_no">Receipt No</option>
      <option value="student_name">Student Name</option>
      <option value="grade">Grade</option>
    </select>
    <button className="nb-btn ml-auto" onClick={print}
            data-testid="fees-print-summary">
      <Download size={14}/>Print Summary
    </button>
  </div>;
}

function Fees() {
  const [structures, setStructures] = useState([]);
  const [students, setStudents] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [settings, setSettings] = useState({fee_types:[], billing_cycles:[], grades:[]});
  const [sForm, setS] = useState({class_name:"", term:"", amount:5000, due_date:"", fee_type:"Tuition Fee", billing_cycle:"Annual"});
  const [editFs, setEditFs] = useState(null);
  const [overrides, setOverrides] = useState({historical_receipts:{active:false, expires_at:null}});
  const [showHist, setShowHist] = useState(false);
  const [hist, setHist] = useState({roll:"", preview:null,
    alloc:{admission:0, tuition:0, transport:0, hostel:0, others:0},
    paid_on:"", method:"cash", note:""});
  const load = async () => {
    setStructures((await api.get("/fees/structures")).data);
    setStudents((await api.get("/students")).data);
    setReceipts((await api.get("/receipts")).data);
    setSettings((await api.get("/settings")).data);
    setOverrides((await api.get("/overrides/my")).data);
  };
  useEffect(()=>{ load(); }, []);
  const createStructure = async (e)=>{ e.preventDefault();
    await api.post("/fees/structures", {...sForm, amount: parseFloat(sForm.amount)});
    toast.success("Structure added"); load();
  };
  const delStructure = async (sid) => {
    if (!window.confirm("Delete this fee structure? Existing invoices will be preserved.")) return;
    await api.delete(`/fees/structures/${sid}`); load();
  };
  const saveEditFs = async () => {
    if (!editFs) return;
    const patch = {
      class_name: editFs.class_name, term: editFs.term,
      amount: parseFloat(editFs.amount||0), due_date: editFs.due_date,
      fee_type: editFs.fee_type, billing_cycle: editFs.billing_cycle,
    };
    try {
      await api.patch(`/fees/structures/${editFs.structure_id}`, patch);
      toast.success("Fee structure updated"); setEditFs(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const openHistorical = () => {
    if (!overrides.historical_receipts?.active) return;
    setShowHist(true);
  };
  const deleteReceipt = async (r) => {
    if (!overrides.historical_receipts?.active) {
      return toast.error("Deletion requires an active Historical Receipts override window");
    }
    if (!window.confirm(`Delete receipt ${r.receipt_no}? This is auditable and irreversible.`)) return;
    try {
      await api.delete(`/receipts/${r.receipt_id}`);
      toast.success(`Deleted ${r.receipt_no}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const [editingReceipt, setEditingReceipt] = useState(null);
  const saveReceiptEdit = async () => {
    if (!editingReceipt) return;
    if (!(editingReceipt.revision_note || "").trim()) {
      return toast.error("Revision note is mandatory for historical edits");
    }
    const payload = {
      payment_date: editingReceipt.paid_at ? editingReceipt.paid_at.slice(0,10) : null,
      payment_mode: editingReceipt.method,
      payment_ref: editingReceipt.payment_ref || "",
      amount: parseFloat(editingReceipt.amount) || 0,
      particulars: editingReceipt._particulars,
      revision_note: editingReceipt.revision_note.trim(),
    };
    try {
      await api.patch(`/receipts/${editingReceipt.receipt_id}`, payload);
      toast.success(`Receipt ${editingReceipt.receipt_no}R revised — audit logged`);
      setEditingReceipt(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const histLookup = async () => {
    try {
      const r = await api.get("/receipts/preview/by-roll", { params: { roll_no: hist.roll.trim() }});
      setHist({...hist, preview: r.data});
    } catch (err) { toast.error(err?.response?.data?.detail || "Not found"); }
  };
  const histCommit = async () => {
    if (!hist.preview) return toast.error("Look up a student first");
    if (!hist.paid_on) return toast.error("Enter the historical paid-on date");
    const totalAlloc = Object.values(hist.alloc).reduce((a,v)=>a+(parseFloat(v)||0), 0);
    if (totalAlloc <= 0) return toast.error("Allocate at least ₹1");
    try {
      const r = await api.post("/receipts", {
        student_id: hist.preview.student.student_id,
        allocation: {
          admission: parseFloat(hist.alloc.admission||0),
          tuition: parseFloat(hist.alloc.tuition||0),
          transport: parseFloat(hist.alloc.transport||0),
          hostel: parseFloat(hist.alloc.hostel||0),
          others: parseFloat(hist.alloc.others||0),
        },
        method: hist.method, note: hist.note || "Historical back-log",
        is_historical: true, paid_on_override: hist.paid_on,
      });
      toast.success(`Historical receipt ${r.data.receipt_no} inserted`);
      setShowHist(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const downloadPdf = async (rid, rno) => {
    const r = await api.get(`/fees/receipt/${rid}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `${rno || rid}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const isHistoricalActive = overrides.historical_receipts?.active;
  return (
    <div className="space-y-6">
      <FeesPrintSummary settings={settings}/>
      <div className="nb-card p-5 bg-[#93C5FD]">
        <div className="nb-label">Fee Configuration</div>
        <div className="font-bold">Define grade-wise Fee Structures per Fee Type (Admission, Tuition, Transport, Hostel, Others). These become the auto-populated defaults when students are enrolled and are pulled into the Accountant&apos;s live receipt preview.</div>
      </div>
      <DiscountStructuresCard/>
      <form onSubmit={createStructure} className="nb-card p-6 bg-[#C4B5FD] grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <h3 className="display text-xl font-bold sm:col-span-2 lg:col-span-3">Add Fee Structure</h3>
        <div><label className="nb-label">Grade</label>
          <select data-testid="fs-class" required className="nb-select mt-1" value={sForm.class_name} onChange={e=>setS({...sForm,class_name:e.target.value})}>
            <option value="">--</option>{settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
          </select></div>
        <div><label className="nb-label">Fee Type</label>
          <select data-testid="fs-feetype" required className="nb-select mt-1" value={sForm.fee_type} onChange={e=>setS({...sForm,fee_type:e.target.value})}>
            {settings.fee_types?.map(ft=><option key={ft} value={ft}>{ft}</option>)}
          </select></div>
        <div><label className="nb-label">Billing Cycle</label>
          <select data-testid="fs-cycle" required className="nb-select mt-1" value={sForm.billing_cycle} onChange={e=>setS({...sForm,billing_cycle:e.target.value})}>
            {settings.billing_cycles?.map(bc=><option key={bc} value={bc}>{bc}</option>)}
          </select></div>
        <div><label className="nb-label">Term Label</label>
          <input data-testid="fs-term" className="nb-input mt-1" placeholder="e.g. Term 1 2026" required value={sForm.term} onChange={e=>setS({...sForm,term:e.target.value})}/></div>
        <div><label className="nb-label">Amount ₹</label>
          <input data-testid="fs-amount" className="nb-input mt-1" type="number" required value={sForm.amount} onChange={e=>setS({...sForm,amount:e.target.value})}/></div>
        <div><label className="nb-label">Due Date</label>
          <input data-testid="fs-due" className="nb-input mt-1" type="date" required value={sForm.due_date} onChange={e=>setS({...sForm,due_date:e.target.value})}/></div>
        <button data-testid="fs-create-btn" className="nb-btn sm:col-span-2 lg:col-span-3 justify-center"><Plus size={16}/>Add Structure</button>
      </form>

      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-white"><h3 className="display text-xl font-bold">Defined Structures</h3></div>
        <table className="nb-table">
          <thead><tr><th>Grade</th><th>Fee Type</th><th>Cycle</th><th>Term</th><th>Amount</th><th>Due</th><th></th></tr></thead>
          <tbody>
            {structures.map(s=>(<tr key={s.structure_id} data-testid={`fs-row-${s.structure_id}`}>
              <td className="font-bold">{s.class_name}</td>
              <td><span className="nb-badge bg-[#C4B5FD]">{s.fee_type||"Tuition Fee"}</span></td>
              <td>{s.billing_cycle||"Annual"}</td>
              <td>{s.term}</td><td className="mono">₹{s.amount}</td><td className="mono text-sm">{s.due_date}</td>
              <td>
                <div className="flex gap-1">
                  <button onClick={()=>setEditFs({...s})} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                          data-testid={`fs-edit-${s.structure_id}`}>
                    <Pencil size={12}/> Edit
                  </button>
                  <button onClick={()=>delStructure(s.structure_id)} className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                          data-testid={`fs-delete-${s.structure_id}`}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              </td>
            </tr>))}
            {structures.length===0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">None yet</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Bulk templates: student fee-profile + historical receipts */}
      <BulkTemplates isHistoricalActive={isHistoricalActive} onDone={load}/>

      <div className="nb-card p-0 overflow-hidden" data-testid="fees-receipts-repo">
        <div className="px-6 py-3 border-b-2 border-black bg-white flex flex-wrap justify-between items-center gap-2">
          <h3 className="display text-xl font-bold">Fee Receipts Repository ({receipts.length})</h3>
          <div className="flex items-center gap-2">
            {isHistoricalActive && (
              <span className="nb-badge bg-[#86EFAC] text-xs">
                Override ACTIVE · until {(overrides.historical_receipts.expires_at||"").slice(0,16).replace("T"," ")}
              </span>
            )}
            <button onClick={openHistorical} disabled={!isHistoricalActive}
                    className={`nb-btn ${isHistoricalActive ? "" : "opacity-50 cursor-not-allowed"}`}
                    data-testid="fees-request-historical"
                    title={isHistoricalActive ? "Insert a prior offline receipt" : "Contact Global Admin to unlock this permission"}>
              <Clock size={14}/> Request Historical Receipt Insertion
            </button>
          </div>
        </div>
        <table className="nb-table">
          <thead><tr><th>Receipt No</th><th>Date</th><th>Student</th><th>Division</th><th>Method</th><th>Amount</th><th></th><th></th></tr></thead>
          <tbody>
            {receipts.map(r=>(
              <tr key={r.receipt_id} data-testid={`rcpt-repo-${r.receipt_id}`}>
                <td className="mono font-bold">
                  {r.receipt_no || "—"}
                  {r.revised && <span className="nb-badge bg-[#FDE047] text-[10px] ml-1"
                                     title="Revised historically"
                                     data-testid={`rcpt-revised-${r.receipt_id}`}>R</span>}
                </td>
                <td className="mono text-xs">{(r.paid_at||"").slice(0,10)}</td>
                <td className="font-bold">{r.student_name || r.student_id} <span className="text-xs">({r.roll_no})</span></td>
                <td className="text-xs">{r.division_name || "—"}</td>
                <td>{(r.method||"").toUpperCase()}</td>
                <td className="mono font-bold">₹{(r.amount||0).toLocaleString()}</td>
                <td>{r.is_historical && <span className="nb-badge bg-[#FDE047] text-xs">HIST</span>}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <button onClick={()=>downloadPdf(r.receipt_id, r.receipt_no)}
                            className="nb-btn nb-btn-secondary px-2 py-1 text-xs">
                      <Download size={12}/> PDF
                    </button>
                    {isHistoricalActive && (
                      <>
                        <button onClick={()=>setEditingReceipt({
                                  ...r,
                                  revision_note: "",
                                  _particulars: (r.particulars||[]).map(p=>({...p, amount: p.amount||0})),
                                })}
                                className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                                data-testid={`rcpt-edit-${r.receipt_id}`}
                                title="Edit historical receipt (audit-tracked)">
                          <Pencil size={12}/>
                        </button>
                        <button onClick={()=>deleteReceipt(r)}
                                className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                                data-testid={`rcpt-delete-${r.receipt_id}`}
                                title="Only available while Historical Receipts override is active">
                          <Trash2 size={12}/>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {receipts.length===0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No receipts yet</td></tr>}
          </tbody>
        </table>
      </div>

      {showHist && hist && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={()=>setShowHist(false)} data-testid="hist-modal">
          <div className="nb-card p-6 bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto space-y-3"
               onClick={e=>e.stopPropagation()}>
            <h3 className="display text-xl font-bold flex items-center gap-2">
              <Clock size={18}/> Insert Historical Receipt (offline back-log)
            </h3>
            <p className="text-xs">Use this only for prior offline receipts. Global Admin has authorised this override until {(overrides.historical_receipts.expires_at||"").slice(0,16).replace("T"," ")}.</p>
            <div className="flex gap-2">
              <input className="nb-input flex-1" placeholder="Student Roll No"
                     value={hist.roll} onChange={e=>setHist({...hist, roll:e.target.value})}
                     data-testid="hist-roll"/>
              <button onClick={histLookup} className="nb-btn" data-testid="hist-lookup"><Search size={14}/>Lookup</button>
            </div>
            {hist.preview && (
              <div className="text-sm mono bg-[#FDE047] p-3 border-2 border-black">
                <b>{hist.preview.student.name}</b> · {hist.preview.student.class_name}-{hist.preview.student.section} · Roll {hist.preview.student.roll_no}
                <div className="text-xs mt-1">Gross ₹{hist.preview.gross_total} · Discount ₹{hist.preview.discount_amount} · Net Payable ₹{hist.preview.net_payable}</div>
              </div>
            )}
            <label><span className="nb-label">Historical Paid-on Date</span>
              <input type="date" className="nb-input mt-1" value={hist.paid_on}
                     onChange={e=>setHist({...hist, paid_on:e.target.value})}
                     data-testid="hist-date"/></label>
            <div className="grid grid-cols-5 gap-2">
              {[["admission","Admission"],["tuition","Tuition"],["transport","Transport"],["hostel","Hostel"],["others","Others"]].map(([k,lbl])=>(
                <label key={k}><span className="nb-label text-[10px]">{lbl}</span>
                  <input type="number" min="0" step="0.01" className="nb-input mono mt-1"
                         value={hist.alloc[k]}
                         onChange={e=>setHist({...hist, alloc:{...hist.alloc, [k]: e.target.value}})}
                         data-testid={`hist-alloc-${k}`}/></label>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label><span className="nb-label">Method</span>
                <select className="nb-select mt-1" value={hist.method}
                        onChange={e=>setHist({...hist, method:e.target.value})}>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="upi">UPI</option>
                  <option value="bank">Bank</option>
                </select></label>
              <label><span className="nb-label">Note</span>
                <input className="nb-input mt-1" value={hist.note}
                       onChange={e=>setHist({...hist, note:e.target.value})}
                       data-testid="hist-note"/></label>
            </div>
            <div className="flex gap-2 pt-2 border-t-2 border-black">
              <button onClick={()=>setShowHist(false)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={histCommit} className="nb-btn flex-1 justify-center"
                      data-testid="hist-commit"><Receipt size={14}/>Commit Historical Receipt</button>
            </div>
          </div>
        </div>
      )}

      {editFs && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={()=>setEditFs(null)} data-testid="fs-edit-modal">
          <div className="nb-card p-6 bg-white max-w-md w-full space-y-3"
               onClick={e=>e.stopPropagation()}>
            <h3 className="display text-xl font-bold">Edit Fee Structure</h3>
            <div className="grid grid-cols-2 gap-2">
              <label><span className="nb-label">Grade</span>
                <select className="nb-select" value={editFs.class_name||""}
                        onChange={e=>setEditFs({...editFs, class_name:e.target.value})}
                        data-testid="fs-edit-class">
                  <option value="">--</option>
                  {settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
                </select></label>
              <label><span className="nb-label">Fee Type</span>
                <select className="nb-select" value={editFs.fee_type||""}
                        onChange={e=>setEditFs({...editFs, fee_type:e.target.value})}
                        data-testid="fs-edit-feetype">
                  {settings.fee_types?.map(ft=><option key={ft} value={ft}>{ft}</option>)}
                </select></label>
            </div>
            <label className="block"><span className="nb-label">Billing Cycle</span>
              <select className="nb-select" value={editFs.billing_cycle||""}
                      onChange={e=>setEditFs({...editFs, billing_cycle:e.target.value})}
                      data-testid="fs-edit-cycle">
                {settings.billing_cycles?.map(bc=><option key={bc} value={bc}>{bc}</option>)}
              </select></label>
            <label className="block"><span className="nb-label">Term Label</span>
              <input className="nb-input" value={editFs.term||""}
                     onChange={e=>setEditFs({...editFs, term:e.target.value})}
                     data-testid="fs-edit-term"/></label>
            <div className="grid grid-cols-2 gap-2">
              <label><span className="nb-label">Amount ₹</span>
                <input type="number" className="nb-input mono" value={editFs.amount||0}
                       onChange={e=>setEditFs({...editFs, amount:e.target.value})}
                       data-testid="fs-edit-amount"/></label>
              <label><span className="nb-label">Due Date</span>
                <input type="date" className="nb-input" value={editFs.due_date||""}
                       onChange={e=>setEditFs({...editFs, due_date:e.target.value})}
                       data-testid="fs-edit-due"/></label>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={()=>setEditFs(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={saveEditFs} className="nb-btn flex-1 justify-center"
                      data-testid="fs-edit-save">Save Changes</button>
            </div>
          </div>
        </div>
      )}
      {editingReceipt && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={()=>setEditingReceipt(null)} data-testid="rcpt-edit-modal">
          <div className="nb-card p-6 bg-white max-w-lg w-full space-y-3 max-h-[92vh] overflow-y-auto"
               onClick={e=>e.stopPropagation()}>
            <h3 className="display text-xl font-bold">
              Edit Receipt {editingReceipt.receipt_no}
              <span className="nb-badge bg-[#FDE047] text-[10px] ml-1">R</span>
            </h3>
            <p className="text-xs">All revisions are audit-logged with a mandatory note. The receipt number will be shown with an <b>R</b> suffix in the ledger.</p>
            <div className="grid grid-cols-2 gap-2">
              <label><div className="nb-label">Payment Date</div>
                <input type="date" className="nb-input"
                       value={(editingReceipt.paid_at||"").slice(0,10)}
                       onChange={e=>setEditingReceipt({...editingReceipt, paid_at:e.target.value})}
                       data-testid="rcpt-edit-date"/></label>
              <label><div className="nb-label">Method</div>
                <select className="nb-select" value={editingReceipt.method||"cash"}
                        onChange={e=>setEditingReceipt({...editingReceipt, method:e.target.value})}
                        data-testid="rcpt-edit-method">
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank">Bank</option>
                  <option value="cheque">Cheque</option>
                </select></label>
            </div>
            <label className="block"><div className="nb-label">Reference #</div>
              <input className="nb-input" value={editingReceipt.payment_ref||""}
                     onChange={e=>setEditingReceipt({...editingReceipt, payment_ref:e.target.value})}/></label>
            <div className="border-2 border-black rounded p-2">
              <div className="nb-label mb-1">Particulars</div>
              {(editingReceipt._particulars||[]).map((p,i)=>(
                <div key={i} className="flex gap-2 items-center mb-1">
                  <span className="flex-1 text-xs">{p.label}</span>
                  <input type="number" min="0" step="0.01" className="nb-input mono text-xs w-28"
                         value={p.amount||0}
                         onChange={e=>{
                           const arr=[...editingReceipt._particulars];
                           arr[i]={...arr[i], amount: parseFloat(e.target.value)||0};
                           const total=arr.reduce((a,b)=>a+(b.amount||0),0);
                           setEditingReceipt({...editingReceipt, _particulars:arr, amount: total});
                         }}
                         data-testid={`rcpt-edit-p-${p.key||i}`}/>
                </div>
              ))}
              <div className="mono text-xs mt-1 font-bold">Total: ₹{(editingReceipt.amount||0).toLocaleString()}</div>
            </div>
            <label className="block"><div className="nb-label text-red-700">Note of Revision *</div>
              <textarea className="nb-textarea"
                        placeholder="Why is this receipt being revised? (mandatory audit trail)"
                        value={editingReceipt.revision_note||""}
                        onChange={e=>setEditingReceipt({...editingReceipt, revision_note:e.target.value})}
                        data-testid="rcpt-edit-note"/></label>
            <div className="flex gap-2">
              <button onClick={()=>setEditingReceipt(null)}
                      className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={saveReceiptEdit}
                      className="nb-btn flex-1 justify-center"
                      data-testid="rcpt-edit-save">Save Revision</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WabaStatusBadge({ row }) {
  const ds = row.delivery_status || (row.status || "").toLowerCase();
  const map = { delivered: ["bg-[#86EFAC]", "Delivered"], sent: ["bg-[#93C5FD]", "Sent"],
    simulated: ["bg-[#93C5FD]", "Sent"], failed: ["bg-[#FCA5A5]", "Failed"],
    failed_waba: ["bg-[#FCA5A5]", "Failed"], queued: ["bg-[#FDE047]", "Queued"] };
  const [cls, label] = map[ds] || ["bg-slate-200", row.status || "—"];
  const failed = label === "Failed";
  return <span className={`nb-badge ${cls}`} title={failed ? (row.error_message || "Failed") : ""} data-testid={`waba-badge-${row.id}`}>{label}</span>;
}

function WabaLogTable() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/waba/logs").then(r => setRows(r.data || [])).catch(() => {}); }, []);
  return (
    <div className="nb-card p-0 overflow-hidden" data-testid="waba-log-table">
      <div className="px-6 py-3 border-b-2 border-black bg-white flex items-center justify-between">
        <h3 className="display text-xl font-bold">WhatsApp (WABA) Log</h3>
        <span className="text-xs opacity-60">{rows.length} entries</span>
      </div>
      <div className="max-h-[60vh] overflow-auto">
        <table className="nb-table">
          <thead><tr><th>When</th><th>Channel</th><th>Mobile / Target</th><th>Message / Template</th><th>Status</th><th>Failure Reason</th></tr></thead>
          <tbody>
            {rows.map(l => {
              const when = l.created_at || l.timestamp;
              const isBulk = l.dispatch_type === "bulk_summary";
              const tpl = l.display_name || l.template_slug || l.template;
              return (
                <tr key={l.id} data-testid={`waba-log-row-${l.id}`}>
                  <td className="mono text-xs">{when ? new Date(when).toLocaleString() : "—"}</td>
                  <td><span className="nb-badge bg-[#86EFAC]">WABA</span></td>
                  <td className="mono text-sm">{isBulk ? <em>{l.bulk_total_count} recipients</em> : (l.recipient_phone || l.to || "—")}</td>
                  <td className="text-sm">
                    {isBulk ? <span className="font-bold">Bulk send — {l.bulk_total_count} messages sent via {tpl}</span> : tpl}
                    {l.recipient_name ? <span className="opacity-60"> · {l.recipient_name}</span> : null}
                  </td>
                  <td><WabaStatusBadge row={l} /></td>
                  <td className="text-xs text-rose-700">{(l.delivery_status === "failed") ? (l.error_message || "Failed") : ""}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No WhatsApp messages yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Notifications() {
  const [logs, setLogs] = useState([]);
  const [f, setF] = useState({recipient_mobile:"", message:""});
  const [channel, setChannel] = useState("SMS");
  const [chans, setChans] = useState({SMS:true, WABA:true, RCS:true});
  const load = ()=> api.get("/notifications").then(r=>setLogs(r.data));
  useEffect(()=>{ load(); api.get("/notifications/channels").then(r=>setChans(r.data)).catch(()=>{}); }, []);
  const templates = [
    "Reminder: {name}'s fees are due. Please pay at the earliest.",
    "{name} was marked ABSENT today. Please contact the school.",
    "Exam schedule published. Check the Parent Portal for details.",
    "School closed tomorrow for a public holiday.",
  ];
  const enabled = chans[channel];
  const send = async (e)=>{ e.preventDefault();
    if(!enabled){ toast.error("This channel is not enabled for your school"); return; }
    try {
      await api.post("/notifications/send", {...f, channel});
      toast.success(channel==="SMS"?"Sent (mocked)":`${channel} queued (stub)`);
      setF({recipient_mobile:"",message:""}); load();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const CH = [{id:"SMS",label:"SMS"},{id:"WABA",label:"WhatsApp (WABA)"},{id:"RCS",label:"RCS"}];
  const shown = logs.filter(l=> (l.channel||"SMS")===channel);
  return (
    <div className="space-y-6">
      <DuesReminder onSent={load}/>
      <div className="flex gap-2 flex-wrap" data-testid="notif-channel-tabs">
        {CH.map(c=>(
          <button key={c.id} onClick={()=>setChannel(c.id)} data-testid={`notif-tab-${c.id}`}
            className={`nb-btn text-sm ${channel===c.id?"":"nb-btn-ghost"} ${!chans[c.id]?"opacity-50":""}`}>
            {c.label}{!chans[c.id] && " · Off"}
          </button>
        ))}
      </div>
      {channel === "WABA" ? <WabaLogTable /> : (
      <div className="grid lg:grid-cols-3 gap-6">
        {enabled ? (
        <form onSubmit={send} className="nb-card p-6 bg-[#93C5FD] space-y-3 h-fit" data-testid="notif-composer">
          <h3 className="display text-xl font-bold">{channel==="SMS"?"Send SMS (Mocked)":`Send ${channel} (Stub)`}</h3>
          <select className="nb-select text-sm" value="" onChange={e=>e.target.value&&setF({...f,message:e.target.value})} data-testid="notif-template">
            <option value="">Ready templates…</option>
            {templates.map((t,i)=><option key={i} value={t}>{t.slice(0,42)}…</option>)}
          </select>
          <input data-testid="notif-mobile" className="nb-input" placeholder="Mobile" required value={f.recipient_mobile} onChange={e=>setF({...f,recipient_mobile:e.target.value})}/>
          <textarea data-testid="notif-message" className="nb-textarea" placeholder="Message (vars: {name}, {school_name})" rows={4} required value={f.message} onChange={e=>setF({...f,message:e.target.value})}/>
          <button data-testid="notif-send" className="nb-btn w-full justify-center"><Send size={16}/>Send {channel}</button>
        </form>
        ) : (
        <div className="nb-card p-6 bg-slate-100 h-fit space-y-2" data-testid="notif-disabled">
          <h3 className="display text-lg font-bold">{channel} unavailable</h3>
          <p className="text-sm text-slate-600">This channel is not included in your current subscription plan. Contact Global Admin to enable {channel}.</p>
        </div>
        )}
        <div className="nb-card p-0 lg:col-span-2 overflow-hidden">
          <div className="px-6 py-3 border-b-2 border-black bg-white"><h3 className="display text-xl font-bold">{channel} Log</h3></div>
          <div className="max-h-[60vh] overflow-auto">
            <table className="nb-table">
              <thead><tr><th>When</th><th>Channel</th><th>Mobile</th><th>Message</th><th>Status</th></tr></thead>
              <tbody>
                {shown.map(l=>(
                  <tr key={l.id}><td className="mono text-xs">{new Date(l.sent_at).toLocaleString()}</td>
                    <td><span className="nb-badge bg-[#C4B5FD]">{l.channel||"SMS"}</span></td>
                    <td className="mono text-sm">{l.mobile}</td><td>{l.message}</td>
                    <td><span className="nb-badge bg-[#86EFAC]">{l.status}</span></td>
                  </tr>
                ))}
                {shown.length===0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No {channel} messages yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>)}
    </div>
  );
}

function DuesReminder({ onSent }) {
  const [threshold, setThreshold] = useState(5000);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings").then(r => {
      if (r.data?.dues_reminder_threshold) setThreshold(r.data.dues_reminder_threshold);
    });
  }, []);
  const doPreview = async () => {
    setBusy(true);
    try {
      const r = await api.get("/fees/dues-preview", { params: { min_dues: threshold }});
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const send = async () => {
    if (!preview || preview.count === 0) {
      toast.info("No students with dues above threshold. Run Preview first."); return;
    }
    if (!window.confirm(`Send SMS reminder to ${preview.count} parent(s)?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/fees/send-dues-reminders", { min_dues: parseFloat(threshold) });
      toast.success(`Reminders sent to ${r.data.sent} parent(s).`);
      setPreview(null); onSent && onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="nb-card p-5 bg-[#FDE047]">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="nb-label">Fee Dues Reminder — Bulk SMS</div>
          <p className="text-sm max-w-xl">
            Sends a courteous, non-transactional reminder to parents of students whose outstanding balance
            is greater than or equal to the threshold below.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-end gap-2">
          <label>
            <div className="nb-label">Threshold (₹)</div>
            <input type="number" min="0" step="100" value={threshold}
                   onChange={e=>setThreshold(e.target.value)}
                   className="nb-input w-32 mt-1 mono"
                   data-testid="dues-threshold"/>
          </label>
          <button onClick={doPreview} disabled={busy}
                  className="nb-btn nb-btn-secondary"
                  data-testid="dues-preview-btn">Preview</button>
          <button onClick={send} disabled={busy || !preview || preview.count === 0}
                  className="nb-btn" data-testid="dues-send-btn">
            {busy ? "Working..." : "Send Reminders"}
          </button>
        </div>
      </div>
      {preview && (
        <div className="mt-4 nb-card-sm p-3 bg-white">
          <div className="font-bold" data-testid="dues-preview-count">
            {preview.count} student{preview.count === 1 ? "" : "s"} have dues ≥ ₹{preview.threshold.toLocaleString()}
          </div>
          {preview.count > 0 && (
            <div className="mt-2 overflow-x-auto">
              <table className="nb-table text-xs">
                <thead><tr><th>Roll</th><th>Name</th><th>Class</th><th>Parent</th><th>Balance</th></tr></thead>
                <tbody>
                  {preview.students.slice(0, 20).map(s => (
                    <tr key={s.student_id}>
                      <td className="mono">{s.roll_no}</td>
                      <td>{s.name}</td>
                      <td>{s.class_name}-{s.section}</td>
                      <td className="text-xs">{s.parent_name} · {s.parent_mobile}</td>
                      <td className="mono font-bold">₹{s.balance.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.students.length > 20 && (
                <div className="text-xs text-slate-500 p-2">+ {preview.students.length - 20} more…</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Content() {
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [nf, setNf] = useState({title:"", body:""});
  const [gf, setGf] = useState({title:"", image_url:"", caption:""});
  const load = async ()=>{
    setNews((await api.get("/news")).data);
    setGallery((await api.get("/gallery")).data);
  };
  useEffect(()=>{ load(); }, []);
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <form onSubmit={async e=>{e.preventDefault(); await api.post("/news", nf); setNf({title:"",body:""}); load(); toast.success("Published");}}
              className="nb-card p-6 bg-[#FDE047] space-y-3">
          <h3 className="display text-xl font-bold">Add News / Update</h3>
          <input data-testid="news-title" className="nb-input" required placeholder="Title" value={nf.title} onChange={e=>setNf({...nf,title:e.target.value})}/>
          <textarea data-testid="news-body" className="nb-textarea" required placeholder="Body" rows={3} value={nf.body} onChange={e=>setNf({...nf,body:e.target.value})}/>
          <button className="nb-btn w-full justify-center"><Plus size={16}/>Publish</button>
        </form>
        <div className="nb-card p-4">
          {news.map(n=>(
            <div key={n.news_id} className="border-b border-slate-200 py-2 flex justify-between gap-2">
              <div><div className="font-bold">{n.title}</div><div className="text-sm text-slate-600">{n.body}</div></div>
              <button onClick={async()=>{await api.delete(`/news/${n.news_id}`); load();}} className="nb-btn nb-btn-danger"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <form onSubmit={async e=>{e.preventDefault(); await api.post("/gallery", gf); setGf({title:"",image_url:"",caption:""}); load(); toast.success("Added");}}
              className="nb-card p-6 bg-[#C4B5FD] space-y-3">
          <h3 className="display text-xl font-bold">Add Gallery Item</h3>
          <input data-testid="gal-title" className="nb-input" required placeholder="Title" value={gf.title} onChange={e=>setGf({...gf,title:e.target.value})}/>
          <input data-testid="gal-url" className="nb-input" required placeholder="Image URL" value={gf.image_url} onChange={e=>setGf({...gf,image_url:e.target.value})}/>
          <input data-testid="gal-caption" className="nb-input" placeholder="Caption" value={gf.caption} onChange={e=>setGf({...gf,caption:e.target.value})}/>
          <button className="nb-btn w-full justify-center"><Plus size={16}/>Add</button>
        </form>
        <div className="grid grid-cols-2 gap-3">
          {gallery.map(g=>(
            <div key={g.item_id} className="nb-card-sm p-2">
              <img src={g.image_url} alt={g.title} className="w-full h-28 object-cover rounded border-2 border-black"/>
              <div className="flex justify-between mt-1"><div className="text-xs font-bold">{g.title}</div>
                <button onClick={async()=>{await api.delete(`/gallery/${g.item_id}`); load();}}><Trash2 size={14}/></button></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Enquiries() {
  const [rows, setRows] = useState([]);
  useEffect(()=>{ api.get("/enquiries").then(r=>setRows(r.data)); }, []);
  return (
    <div className="nb-card p-0 overflow-hidden">
      <table className="nb-table">
        <thead><tr><th>When</th><th>Name</th><th>Email</th><th>Phone</th><th>Message</th></tr></thead>
        <tbody>
          {rows.map(r=>(
            <tr key={r.enquiry_id}><td className="mono text-xs">{new Date(r.created_at).toLocaleString()}</td>
              <td className="font-bold">{r.name}</td><td>{r.email}</td>
              <td className="mono">{r.phone}</td><td>{r.message}</td></tr>
          ))}
          {rows.length===0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No enquiries</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ==================================================================
   Iter30 — Master Event Log (Admin Event Management)
   Consolidated read-only historical record per event.
   ================================================================== */
function MasterEventLog() {
  const [events, setEvents] = useState([]);
  const [openId, setOpenId] = useState(null);
  useEffect(()=>{ api.get("/events").then(r=>setEvents(r.data)).catch(()=>{}); }, []);
  return <div className="nb-card p-6 bg-[#DBEAFE] mt-6 space-y-3" data-testid="master-event-log">
    <div>
      <h3 className="display text-2xl font-bold">Master Event Log</h3>
      <p className="text-xs">Consolidated historical record per event — core metadata, full schedule breakdown, and uploaded media assets in one place.</p>
    </div>
    <div className="nb-card-sm bg-white p-0 overflow-x-auto">
      <table className="nb-table text-xs">
        <thead><tr>
          <th>Title</th><th>Date</th><th>Venue</th><th>Manager</th><th>Guests</th>
          <th>Status</th><th>Items</th><th>Media</th><th></th>
        </tr></thead>
        <tbody>
          {events.map(e => (
            <tr key={e.event_id} data-testid={`mel-row-${e.event_id}`}>
              <td className="font-bold">{e.name}</td>
              <td className="mono">{(e.date||"").slice(0,10)}</td>
              <td className="text-[10px]">{e.venue || "—"}</td>
              <td>{e.manager_name || "—"}</td>
              <td className="text-[10px] max-w-[180px] truncate" title={(e.guests||[]).join(", ")}>
                {(e.guests||[]).join(", ") || "—"}</td>
              <td><span className="nb-badge text-[10px]">{e.status}</span></td>
              <td className="mono">{(e.items||[]).length}</td>
              <td className="mono">{(e.media||[]).length}</td>
              <td>
                <button onClick={()=>setOpenId(openId === e.event_id ? null : e.event_id)}
                        className="nb-btn nb-btn-secondary px-2 py-1 text-[10px]"
                        data-testid={`mel-toggle-${e.event_id}`}>
                  {openId === e.event_id ? "Collapse" : "Expand"}
                </button>
              </td>
            </tr>
          ))}
          {events.length === 0 && <tr><td colSpan={9} className="text-center py-4 text-slate-500">No events yet</td></tr>}
        </tbody>
      </table>
    </div>
    {openId && <MasterEventDetail event={events.find(e=>e.event_id===openId)}/>}
  </div>;
}

function MasterEventDetail({ event }) {
  if (!event) return null;
  return <div className="nb-card p-4 bg-white space-y-4" data-testid={`mel-detail-${event.event_id}`}>
    <div>
      <div className="nb-label">Core Metadata</div>
      <div className="grid md:grid-cols-2 gap-2 text-xs">
        <div><b>Title:</b> {event.name}</div>
        <div><b>Date:</b> {(event.date||"").slice(0,10)}</div>
        <div><b>Venue:</b> {event.venue || "—"}</div>
        <div><b>Manager:</b> {event.manager_name || "—"}</div>
        <div className="col-span-2"><b>Guests:</b> {(event.guests||[]).join(", ") || "—"}</div>
        <div className="col-span-2"><b>Judges:</b> {(event.judges||[]).map(j=>j.name).join(", ") || "—"}</div>
        <div className="col-span-2"><b>Description:</b> {event.description || "—"}</div>
      </div>
    </div>
    <div>
      <div className="nb-label">Schedule Breakdown ({(event.items||[]).length} programs)</div>
      <div className="overflow-x-auto">
        <table className="nb-table text-[11px]">
          <thead><tr>
            <th>Sl</th><th>Program</th><th>Time</th><th>Staff</th><th>Team</th>
            <th>Rehearsed</th><th>Approval</th><th>Score</th><th>Rank</th><th>Cert</th>
          </tr></thead>
          <tbody>
            {(event.items||[]).map(it => (
              <tr key={it.item_id}>
                <td className="mono">{it.sl_no}</td>
                <td className="font-bold">{it.program_name}</td>
                <td className="mono">{(it.time_from||"—")}–{(it.time_to||"—")}</td>
                <td>{it.responsible_staff_name || "—"}</td>
                <td className="max-w-[160px] truncate" title={(it.team_members||[]).join(", ")}>
                  {(it.team_members||[]).join(", ") || "—"}
                </td>
                <td>{it.rehearsed ? "Yes" : "No"}</td>
                <td><span className="nb-badge text-[10px]">{it.approval_status || "pending"}</span></td>
                <td className="mono">{it.performance_score ?? "—"}</td>
                <td className="mono">{it.rank ?? "—"}</td>
                <td>{it.certificate_type || "—"}</td>
              </tr>
            ))}
            {(event.items||[]).length===0 && <tr><td colSpan={10} className="text-center py-2 text-slate-500">No programs</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
    <div>
      <div className="nb-label">Media Assets ({(event.media||[]).length})</div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {(event.media||[]).map(m => (
          <div key={m.media_id} className="nb-card-sm p-1 bg-slate-50">
            {(m.kind||m.type||"").startsWith("image") || (m.storage_path||"").match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
              <img src={`${(process.env.REACT_APP_BACKEND_URL||"")}/api/files/serve/${m.storage_path}`}
                   alt={m.caption||"media"} className="w-full h-16 object-cover rounded"/>
            ) : (
              <div className="text-[10px] break-all p-1">{m.storage_path?.split("/").pop() || "file"}</div>
            )}
            <div className="text-[10px] mt-1">
              <span className="nb-badge">{m.pushed_at ? "Syndicated" : "Draft"}</span>
            </div>
          </div>
        ))}
        {(event.media||[]).length===0 && <div className="text-xs text-slate-500 col-span-6">No media uploaded</div>}
      </div>
    </div>
  </div>;
}

